import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';

// 見出しID生成関数（8文字のランダム英数字）
const generateHeadingId = () => {
  return Math.random().toString(36).substring(2, 10);
};

// 優先度→色マッピング（透明度0.6）
const getPriorityColor = priority => {
  const colorMap = {
    critical: 'rgba(207, 19, 34, 0.6)', // 最重要
    high: 'rgba(255, 77, 79, 0.6)', // 高
    medium: 'rgba(250, 173, 20, 0.6)', // 中
    low: 'rgba(82, 196, 26, 0.6)', // 低
    minor: 'rgba(217, 217, 217, 0.6)', // 微調整
  };
  return colorMap[priority] || colorMap.medium;
};

// 最高優先度を取得（critical > high > medium > low > minor）
const getHighestPriority = priorities => {
  const order = ['critical', 'high', 'medium', 'low', 'minor'];
  for (const p of order) {
    if (priorities.includes(p)) return p;
  }
  return 'medium';
};
import { flushSync } from 'react-dom';
import './step3.css';
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  Space,
  Input,
  Modal,
  Segmented,
  Tag,
  message,
  Dropdown,
  Menu,
  Radio,
  Input as AntInput,
  Switch,
  InputNumber,
  Select,
  Tabs,
  Table,
  Collapse,
  Progress,
  Affix,
  Checkbox,
  Tooltip,
  Skeleton,
  Alert,
  Slider,
  Tour,
} from 'antd';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined,
  BarChartOutlined,
  SendOutlined,
  CopyOutlined,
  PlusCircleOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
  SaveOutlined,
  SearchOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  SwapOutlined,
  BulbOutlined,
} from '@ant-design/icons';

// Fixtures（開発用フォールバックは実行時fetchに切替。直importはしない）
let topicsFixture = [];
let competitorsFixture = [];
let lsiHeatmapFixture = { rows: [], cols: [] };
// スコアリングデータはscoring APIから取得（ダミー値なし）
let analysisFullFixture = null; // 互換維持（将来削除）

import TopicSunburstMap from '../../Analysis/TopicSunburstMap';
import '../../Analysis/CompetitorAnalysis.css';
import '../../Analysis/steps.css';
import {
  v2AnalysisApi,
  v2ArticlesApi,
  v2ToppingsApi,
  publicSamplesApi,
} from '../../../../api';
import {
  getHeatmapColor,
  getTermsHeatmapColor,
  getRelatedHeatmapColor,
  getLSIHeatmapColor,
} from '../../common/heatmapColors';
import TipsRotator from '../../../Common/TipsRotator';
import { generationStep3Tips } from '../../../Common/tipsData';
import { useTutorialTour } from '../../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../../config/tourConfigs';
import HelpIcon from '../../../Common/HelpIcon';
import { generationStep3Help } from '../../../Common/helpContents';
import EnhancedTour from '../../../Common/EnhancedTour';

// DND用Droppableラッパー
const LoggedDroppable = ({ droppableId, type, children, ...rest }) => {
  return (
    <Droppable droppableId={droppableId} type={type} {...rest}>
      {(provided, snapshot) => {
        return children(provided, snapshot);
      }}
    </Droppable>
  );
};

export const Step3Composer = ({
  initialOutline,
  onComplete,
  keyword,
  analysisId: propAnalysisId,
  uuid,
  articleData,
  isGuest = false,
}) => {
  // 閲覧専用モード（status='completed'の場合）
  const isReadOnly = articleData?.status === 'completed';

  // STEP2で選択されたアウトライン（一意URLから復元）
  const [selectedOutlineFromServer, setSelectedOutlineFromServer] =
    useState(null);
  // 右カラム：fixtures 状態
  // スコアリングデータはscoringStatus/scoringDataで管理（ダミー値なし）
  const [fxTopics, setFxTopics] = useState([]);
  const [fxCompetitors, setFxCompetitors] = useState([]);
  const [fxLsiHeatmap, setFxLsiHeatmap] = useState({ rows: [], cols: [] });
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisId, setAnalysisId] = useState(propAnalysisId || null);
  const [fullAnalysis, setFullAnalysis] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [topicScores, setTopicScores] = useState({});
  const [avgScores, setAvgScores] = useState({});

  // STEP3スコアリング状態
  const [scoringStatus, setScoringStatus] = useState('idle'); // idle, processing, completed, failed
  const [scoringData, setScoringData] = useState(null);
  const [coverageScore, setCoverageScore] = useState(null);
  const [uniquenessScore, setUniquenessScore] = useState(null);
  const scoringPollingRef = useRef(null);
  const scoringLoadedRef = useRef(false); // 初回ロード済みフラグ
  const [actionPlanChecks, setActionPlanChecks] = useState({});

  // スコアリング回数制御
  const [scoringExecutionCount, setScoringExecutionCount] = useState(0);

  // メモ欄（チーム間共有用、生成には使用しない）
  const [outlineMemo, setOutlineMemo] = useState('');

  // トピック絞り込み用スコア範囲（0-10）
  const [scoreRange, setScoreRange] = useState([0, 5]);

  // トピックアコーディオンのアクティブキー
  const [topicActiveKey, setTopicActiveKey] = useState(null);

  // 分析結果の有無（トピック・スコア関連UIの表示制御用）
  const hasAnalysis = Boolean(analysisId);

  // ✅ ツアー管理
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep: tourCurrentStep,
    updateCurrentStep: updateTourCurrentStep,
    loading: tourLoading,
    isMicroMode: isTourMicro,
  } = useTutorialTour('generation_step3', { microKey: 'generation_step3_micro', disabled: isGuest });

  // 親コンポーネントからのツアー再表示イベントをリッスン
  useEffect(() => {
    const handleRestartTour = () => restartTour();
    window.addEventListener('restartGenerationTour', handleRestartTour);
    return () => window.removeEventListener('restartGenerationTour', handleRestartTour);
  }, [restartTour]);

  // ツアー開始タイミング管理用のref
  const tourStartedRef = useRef(false);

  // 自動保存状態
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle, saving, saved
  const [lastSavedData, setLastSavedData] = useState(null);


  // 未チェックアクションの見出しIDごとマッピング（useMemo for performance）
  const uncheckedActionsMap = useMemo(() => {
    const map = {
      all: [], // 全体に関わるアクション
      byHeading: {}, // 見出しIDごとのアクション
    };

    // ⭐ v19: action_plan → improvement_ideas
    const improvementIdeas =
      scoringData?.summary_and_action?.improvement_ideas ||
      scoringData?.summary_and_action?.action_plan || // 後方互換性
      [];

    if (!improvementIdeas.length) {
      return map;
    }

    improvementIdeas.forEach((item, idx) => {
      const key = item.id || `action_${idx}`;
      const isChecked = actionPlanChecks[key] || false;

      // チェック済みはスキップ
      if (isChecked) return;

      const targetHeadings = item.target_headings || [];

      // "all" type
      if (targetHeadings.includes('all')) {
        map.all.push(item);
      } else {
        // 特定見出しに関するアクション
        targetHeadings.forEach(headingId => {
          if (!map.byHeading[headingId]) {
            map.byHeading[headingId] = [];
          }
          map.byHeading[headingId].push(item);
        });
      }
    });

    return map;
  }, [scoringData, actionPlanChecks]);

  const [rightActive, setRightActive] = useState('topic');
  const stickyWrapRef = React.useRef(null);
  const stickyHeaderRef = React.useRef(null);
  const [stickyBodyHeight, setStickyBodyHeight] = useState(520);

  // ⭐ 参考データ（リサーチ結果）
  const researchFindings = useMemo(() => {
    const step2 = articleData?.step_states?.new_flow?.step2_research;
    if (step2?.all_findings && Array.isArray(step2.all_findings)) {
      return step2.all_findings;
    }
    return [];
  }, [articleData]);

  // ドメイン抽出用ヘルパー
  const extractDomain = (url) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };


  // ⭐ 分析結果の有無に応じて初回の右カラムタブを設定
  useEffect(() => {
    if (hasAnalysis) {
      setRightActive('topic'); // 分析結果ありの場合はトピックタブをアクティブに
    } else {
      setRightActive('tops'); // 分析結果なしの場合は独自情報タブをアクティブに
    }
  }, [hasAnalysis]);
  // 分析モーダル内：横スクロール用リファレンス（フックはトップレベルで定義）
  const analysisScrollerRef = React.useRef(null);
  const analysisPanRef = React.useRef({
    active: false,
    startX: 0,
    startLeft: 0,
  });
  // 競合カード（モーダル内の横スクロールD&D用）
  const [compList, setCompList] = useState([]);
  // ヒートマップソート（列クリックでトグル）
  const [heatSort, setHeatSort] = useState({ col: -1, dir: 'desc' });
  // ヒートマップタイプ切替（topic/terms/related/lsi）
  const [heatmapType, setHeatmapType] = useState('topic');
  // スコア詳細の小モーダル
  const [scoreModal, setScoreModal] = useState({
    open: false,
    row: null,
    col: null,
    value: null,
  });

  // トピックのラベル（必須/重要/独自）をfixtures準拠のルールで算出
  const classifyTopic = useCallback(row => {
    const avgRaw = Number(
      row?.average_normalized_score ?? row?.average_score ?? 0,
    );
    const avg = Number.isFinite(avgRaw) ? avgRaw : 0;
    const avgRounded = Math.round(avg * 10) / 10;
    // スコア配列抽出
    const siteScores = (() => {
      const arr = [];
      if (Array.isArray(row?.values)) {
        row.values.forEach(v => {
          const s = Number(
            v?.normalized_score ??
              v?.average_score ??
              v?.strength ??
              v?.relevance,
          );
          if (Number.isFinite(s)) arr.push(s);
        });
      } else {
        // site1..site10に数値があるケース
        for (let i = 1; i <= 10; i++) {
          const k = `site${i}`;
          if (typeof row?.[k] === 'number') arr.push(row[k]);
        }
      }

      return arr;
    })();
    const sorted = siteScores.slice().sort((a, b) => b - a);
    const top3 = sorted.slice(0, 3);
    const ge = (t, thr) => (t || []).filter(x => x >= thr).length;
    const strongTop3 = ge(top3, 8.5);
    const strongCount75 = ge(siteScores, 7.5);
    const strongCount85 = ge(siteScores, 8.5);

    // 判定順序（Unique優先 → Must → Important）
    if (avgRounded <= 5.0 && strongCount85 <= 2)
      return { jp: '独自', color: 'cyan' };
    if (avgRounded >= 8.0 || strongTop3 >= 2)
      return { jp: '必須', color: 'red' };
    if (avgRounded >= 6.0 || strongCount75 >= 3)
      return { jp: '重要', color: 'gold' };
    return null;
  }, []);
  const [fxCategories, setFxCategories] = useState([]);

  const groupedTopics = useMemo(() => {
    const buckets = new Map();
    const add = (key, topic) => {
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(topic);
    };
    const base = Array.isArray(fxTopics) ? fxTopics : [];
    // 優先: カテゴリでグルーピング（generalは除外）
    const cats = Array.isArray(fxCategories) ? fxCategories : [];
    if (cats.length > 0) {
      const topicToCat = new Map();
      cats.forEach(cat => {
        const cname = String(cat?.category_name || '').trim();
        if (!cname) return;
        const topics = Array.isArray(cat?.unified_topics)
          ? cat.unified_topics
          : [];
        topics.forEach(tn => {
          const key = String(tn).trim();
          if (key) topicToCat.set(key, cname);
        });
      });
      base.forEach(t => {
        const name = String(t?.name || t?.topic || t?.keyword || '').trim();
        const cname = topicToCat.get(name);
        if (!cname) return;
        const lower = cname.toLowerCase();
        if (lower === 'general') return; // general除外
        add(cname, t);
      });
    }
    let hasAnyGroup = buckets.size > 0;
    if (!hasAnyGroup) {
      // t側のカテゴリフィールド
      let hasRealCategory = false;
      base.forEach(t => {
        const catField =
          t?.category ||
          t?.category_name ||
          t?.categoryName ||
          t?.topic_category ||
          t?.parentCategory ||
          t?.group ||
          t?.group_name ||
          t?.parent ||
          t?.cluster ||
          t?.topic_group ||
          t?.section;
        if (catField) hasRealCategory = true;
      });
      if (hasRealCategory) {
        base.forEach(t => {
          const catField =
            t?.category ||
            t?.category_name ||
            t?.categoryName ||
            t?.topic_category ||
            t?.parentCategory ||
            t?.group ||
            t?.group_name ||
            t?.parent ||
            t?.cluster ||
            t?.topic_group ||
            t?.section;
          const cat = String(catField).trim();
          if (!cat) return;
          if (cat.toLowerCase() === 'general') return; // general除外
          add(cat, t);
        });
      }
    }
    if (buckets.size === 0) {
      // フォールバック: ステータス分類
      base.forEach(t => {
        const statusRaw = (t.status || '').toString().toLowerCase();
        let cat = 'その他';
        if (statusRaw.includes('must')) cat = '必須';
        else if (statusRaw.includes('important')) cat = '重要';
        else if (statusRaw.includes('unique')) cat = 'ユニーク候補';
        add(cat, t);
      });
    }
    const entries = Array.from(buckets.entries()).sort(
      (a, b) => b[1].length - a[1].length,
    );
    let res = entries.map(([k, items]) => ({ key: k, label: k, items }));
    if ((!res || res.length === 0) && base.length > 0) {
      res = [{ key: 'すべて', label: 'すべて', items: base }];
    }
    return res;
  }, [fxTopics, fxCategories]);

  // スコア範囲でフィルタリングされたトピックグループ
  const filteredTopicGroups = useMemo(() => {
    if (!groupedTopics || groupedTopics.length === 0) return [];

    const [minScore, maxScore] = scoreRange;

    const filtered = groupedTopics
      .map(group => {
        const filteredItems = group.items.filter(t => {
          const key = t.topicId || t.name;
          const score = topicScores[key] ?? 5.0; // デフォルトは5.0
          return score >= minScore && score <= maxScore;
        });

        return {
          ...group,
          items: filteredItems,
        };
      })
      .filter(group => group.items.length > 0); // 空のグループを除外

    return filtered;
  }, [groupedTopics, scoreRange, topicScores]);

  // filteredTopicGroupsが変更されたら、最初のグループを開く
  useEffect(() => {
    if (filteredTopicGroups && filteredTopicGroups.length > 0) {
      setTopicActiveKey(filteredTopicGroups[0].key);
    }
  }, [filteredTopicGroups]);

  useEffect(() => {
    // STEP1からprops優先、なければURLクエリから取得
    if (propAnalysisId) {
      setAnalysisId(propAnalysisId);
      return;
    }
    try {
      const url = new URL(window.location.href);
      const aid = url.searchParams.get('analysis_id');
      if (aid) setAnalysisId(aid);
    } catch (e) {
      // noop
    }
  }, [propAnalysisId]);

  // uuid から選択アウトラインを復元
  useEffect(() => {
    const loadSelectedOutline = async () => {
      if (!uuid) return;
      try {
        const artApi = isGuest ? publicSamplesApi : v2ArticlesApi;
        const gen = await artApi.getArticle(uuid);
        const outlineData = gen?.outline?.outline_data;

        // 優先度1: working_draft（一時保存されたデータ）
        if (outlineData?.working_draft) {
          const draft = outlineData.working_draft;

          // ⭐ タイトルが空でない場合のみ設定
          if (draft.title && draft.title.trim()) {
            setTitleH1(draft.title);
          }

          if (draft.sections && Array.isArray(draft.sections)) {
            // working_draftから直接sectionsをロード（IDを含む）
            // working_draftの構造をselectedOutlineFromServerに設定することで、
            // initialSectionsのuseMemoが正しく処理する
            setSelectedOutlineFromServer({
              title: draft.title || '', // ⭐ タイトルも含める
              sections: draft.sections,
            });

            // メモの復元
            if (draft.memo) {
              setOutlineMemo(draft.memo);
            }
          }
        }
        // 優先度2: selected候補
        else {
          const selectedId = outlineData?.selected_id;
          const candidates = outlineData?.candidates || [];
          if (selectedId != null) {
            const chosen =
              candidates.find(c => String(c.id) === String(selectedId)) ||
              candidates[Number(selectedId)] ||
              null;
            if (chosen) setSelectedOutlineFromServer(chosen);
          }
        }

        // ⭐ 記事データから分析結果IDを取得（propAnalysisIdよりも信頼できる）
        if (gen?.step1_analysis_id) {
          setAnalysisId(gen.step1_analysis_id);
        } else {
          setAnalysisId(null);
        }
      } catch (e) {
        console.error('[Step3] Failed to load outline:', e);
      }
    };
    loadSelectedOutline();
  }, [uuid]);

  useEffect(() => {
    const loadFromAnalysis = async () => {
      if (!analysisId) return;
      setAnalysisLoading(true);
      try {
        const analysisApi = isGuest ? publicSamplesApi : v2AnalysisApi;
        const data = await analysisApi.getAnalysisResult(analysisId);
        // topics
        const topicRows =
          data?.topic_analysis?.integrated_heatmap?.formatted_heatmaps
            ?.topic_heatmap?.rows ||
          data?.topic_analysis?.site_topic_analysis?.heatmap_data?.rows ||
          // 追加フォールバック（直下/非formatted構造）
          data?.topic_analysis?.topic_heatmap?.rows ||
          data?.topic_analysis?.integrated_heatmap?.topic_heatmap?.rows ||
          [];
        // 正規化: Step3が期待するキーへ変換（name/average_normalized_score/topicId）
        const normalizedTopics = (
          Array.isArray(topicRows) ? topicRows : []
        ).map((r, idx) => {
          const name = r?.name || r?.topic || r?.keyword || `Topic ${idx + 1}`;
          let avg = Number(r?.average_score);
          if (!Number.isFinite(avg)) {
            if (Array.isArray(r?.values) && r.values.length > 0) {
              let sum = 0;
              let cnt = 0;
              r.values.forEach(v => {
                const val = Number(
                  v?.average_score ??
                    v?.normalized_score ??
                    v?.strength ??
                    v?.relevance,
                );
                if (Number.isFinite(val)) {
                  sum += val;
                  cnt += 1;
                }
              });
              avg = cnt > 0 ? sum / cnt : 0;
            } else {
              avg = 0;
            }
          }
          return {
            topicId: r?.topicId || name,
            name,
            average_score: avg,
            average_normalized_score: avg,
            ...r,
          };
        });
        setFxTopics(normalizedTopics);
        // competitors
        const competitors = (data?.competitor_analysis?.competitors || []).map(
          (c, i) => {
            const heads =
              (c?.headings_analysis &&
              Array.isArray(c.headings_analysis.headings)
                ? c.headings_analysis.headings
                : Array.isArray(c?.headings)
                  ? c.headings
                  : []) || [];
            return {
              id: c?.id || i + 1,
              title: c?.title || c?.domain || c?.url || `Site ${i + 1}`,
              domain: c?.domain || '',
              url: c?.url || '',
              headings_analysis: { headings: heads },
              strength: Number.isFinite(c?.strength) ? c.strength : undefined,
              ...c,
            };
          },
        );
        setFxCompetitors(Array.isArray(competitors) ? competitors : []);
        // LSI heatmap
        const lsiRows =
          data?.keywords_analysis?.lsi_heatmap_data?.rows ||
          data?.keywords_analysis?.heatmap_data?.lsi_keywords?.rows ||
          data?.topic_analysis?.integrated_heatmap?.formatted_heatmaps
            ?.lsi_keywords_heatmap?.rows ||
          [];
        setFxLsiHeatmap({
          rows: Array.isArray(lsiRows) ? lsiRows : [],
          cols: [],
        });

        // Terms/Related heatmaps
        const termsRows =
          data?.topic_analysis?.integrated_heatmap?.formatted_heatmaps
            ?.important_terms_heatmap?.rows ||
          data?.terms_evaluation?.heatmap_data?.rows ||
          [];
        const relatedRows =
          data?.topic_analysis?.integrated_heatmap?.formatted_heatmaps
            ?.related_keywords_heatmap?.rows ||
          (Array.isArray(data?.keywords_analysis?.heatmap_data?.rows)
            ? data.keywords_analysis.heatmap_data.rows.filter(
                r => r?.keyword_type === 'related',
              )
            : []) ||
          [];
        // スコアリングデータはscoring APIから取得（ダミー値削除）

        // 可視化用の統合データ（既存UIはanalysisFullFixtureを参照するため構築）
        // categories（TopicSunburstMapはカテゴリーが必須）
        const categories =
          data?.topic_analysis?.topic_extraction?.main_categories ||
          data?.topic_analysis?.main_categories ||
          data?.main_categories ||
          [];
        setFxCategories(Array.isArray(categories) ? categories : []);

        const merged = {
          topic_analysis: {
            topic_extraction: {
              main_categories: Array.isArray(categories) ? categories : [],
            },
            // 互換: 直下にも配置
            main_categories: Array.isArray(categories) ? categories : [],
            integrated_heatmap: {
              formatted_heatmaps: {
                topic_heatmap: { rows: normalizedTopics, columns: [] },
                important_terms_heatmap: { rows: termsRows, columns: [] },
                related_keywords_heatmap: { rows: relatedRows, columns: [] },
                lsi_keywords_heatmap: {
                  rows: Array.isArray(lsiRows) ? lsiRows : [],
                  columns: [],
                },
              },
            },
            site_topic_analysis: { heatmap_data: { rows: normalizedTopics } },
          },
          competitor_analysis: {
            competitor_details: competitors,
          },
          // main keyword をトップにも配置（useTopicMapDataのmainQuery用途）
          keyword:
            data?.keywords_analysis?.main_keyword_metrics?.keyword ||
            data?.keywords_analysis?.main_keyword ||
            data?.serp_data?.keyword ||
            data?.serp_data?.query ||
            '',
        };
        analysisFullFixture = merged; // 既存参照互換
        setFullAnalysis(merged);
      } catch (e) {
        console.warn('[Step3] load failed, fallback fixtures');
        // スコアリングデータはscoring APIから取得（ダミー値削除）
        setFxTopics(Array.isArray(topicsFixture) ? topicsFixture : []);
        setFxCompetitors(
          Array.isArray(competitorsFixture) ? competitorsFixture : [],
        );
        setFxLsiHeatmap(lsiHeatmapFixture || { rows: [], cols: [] });
      } finally {
        setAnalysisLoading(false);
      }
    };
    loadFromAnalysis();
  }, [analysisId]);

  // スコアリング結果の読み込み・ポーリング（自動実行はしない）
  useEffect(() => {
    if (!uuid || !analysisId) return;

    const loadScoring = async () => {
      try {
        // 既存のスコアリング結果をチェック
        const scoringApi = isGuest ? publicSamplesApi : v2ArticlesApi;
        const existingScoring = await scoringApi.getScoring(uuid);

        // スコアリング実行回数を設定
        if (existingScoring?.execution_count !== undefined) {
          setScoringExecutionCount(existingScoring.execution_count);
        }

        if (existingScoring?.status === 'completed') {
          // 既に完了済みの結果がある場合は使用
          setScoringData(existingScoring.scoring_data);
          setCoverageScore(existingScoring.coverage_score);
          setUniquenessScore(existingScoring.uniqueness_score);
          setScoringStatus('completed');

          // ⭐ v19: 改善アイデアのチェック状態を復元
          const improvementIdeas =
            existingScoring.scoring_data?.summary_and_action
              ?.improvement_ideas ||
            existingScoring.scoring_data?.summary_and_action?.action_plan || // 後方互換性
            [];
          const checks = {};
          improvementIdeas.forEach((item, idx) => {
            const key = item.id || `action_${idx}`;
            checks[key] = item.checked || false;
          });
          setActionPlanChecks(checks);
          // ポーリングは開始しない
        } else if (existingScoring?.status === 'processing') {
          // 処理中の場合はポーリング開始
          setScoringStatus('processing');
          startPolling();
        } else if (existingScoring?.status === 'failed') {
          // 失敗の場合：前のデータがあればフォールバック、なければスケルトン
          const hasPreviousData =
            existingScoring.scoring_data &&
            !existingScoring.scoring_data.error &&
            (existingScoring.coverage_score != null ||
              existingScoring.scoring_data.coverage_breakdown);

          if (hasPreviousData) {
            // 前のデータがある場合はフォールバック（警告表示）
            setScoringData(existingScoring.scoring_data);
            setCoverageScore(existingScoring.coverage_score);
            setUniquenessScore(existingScoring.uniqueness_score);
            setScoringStatus('failed');
            message.warning(
              '前回のスコアリングが失敗しました。以前の結果を表示しています。再スコアリングボタンから再実行できます。',
            );
          } else {
            // 前のデータがない場合はスケルトン
            setScoringStatus('failed');
          }
        } else if (existingScoring?.status === 'not_started') {
          // スコアリングがまだ開始されていない場合、STEP2からの自動開始をチェック
          try {
            const scoringStartedKey = `scoring_started_${uuid}`;
            const scoringStartedTime =
              sessionStorage.getItem(scoringStartedKey);

            if (scoringStartedTime) {
              // 5分以内であれば、ポーリングを開始
              const elapsed = Date.now() - parseInt(scoringStartedTime, 10);

              if (elapsed < 5 * 60 * 1000) {
                setScoringStatus('processing');
                startPolling();
              } else {
                // 5分以上経過している場合はマークをクリア
                sessionStorage.removeItem(scoringStartedKey);
                setScoringStatus('idle');
              }
            } else {
              setScoringStatus('idle');
            }
          } catch (e) {
            console.error('[Step3][Scoring] Error checking scoring marker:', e);
            // sessionStorage使用不可の場合はidleに設定
            setScoringStatus('idle');
          }
        } else {
          // status === 'idle' の場合は何もしない（手動実行を待つ）
          const currentStatus = existingScoring?.status || 'idle';
          setScoringStatus(currentStatus);
        }
      } catch (error) {
        console.error('[Step3][Scoring] Failed to load scoring:', error);
        // API エラー（404など）の場合もidleにする
        setScoringStatus('idle');
      }
    };

    const startPolling = () => {
      // 既にポーリング中の場合は開始しない
      if (scoringPollingRef.current) {
        return;
      }

      // ⭐ ポーリング回数とタイムアウト設定
      let pollingAttempts = 0;
      const maxPollingAttempts = 60; // 最大60回 = 3分間（3秒 × 60回）

      scoringPollingRef.current = setInterval(async () => {
        pollingAttempts++;
        
        try {
          const result = await v2ArticlesApi.getScoring(uuid);

          if (result?.status === 'completed') {
            // 状態を更新
            const newScoringData = result.scoring_data || {};
            const newCoverageScore = result.coverage_score;
            const newUniquenessScore = result.uniqueness_score;

            setScoringData(newScoringData);
            setCoverageScore(newCoverageScore);
            setUniquenessScore(newUniquenessScore);
            setScoringStatus('completed');

            // ⭐ v19: 改善アイデアのチェック状態を復元
            const improvementIdeas =
              newScoringData?.summary_and_action?.improvement_ideas ||
              newScoringData?.summary_and_action?.action_plan || // 後方互換性
              [];
            const checks = {};
            improvementIdeas.forEach((item, idx) => {
              const key = item.id || `action_${idx}`;
              checks[key] = item.checked || false;
            });
            setActionPlanChecks(checks);

            // ポーリング停止
            if (scoringPollingRef.current) {
              clearInterval(scoringPollingRef.current);
              scoringPollingRef.current = null;
            }

            // ⭐ スコアリング開始マークをクリア
            try {
              sessionStorage.removeItem(`scoring_started_${uuid}`);
            } catch (e) {
              // 無視
            }
          } else if (result?.status === 'failed') {
            setScoringStatus('failed');
            message.error(
              'スコアリングが失敗しました。再スコアリングボタンから再実行できます。',
            );
            console.error('[Step3][Scoring] Scoring failed:', result);

            // ポーリング停止
            if (scoringPollingRef.current) {
              clearInterval(scoringPollingRef.current);
              scoringPollingRef.current = null;
            }
          } else if (pollingAttempts >= maxPollingAttempts) {
            // ⭐ タイムアウト処理
            console.warn('[Step3][Scoring] Polling timeout reached');
            setScoringStatus('failed');
            message.warning(
              'スコアリングの処理に時間がかかっています。しばらく待ってから再スコアリングボタンをお試しください。',
            );
            
            // ポーリング停止
            if (scoringPollingRef.current) {
              clearInterval(scoringPollingRef.current);
              scoringPollingRef.current = null;
            }
            
            // スコアリング開始マークをクリア
            try {
              sessionStorage.removeItem(`scoring_started_${uuid}`);
            } catch (e) {
              // 無視
            }
          }
        } catch (error) {
          console.error('[Step3][Scoring] Polling error:', error);
          // ⭐ エラーが続く場合もタイムアウトとしてカウント
        }
      }, 3000); // 3秒ごとにポーリング
    };

    // 初回ロードを実行
    if (uuid && analysisId) {
      if (!scoringLoadedRef.current) {
        scoringLoadedRef.current = true;
        loadScoring();
      }
    }

    // クリーンアップ：uuid/analysisIdが変わった場合にリセット
    return () => {
      // 次のuuid/analysisIdのためにリセット
      scoringLoadedRef.current = false;
      if (scoringPollingRef.current) {
        clearInterval(scoringPollingRef.current);
        scoringPollingRef.current = null;
      }
    };
  }, [uuid, analysisId]);

  // スコアリング完了時にトピックスコアを更新
  useEffect(() => {
    if (scoringStatus === 'completed' && scoringData) {
      try {
        const topicCoverage = scoringData?.topic_coverage?.topic_analysis;
        if (topicCoverage && Array.isArray(topicCoverage)) {
          const newScores = {};
          topicCoverage.forEach(topic => {
            // topic_name または topic または name
            const topicName = topic.topic_name || topic.topic || topic.name;
            // coverage_score または score または relevance_score
            const score =
              topic.coverage_score ?? topic.score ?? topic.relevance_score ?? 0;
            if (topicName) {
              // スコアを0-10のスケールに正規化（0-1の場合は10倍、0-100の場合は/10）
              const normalizedScore =
                score <= 1 ? score * 10 : score > 10 ? score / 10 : score;
              newScores[topicName] = normalizedScore;
            }
          });
          setTopicScores(newScores); // prevとマージせずに置き換え
        }
      } catch (error) {
        console.error('[Step3][Scoring] Failed to update topic scores:', error);
      }

      // ✅ スコアリング完了時にツアーを開始
      if (!tourStartedRef.current && !tourLoading) {
        tourStartedRef.current = true;
        // ツアーは useTutorialTour フックが自動的に開始
      }
    }
  }, [scoringStatus, scoringData, tourLoading]);

  // 競合カードを初期化（モーダル開閉に依存せず、実データから一度セット）
  useEffect(() => {
    const src = fullAnalysis || analysisFullFixture;
    const arr = (src?.competitor_analysis?.competitor_details || []).slice(
      0,
      10,
    );
    setCompList(arr);
  }, [fullAnalysis]);

  // 右カラムAffixのボディ高さを動的に計算（フッターと被らないように調整）
  useEffect(() => {
    const recalc = () => {
      try {
        const wrap = stickyWrapRef.current;
        if (!wrap) return;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const footerEl = document.querySelector('footer');
        const affixTop = 16; // offsetTop と同じ
        const wrapTop = Math.max(
          affixTop,
          Math.floor(wrap.getBoundingClientRect().top),
        );
        let limit = vh;
        if (footerEl) {
          const fr = footerEl.getBoundingClientRect();
          if (fr.top > 0) limit = Math.min(limit, fr.top - 8);
        }
        const headerH = stickyHeaderRef.current
          ? Math.ceil(stickyHeaderRef.current.getBoundingClientRect().height)
          : 0;
        const contentPadding = 12; // body内の上下パディング合計目安
        const available = Math.max(0, limit - wrapTop);
        // モバイル時（800px以下）は最小高さを420pxに（1.5倍）
        const isMobile = window.innerWidth < 800;
        const minHeight = isMobile ? 420 : 160;
        const maxH = Math.max(
          minHeight,
          Math.floor(available - headerH - contentPadding),
        );
        setStickyBodyHeight(maxH);
      } catch (_) {
        // no-op
      }
    };
    recalc();
    window.addEventListener('scroll', recalc, { passive: true });
    window.addEventListener('resize', recalc);
    return () => {
      window.removeEventListener('scroll', recalc);
      window.removeEventListener('resize', recalc);
    };
  }, []);

  // ランダムトピックスコアは削除（scoringDataから取得）
  // avgScoresは競合平均なので別途計算（ヒートマップから）
  useEffect(() => {
    const nextAvg = {};
    const base = Array.isArray(fxTopics) ? fxTopics : [];
    base.forEach(t => {
      const key = t.topicId || t.name;
      if (!key) return;
      // 分析結果のaverage_scoreを直接使用
      const avgVal = Number(
        t?.average_score ?? t?.average_normalized_score ?? 0,
      );
      nextAvg[key] = Number.isFinite(avgVal) ? avgVal : 0;
    });
    setAvgScores(nextAvg);
  }, [fxTopics]);

  // 左側（構成案スコア）用: グレー濃淡（0に近いほど薄く）
  const getGrayShade = useCallback(v => {
    const t = Math.max(0, Math.min(1, (v - 0.1) / 9.9));
    const light = 85 - t * 55; // 85% → 30%
    return `hsl(0, 0%, ${light}%)`;
  }, []);
  // 左側高域カラー（4以上は従来の黄→ライム→緑）
  const getHighColor = useCallback(v => {
    if (v >= 8) return '#52c41a';
    if (v >= 6) return '#a0d911';
    // v >= 4
    return '#fadb14';
  }, []);
  // 右側（AVG）用: 黄色の濃淡（ヒートマップ系）
  // 分析詳細ヒートマップと同じ黄色系スケール（CompetitorAnalysisのgetHeatmapColorを簡約）
  const getYellowShade = useCallback(value => {
    if (value <= 0 || value < 1) return '#fffef7';
    const normalizedValue = Math.min(Math.max(value, 1), 10.0);
    const ratio = (normalizedValue - 1) / (10.0 - 1);
    if (ratio <= 0.1) return '#fffef9';
    if (ratio <= 0.2) return '#fffdf2';
    if (ratio <= 0.3) return '#fffbeb';
    if (ratio <= 0.4) return '#fff8e1';
    if (ratio <= 0.5) return '#fff5d6';
    if (ratio <= 0.6) return '#fff2cc';
    if (ratio <= 0.7) return '#ffefbf';
    if (ratio <= 0.8) return '#ffecb3';
    if (ratio <= 0.9) return '#ffe9a6';
    return '#ffec99';
  }, []);

  const handleRecomputeScores = () => {
    // スコアリング再計算はモーダルの再スコアリングボタンから実行
    // この関数は使用されていません（削除候補）
    console.warn('[Step3] handleRecomputeScores is deprecated');
  };

  // トピック対応状況（構成案に含まれているか）は sections 初期化後に算出する
  const effectiveOutline = useMemo(() => {
    return selectedOutlineFromServer || initialOutline;
  }, [selectedOutlineFromServer, initialOutline]);

  const initialSections = useMemo(() => {
    const rawItems = Array.isArray(effectiveOutline?.sections)
      ? effectiveOutline.sections
      : typeof effectiveOutline?.sections === 'string'
        ? effectiveOutline.sections.split(/\n+/)
        : [];

    // ⭐ working_draftなど、既に完全な構造（subsectionsを含む）を持っている場合
    if (
      rawItems.length > 0 &&
      rawItems[0] &&
      typeof rawItems[0] === 'object' &&
      rawItems[0].subsections !== undefined
    ) {
      // 必須フィールド（type, locked, memo）を追加
      return rawItems.map(section => ({
        ...section,
        locked: section.locked ?? false,
        memo: section.memo ?? '',
        subsections: (section.subsections || []).map(sub => ({
          ...sub,
          type: sub.type || 'heading',
          locked: sub.locked ?? false,
          memo: sub.memo ?? '',
        })),
      }));
    }

    const sections = [];
    let current = null;
    let anyMarked = false;
    const coerce = item => {
      if (item == null) return null;
      if (typeof item === 'string') {
        const s = String(item).trim();
        if (!s) return null;
        const m = /^(H[234])\s*:\s*(.*)$/.exec(s);
        if (m) return { level: m[1], title: m[2] };
        return { level: 'H2', title: s };
      }
      if (typeof item === 'object') {
        const lvlRaw = item.level || item.h || item.heading || item.type;
        let lv =
          typeof lvlRaw === 'number'
            ? `H${lvlRaw}`
            : String(lvlRaw || '').toUpperCase();
        if (!/^H[234]$/.test(lv)) lv = 'H2';
        const tt =
          item.title ||
          item.text ||
          item.name ||
          item.heading ||
          (item.content && item.content.title);
        const title = String(tt || '').trim();
        if (!title) return { level: lv, title: '' };
        return { level: lv, title };
      }
      return null;
    };
    rawItems.forEach(raw => {
      const coerced = coerce(raw);
      if (!coerced) return;
      const level = coerced.level;
      const title = coerced.title;
      // rawからIDを取得（既存のIDがあれば使用）
      const existingId = raw && typeof raw === 'object' ? raw.id : null;
      anyMarked = true;
      if (level === 'H2') {
        current = {
          id: existingId || generateHeadingId(), // ⭐ 既存ID優先、なければ新規生成
          title,
          locked: false,
          memo: '',
          subsections: [],
        };
        sections.push(current);
      } else if (level === 'H3') {
        if (!current) {
          current = {
            id: generateHeadingId(), // ⭐ 親見出しがない場合は新規生成
            title: 'セクション',
            locked: false,
            memo: '',
            subsections: [],
          };
          sections.push(current);
        }
        current.subsections.push({
          id: existingId || generateHeadingId(), // ⭐ 既存ID優先、なければ新規生成
          title,
          type: 'heading',
          locked: false,
          memo: '',
        });
      } else if (level === 'H4') {
        if (!current) {
          current = {
            id: generateHeadingId(), // ⭐ 親見出しがない場合は新規生成
            title: 'セクション',
            locked: false,
            memo: '',
            subsections: [],
          };
          sections.push(current);
        }
        current.subsections.push({
          id: existingId || generateHeadingId(), // ⭐ 既存ID優先、なければ新規生成
          title,
          type: 'h4',
          locked: false,
          memo: '',
        });
      }
    });
    if (!anyMarked && sections.length === 0) {
      rawItems.forEach(raw => {
        const title =
          typeof raw === 'object'
            ? String((raw && (raw.title || raw.text || raw.name)) || '').trim()
            : String(raw || '').trim();
        if (!title) return;
        sections.push({
          id: generateHeadingId(), // ⭐ 新しい短いIDを使用
          title,
          locked: false,
          memo: '',
          subsections: [],
        });
      });
    }
    return sections;
  }, [effectiveOutline]);

  // ⭐ initialSectionsからIDを自動付与してsectionsステートを生成
  const sectionsWithIds = useMemo(() => {
    const result = initialSections.map((section, idx) => {
      // 既存IDが古い形式（sec-xxx, sub-xxx, top-xxx）または未設定の場合、新しいIDを生成
      const needsNewId =
        !section.id ||
        section.id.startsWith('sec-') ||
        section.id.startsWith('sub-') ||
        section.id.startsWith('top-');

      const processed = {
        ...section,
        id: needsNewId ? generateHeadingId() : section.id,
        subsections: (section.subsections || []).map(sub => {
          const subNeedsNewId =
            !sub.id ||
            sub.id.startsWith('sec-') ||
            sub.id.startsWith('sub-') ||
            sub.id.startsWith('top-');
          // toppingDataのIDも変換
          const toppingData = sub.toppingData
            ? {
                ...sub.toppingData,
                id:
                  !sub.toppingData.id ||
                  String(sub.toppingData.id).startsWith('top-')
                    ? generateHeadingId()
                    : sub.toppingData.id,
              }
            : sub.toppingData;
          return {
            ...sub,
            id: subNeedsNewId ? generateHeadingId() : sub.id,
            toppingData,
          };
        }),
      };

      return processed;
    });

    return result;
  }, [initialSections]);

  // ⭐ sectionsはsectionsWithIdsから直接管理（ステート不要）
  const [sections, setSections] = useState(sectionsWithIds);

  // Undo/Redo履歴管理（最大10回分の変更を保持）
  const [sectionsHistory, setSectionsHistory] = useState([sectionsWithIds]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const isUndoRedoAction = useRef(false);
  const isInitialMount = useRef(true);
  const sectionsHistoryRef = useRef(sectionsHistory);
  const historyIndexRef = useRef(historyIndex);

  // Refを常に最新に保つ
  useEffect(() => {
    sectionsHistoryRef.current = sectionsHistory;
    historyIndexRef.current = historyIndex;
  }, [sectionsHistory, historyIndex]);

  // sectionsが変更されたら履歴に追加（Undo/Redo操作以外の時）
  // テキスト入力中は500msのdebounceを使って連続変更を防ぐ
  useEffect(() => {
    // 初回マウント時はスキップ
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }

    // 最新の履歴と同じ場合はスキップ
    const currentHistory = sectionsHistoryRef.current;
    const currentIndex = historyIndexRef.current;
    if (
      currentHistory[currentIndex] &&
      JSON.stringify(sections) === JSON.stringify(currentHistory[currentIndex])
    ) {
      return;
    }

    // 500msのdebounceで履歴追加（テキスト入力中の連続変更を防ぐ）
    const timer = setTimeout(() => {
      // 履歴とインデックスを同時に更新
      setSectionsHistory(prev => {
        const newHistory = prev.slice(0, currentIndex + 1);
        newHistory.push(sections);
        // 最大11個（初期状態+10回の変更）まで保持
        if (newHistory.length > 11) {
          newHistory.shift();
          // 先頭削除したのでインデックスは維持（最後を指す）
          setHistoryIndex(newHistory.length - 1);
        } else {
          // 通常時はインデックスを+1
          setHistoryIndex(newHistory.length - 1);
        }
        return newHistory;
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [sections]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0 && sectionsHistory.length > 0) {
      const newIndex = historyIndex - 1;
      const targetSections = sectionsHistory[newIndex];
      if (targetSections && targetSections.length > 0) {
        isUndoRedoAction.current = true;
        setHistoryIndex(newIndex);
        setSections(targetSections);
        message.info('元に戻しました');
      } else {
        message.warning('これ以上戻せません');
      }
    } else {
      message.warning('これ以上戻せません');
    }
  }, [historyIndex, sectionsHistory]);

  // Redo
  const redo = useCallback(() => {
    if (
      historyIndex < sectionsHistory.length - 1 &&
      sectionsHistory.length > 0
    ) {
      const newIndex = historyIndex + 1;
      const targetSections = sectionsHistory[newIndex];
      if (targetSections && targetSections.length > 0) {
        isUndoRedoAction.current = true;
        setHistoryIndex(newIndex);
        setSections(targetSections);
        message.info('やり直しました');
      } else {
        message.warning('これ以上進めません');
      }
    } else {
      message.warning('これ以上進めません');
    }
  }, [historyIndex, sectionsHistory]);

  // キーボードショートカット
  useEffect(() => {
    const handleKeyDown = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        // テキストモードのエディタにフォーカスがある場合は、ブラウザ標準のUndo/Redoに委譲
        const textArea = textAreaRef.current?.resizableTextArea?.textArea;
        if (textArea && document.activeElement === textArea) {
          // テキストエディタのUndo/Redoを優先
          return;
        }

        // パズルモードのUndo/Redo
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // H2見出しから「〇選」パターンを検出して展開数を抽出
  const extractCountFromTitle = title => {
    if (!title) return null;
    // 「5選」「10選」「３選」などのパターンを検出
    const match = title.match(/[0-9０-９]+選/);
    if (match) {
      // 全角数字を半角に変換
      const numStr = match[0]
        .replace('選', '')
        .replace(/[０-９]/g, s =>
          String.fromCharCode(s.charCodeAt(0) - 0xfee0),
        );
      return parseInt(numStr, 10);
    }
    return null;
  };

  // 展開セクションのautoExpansionを補完
  const normalizeAutoExpansion = sec => {
    if (!sec.isExpansionSection) return sec;

    const existingCfg = sec.autoExpansion || {};

    // countが未設定の場合、H2見出しから「〇選」を検出して設定
    let count = existingCfg.count;
    if (count === undefined || count === null) {
      const detectedCount = extractCountFromTitle(sec.title);
      count = detectedCount || 5; // 「〇選」パターンがなければデフォルト5
    }

    return {
      ...sec,
      autoExpansion: {
        enabled: existingCfg.enabled ?? true,
        sourceToppingId:
          existingCfg.sourceToppingId || sec.expansionTopping?.id,
        count,
        basis: existingCfg.basis || 'top',
        memo: existingCfg.memo || '',
      },
    };
  };

  // ⭐ sectionsWithIdsが変更されたら即座に反映
  useEffect(() => {
    // 展開セクションのautoExpansionを補完
    const normalizedSections = sectionsWithIds.map(normalizeAutoExpansion);
    setSections(normalizedSections);
  }, [sectionsWithIds]);


  // 現在の構成に含まれる見出し名（H2/H3/H4）集合（present判定用）
  const presentNameSet = useMemo(() => {
    try {
      const set = new Set();
      (sections || []).forEach(sec => {
        const h2 = String(sec?.title || '').trim();
        if (h2) set.add(h2);
        (sec?.subsections || []).forEach(sub => {
          const ttl = String(
            (sub?.title || sub?.toppingData?.title || '')
              .replace(/\s*\(H4\)\s*$/i, '')
              .trim(),
          );
          if (ttl) set.add(ttl);
        });
      });
      return set;
    } catch (_) {
      return new Set();
    }
  }, [sections]);
  const uidRef = React.useRef(1);
  // ⚠️ 非推奨: generateHeadingId() を使用してください
  const genId = prefix => {
    // 既存コードとの互換性のため残していますが、新規コードでは使用しないでください
    return generateHeadingId();
  };
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [sideMode, setSideMode] = useState('ai');
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState([]);
  const [contextModal, setContextModal] = useState({ open: false });
  const [contextSelections, setContextSelections] = useState({
    topics: new Set(),
    keywords: new Set(),
    competitors: new Set(),
    pastOutlines: new Set(),
  });
  const [pastOutlineSearch, setPastOutlineSearch] = useState('');
  const [pastOutlinePreview, setPastOutlinePreview] = useState({});
  const [pastPreviewOn, setPastPreviewOn] = useState(true);
  const [outlinePreviewOpen, setOutlinePreviewOpen] = useState(null);
  const messagesWrapRef = React.useRef(null);

  // チャット自動スクロール（AIタブ表示時）
  useEffect(() => {
    try {
      const wrap = messagesWrapRef.current;
      if (rightActive === 'ai' && wrap) {
        wrap.scrollTop = wrap.scrollHeight;
      }
    } catch (_) {
      void 0;
    }
  }, [aiMessages, rightActive]);
  const [pendingDelSec, setPendingDelSec] = useState(null);
  const [pendingDelSub, setPendingDelSub] = useState({ sec: null, sub: null });
  const [addModal, setAddModal] = useState({ open: false, secIndex: null });
  const [addModalAfterIndex, setAddModalAfterIndex] = useState(null);
  const [hoveredSectionId, setHoveredSectionId] = useState(null);
  const [hoveredSubId, setHoveredSubId] = useState(null);
  const [memoModal, setMemoModal] = useState({
    open: false,
    type: null,
    secIndex: null,
    subIndex: null,
    text: '',
  });
  const [hoverMode, setHoverMode] = useState(null);
  const [expandedSubsections, setExpandedSubsections] = useState({});
  const [hoverInsert, setHoverInsert] = useState({
    droppableId: null,
    index: null,
  });
  const [suppressSubTransitions, setSuppressSubTransitions] = useState(false);
  const [expansionModal, setExpansionModal] = useState({
    open: false,
    secIndex: null,
  });

  // トピック対応状況（構成案に含まれているか）を算出（fixturesベース）
  const topicsAnnotated = useMemo(() => {
    const sectionTitles = [
      ...sections.map(s => s.title || ''),
      ...sections.flatMap(s =>
        (s.subsections || []).map(sub => sub.title || ''),
      ),
    ];
    const allTitles = sectionTitles.join('\n');
    const baseTopics = Array.isArray(fxTopics) ? fxTopics : [];
    return baseTopics.map(t => {
      const name = t?.name || '';
      const present = !!(name && allTitles.includes(name));
      return { ...t, present };
    });
  }, [fxTopics, sections]);
  const [expansionConfig, setExpansionConfig] = useState({
    count: 5,
    basis: 'top',
    memo: '',
    h2Title: '',
  });
  const [dragGroup, setDragGroup] = useState({
    secIndex: null,
    start: null,
    end: null,
  });
  const [toppingModal, setToppingModal] = useState({
    open: false,
    secIndex: null,
    subIndex: null,
    data: null,
  });
  const [addRawModal, setAddRawModal] = useState({
    open: false,
    secIndex: null,
    afterIndex: null,
    html: '',
    text: '',
  });
  const [editorMode, setEditorMode] = useState('puzzle');
  const [editorText, setEditorText] = useState('');
  const [droppableVersion, setDroppableVersion] = useState({});
  const bumpDroppable = idxArray => {
    setDroppableVersion(prev => {
      const next = { ...prev };
      (idxArray || []).forEach(i => {
        if (typeof i === 'number' && i >= 0) {
          next[i] = (next[i] || 0) + 1;
        }
      });
      next._sections = (next._sections || 0) + 1;
      return next;
    });
  };
  // 初回マウント直後にDroppableを一度だけ再測定させ、初回ドラッグ時の無反応を防ぐ
  useEffect(() => {
    setDroppableVersion(prev => ({
      ...prev,
      _sections: (prev._sections || 0) + 1,
    }));
  }, []);
  const textAreaRef = React.useRef(null);
  const handleEditorKeyDown = e => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const value = String(editorText);
      const el = textAreaRef.current?.resizableTextArea?.textArea || e.target;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const before = value.slice(0, lineStart);
      const line = value.slice(lineStart, end);
      const after = value.slice(end);
      if (e.shiftKey) {
        const unindented = line.replace(/^\s{1,2}(-\s*)?/, m =>
          m.includes('-') ? m.slice(m.indexOf('-')) : '',
        );
        const delta = line.length - unindented.length;
        const nextValue = before + unindented + after;
        setEditorText(nextValue);
        const nextPos = Math.max(start - delta, lineStart);
        setTimeout(() => {
          const ta = textAreaRef.current?.resizableTextArea?.textArea;
          if (ta) ta.setSelectionRange(nextPos, nextPos);
        }, 0);
      } else {
        const indented = '  ' + line;
        const nextValue = before + indented + after;
        setEditorText(nextValue);
        const nextPos = start + 2;
        setTimeout(() => {
          const ta = textAreaRef.current?.resizableTextArea?.textArea;
          if (ta) ta.setSelectionRange(nextPos, nextPos);
        }, 0);
      }
    }
  };

  const serializeSectionsToText = () => {
    const lines = [];
    // H1はエディタでは出力しない
    sections.forEach(sec => {
      lines.push(`[H2] ${sec.title}`);
      // H2のメモがあれば見出しの下に出力
      if (sec.memo) {
        lines.push(`  [memo] ${sec.memo}`);
      }
      (sec.subsections || []).forEach(sub => {
        if (sub.type === 'heading') {
          lines.push(`  [H3] ${sub.title}`);
          // H3のメモがあれば見出しの下に出力
          if (sub.memo) {
            lines.push(`    [memo] ${sub.memo}`);
          }
        } else if (sub.type === 'h4') {
          lines.push(`    [H4] ${sub.title}`);
          // H4のメモがあれば見出しの下に出力
          if (sub.memo) {
            lines.push(`      [memo] ${sub.memo}`);
          }
        } else if (sub.type === 'topping') {
          // 独自情報の出力
          if (sub.placementLevel === 'h3') {
            lines.push(`  [H3-独自] ${sub.title}`);
            if (sub.memo) {
              lines.push(`    [memo] ${sub.memo}`);
            }
          } else if (sub.placementLevel === 'h3_child') {
            lines.push(`    [└H3] ${sub.title}`);
            if (sub.memo) {
              lines.push(`      [memo] ${sub.memo}`);
            }
          } else if (sub.placementLevel === 'h4_child') {
            lines.push(`      [└H4] ${sub.title}`);
            if (sub.memo) {
              lines.push(`        [memo] ${sub.memo}`);
            }
          } else {
            // placementLevelが設定されていない場合（後方互換性）
            lines.push(`  [独自] ${sub.title}`);
            if (sub.memo) {
              lines.push(`    [memo] ${sub.memo}`);
            }
          }
        }
      });
    });
    return lines.join('\n');
  };

  const parseEditorTextToSections = (text, existingSections = []) => {
    const out = [];
    let current = null;
    const rows = String(text || '').split('\n');

    // 既存のセクションをIDでマップ（タイトルマッチング用）
    const existingSectionsMap = new Map();
    existingSections.forEach(sec => {
      existingSectionsMap.set(sec.title.trim().toLowerCase(), sec);
    });

    // 直前の見出し/セクションへのメモ適用用
    let lastSection = null;
    let lastSubsection = null;

    for (let i = 0; i < rows.length; i += 1) {
      const rawLine = rows[i];
      if (!rawLine || /^\s*$/.test(rawLine)) continue;
      const mm = /^(\s*)(.*)$/.exec(rawLine);
      if (!mm) continue;
      const indent = (mm[1] || '').length; // 0,2,4,6...
      const restFull = (mm[2] || '').trim();
      if (!restFull) continue;

      // メモのパターンマッチング - 直前の見出しに適用
      const memoMatch = /^\[memo\]\s*(.*)$/.exec(restFull);
      if (memoMatch) {
        const memoText = memoMatch[1];
        // インデントに応じて適用先を判定
        if (indent >= 4 && lastSubsection) {
          // H3/H4/独自情報のメモ
          lastSubsection.memo = memoText;
        } else if (indent >= 2 && lastSubsection) {
          // H3レベルのメモ
          lastSubsection.memo = memoText;
        } else if (indent === 0 && lastSection) {
          // H2レベルのメモ
          lastSection.memo = memoText;
        }
        continue;
      }

      // 独自情報のパターンマッチング
      const toppingH3 = /^\[H3-独自\]\s*(.*)$/.exec(restFull); // H3レベルの独自情報
      const toppingH3Child = /^\[└H3\]\s*(.*)$/.exec(restFull); // H3直後の└
      const toppingH4Child = /^\[└H4\]\s*(.*)$/.exec(restFull); // H4直後の└
      const toppingGeneric = /^\[独自\]\s*(.*)$/.exec(restFull); // 旧形式（後方互換性）

      // 見出し接頭辞([H2]等)やコロン記法があってもタイトル抽出のみ使い、レベルはインデントで決定
      const bx = /^\[(H[234])\]\s*(.*)$/.exec(restFull);
      const cx = /^(H[234])\s*:\s*(.*)$/.exec(restFull);

      let title = restFull;
      if (toppingH3) {
        title = toppingH3[1];
      } else if (toppingH3Child) {
        title = toppingH3Child[1];
      } else if (toppingH4Child) {
        title = toppingH4Child[1];
      } else if (toppingGeneric) {
        title = toppingGeneric[1];
      } else if (bx) {
        title = bx[2];
      } else if (cx) {
        title = cx[2];
      }

      if (indent === 0) {
        // H2セクション - 既存データがあればマージ
        const existingSec = existingSectionsMap.get(title.trim().toLowerCase());
        current = {
          id: existingSec?.id || genId('sec'),
          title,
          locked: existingSec?.locked || false,
          memo: '', // テキストモードで明示的に指定されない限り空にする
          subsections: [],
          // 既存の追加フィールドを保持
          ...(existingSec && {
            isExpansionSection: existingSec.isExpansionSection,
            expansionTopping: existingSec.expansionTopping,
            autoExpansion: existingSec.autoExpansion,
          }),
        };
        out.push(current);
        lastSection = current;
        lastSubsection = null;
        continue;
      }
      if (!current) continue;

      // 既存のサブセクションをタイトルでマッチング
      const existingSubsections =
        existingSectionsMap.get(current.title.trim().toLowerCase())
          ?.subsections || [];
      const findExistingSubByTitle = subTitle => {
        return existingSubsections.find(
          s => s.title?.trim().toLowerCase() === subTitle.trim().toLowerCase(),
        );
      };

      // 独自情報の処理
      if (toppingH3) {
        const existingSub = findExistingSubByTitle(title);
        const newSub = {
          id: existingSub?.id || genId('sub'),
          title,
          type: 'topping',
          placementLevel: 'h3',
          locked: existingSub?.locked || false,
          memo: '', // テキストモードで明示的に指定されない限り空にする
          toppingData: existingSub?.toppingData, // 既存データを保持
          selectedGlobalToppings: existingSub?.selectedGlobalToppings,
          supplement: existingSub?.supplement,
        };
        current.subsections.push(newSub);
        lastSubsection = newSub;
        continue;
      }
      if (toppingH3Child || (toppingGeneric && indent === 4)) {
        const existingSub = findExistingSubByTitle(title);
        const newSub = {
          id: existingSub?.id || genId('sub'),
          title,
          type: 'topping',
          placementLevel: 'h3_child',
          locked: existingSub?.locked || false,
          memo: '', // テキストモードで明示的に指定されない限り空にする
          toppingData: existingSub?.toppingData, // 既存データを保持
          selectedGlobalToppings: existingSub?.selectedGlobalToppings,
          supplement: existingSub?.supplement,
        };
        current.subsections.push(newSub);
        lastSubsection = newSub;
        continue;
      }
      if (toppingH4Child || (toppingGeneric && indent === 6)) {
        const existingSub = findExistingSubByTitle(title);
        const newSub = {
          id: existingSub?.id || genId('sub'),
          title,
          type: 'topping',
          placementLevel: 'h4_child',
          locked: existingSub?.locked || false,
          memo: '', // テキストモードで明示的に指定されない限り空にする
          toppingData: existingSub?.toppingData, // 既存データを保持
          selectedGlobalToppings: existingSub?.selectedGlobalToppings,
          supplement: existingSub?.supplement,
        };
        current.subsections.push(newSub);
        lastSubsection = newSub;
        continue;
      }

      // 通常の見出しの処理
      if (indent === 2) {
        const existingSub = findExistingSubByTitle(title);
        const newSub = {
          id: existingSub?.id || genId('sub'),
          title,
          type: existingSub?.type || 'heading',
          placementLevel: existingSub?.placementLevel || '',
          locked: existingSub?.locked || false,
          memo: '', // テキストモードで明示的に指定されない限り空にする
          // 既存の追加フィールドを保持
          ...(existingSub && {
            toppingData: existingSub.toppingData,
            selectedGlobalToppings: existingSub.selectedGlobalToppings,
            supplement: existingSub.supplement,
            content: existingSub.content,
            raw: existingSub.raw,
          }),
        };
        current.subsections.push(newSub);
        lastSubsection = newSub;
        continue;
      }
      if (indent >= 4) {
        const existingSub = findExistingSubByTitle(title);
        const newSub = {
          id: existingSub?.id || genId('sub'),
          title,
          type: existingSub?.type || 'h4',
          placementLevel: existingSub?.placementLevel || '',
          locked: existingSub?.locked || false,
          memo: '', // テキストモードで明示的に指定されない限り空にする
          // 既存の追加フィールドを保持
          ...(existingSub && {
            toppingData: existingSub.toppingData,
            selectedGlobalToppings: existingSub.selectedGlobalToppings,
            supplement: existingSub.supplement,
            content: existingSub.content,
            raw: existingSub.raw,
          }),
        };
        current.subsections.push(newSub);
        lastSubsection = newSub;
        continue;
      }
    }
    return out;
  };
  const [rightToppings, setRightToppings] = useState([]);
  const [toppingsLoading, setToppingsLoading] = useState(false);

  // 実際のユーザーの有効な独自情報を取得（アクティブなもののみ）
  useEffect(() => {
    if (isGuest) return;
    const fetchUserToppings = async () => {
      setToppingsLoading(true);
      try {
        // is_active=true でアクティブなトッピングのみ取得
        const response = await v2ToppingsApi.getToppings({ is_active: true });
        const toppingsData = response.data || [];

        // Step3で使いやすい形式に変換（全データを保持）
        const formattedToppings = toppingsData.map(topping => {
          // contentフィールドを保持しつつ、表示用のcontentオブジェクトも作成
          const originalContent = topping.content;
          return {
            ...topping, // すべてのフィールドを保持
            id: topping.id,
            title: topping.title || 'タイトルなし',
            category: topping.category || 'other',
            // 元のcontentを保持（文字列のまま）
            content: originalContent || '',
            // 表示用のsummaryを別フィールドに
            displaySummary: topping.summary || topping.description || '',
          };
        });

        setRightToppings(formattedToppings);
      } catch (error) {
        console.error('[Step3] Failed to fetch user toppings:', error);
        message.warning('独自情報の取得に失敗しました。');
        setRightToppings([]);
      } finally {
        setToppingsLoading(false);
      }
    };

    fetchUserToppings();
  }, []);
  const [titleH1, setTitleH1] = useState(
    (typeof effectiveOutline?.title === 'string' && effectiveOutline.title) ||
      (typeof keyword === 'string' && keyword) ||
      'タイトル',
  );

  // オートセーブ（1分ごと）
  useEffect(() => {
    if (!uuid) return;

    // 閲覧専用モードでは自動保存をスキップ
    if (isReadOnly) {
      return;
    }

    // 初回マウント時はスキップ
    if (isInitialMount.current) {
      return;
    }

    // Undo/Redo操作時はスキップ
    if (isUndoRedoAction.current) {
      return;
    }

    // 自動保存関数
    const autoSave = async () => {
      try {
        const currentData = JSON.stringify({
          title: titleH1,
          sections,
          memo: outlineMemo,
        });

        // 前回保存時と同じ内容ならスキップ
        if (currentData === lastSavedData) {
          return;
        }

        setAutoSaveStatus('saving');

        await v2ArticlesApi.saveOutlineDraft(uuid, {
          title: titleH1,
          sections,
          memo: outlineMemo,
        });

        setLastSavedData(currentData);
        setAutoSaveStatus('saved');

        // 2秒後に「saved」状態をクリア
        setTimeout(() => {
          setAutoSaveStatus('idle');
        }, 2000);
      } catch (error) {
        console.error('[Step3][AutoSave] Failed:', error);
        setAutoSaveStatus('idle');
        // エラー時は静かに失敗（ユーザーには通知しない）
      }
    };

    // 1分ごとに自動保存
    const intervalId = setInterval(autoSave, 60000);

    // クリーンアップ
    return () => clearInterval(intervalId);
  }, [sections, titleH1, outlineMemo, uuid, lastSavedData]);

  // ⭐ 旧ロジック: initialSections/sectionsWithIdsで処理済みのため無効化
  // 選択アウトライン取得後にタイトル/セクションをリセット（初回のみ）
  const initialSyncDoneRef = React.useRef(false);
  useEffect(() => {
    // ⭐ この同期ロジックは無効化（新しいinitialSections/sectionsWithIdsロジックを使用）
    // 古いロジックがsectionsを上書きするのを防ぐため、常にreturnで早期終了
    // eslint-disable-next-line no-constant-condition
    if (true) return;

    if (
      !initialSyncDoneRef.current &&
      (selectedOutlineFromServer || initialOutline)
    ) {
      // タイトル同期
      const nextTitle =
        selectedOutlineFromServer?.title ||
        initialOutline?.title ||
        keyword ||
        'タイトル';
      setTitleH1(nextTitle);
      // セクション同期
      const rawItems = Array.isArray(effectiveOutline?.sections)
        ? effectiveOutline.sections
        : typeof effectiveOutline?.sections === 'string'
          ? effectiveOutline.sections.split(/\n+/)
          : [];
      if (Array.isArray(rawItems) && rawItems.length) {
        const rebuilt = [];
        let cur = null;
        let anyMarked = false;
        const coerce = item => {
          if (item == null) return null;
          if (typeof item === 'string') {
            const s = String(item).trim();
            if (!s) return null;
            const m = /^(H[234])\s*:\s*(.*)$/.exec(s);
            if (m) return { level: m[1], title: m[2] };
            return { level: 'H2', title: s };
          }
          if (typeof item === 'object') {
            const lvlRaw = item.level || item.h || item.heading || item.type;
            let lv =
              typeof lvlRaw === 'number'
                ? `H${lvlRaw}`
                : String(lvlRaw || '').toUpperCase();
            if (!/^H[234]$/.test(lv)) lv = 'H2';
            const tt =
              item.title ||
              item.text ||
              item.name ||
              item.heading ||
              (item.content && item.content.title);
            const title = String(tt || '').trim();
            if (!title) return { level: lv, title: '' };
            return { level: lv, title };
          }
          return null;
        };
        rawItems.forEach(raw => {
          const coerced = coerce(raw);
          if (!coerced) return;
          const level = coerced.level;
          const ttl = coerced.title;
          anyMarked = true;
          if (level === 'H2') {
            cur = {
              id: genId('sec'),
              title: ttl,
              locked: false,
              memo: '',
              subsections: [],
            };
            rebuilt.push(cur);
          } else if (level === 'H3') {
            if (!cur) {
              cur = {
                id: genId('sec'),
                title: 'セクション',
                locked: false,
                memo: '',
                subsections: [],
              };
              rebuilt.push(cur);
            }
            cur.subsections.push({
              id: genId('sub'),
              title: ttl,
              type: 'heading',
              locked: false,
              memo: '',
            });
          } else if (level === 'H4') {
            if (!cur) {
              cur = {
                id: genId('sec'),
                title: 'セクション',
                locked: false,
                memo: '',
                subsections: [],
              };
              rebuilt.push(cur);
            }
            cur.subsections.push({
              id: genId('sub'),
              title: ttl,
              type: 'h4',
              locked: false,
              memo: '',
            });
          }
        });
        if (!anyMarked && rebuilt.length === 0) {
          rawItems.forEach(raw => {
            const ttl =
              typeof raw === 'object'
                ? String(
                    (raw && (raw.title || raw.text || raw.name)) || '',
                  ).trim()
                : String(raw || '').trim();
            if (!ttl) return;
            rebuilt.push({
              id: genId('sec'),
              title: ttl,
              locked: false,
              memo: '',
              subsections: [],
            });
          });
        }
        if (rebuilt.length) setSections(rebuilt);
      }
      initialSyncDoneRef.current = true;
    }
  }, [selectedOutlineFromServer, initialOutline, effectiveOutline, keyword]);

  // 二度押し用の状態は上で宣言済み
  const [confirmTimers, setConfirmTimers] = useState([]);

  useEffect(
    () => () => {
      // クリーンアップ: タイマー解除
      confirmTimers.forEach(t => clearTimeout(t));
    },
    [confirmTimers],
  );

  const clearConfirmLater = () => {
    const timer = setTimeout(() => {
      setPendingDelSec(null);
      setPendingDelSub({ sec: null, sub: null });
    }, 3000);
    setConfirmTimers(prev => [...prev, timer]);
  };

  const getSectionIndexByDroppableId = droppableId => {
    if (droppableId.startsWith('section-')) {
      const idx = parseInt(droppableId.split('-')[1], 10);
      return Number.isNaN(idx) ? -1 : idx;
    }
    if (droppableId.startsWith('sec-slot-')) {
      const idx = parseInt(
        droppableId.split('-')[2] ?? droppableId.split('-')[1],
        10,
      );
      return Number.isNaN(idx) ? -1 : idx;
    }
    return -1;
  };

  const [isDraggingSub, setIsDraggingSub] = useState(false);
  const [subTransMode, setSubTransMode] = useState('baseline'); // 'baseline' | 'suppress' | 'smooth'
  const [h3SlotHoverSecIndex, setH3SlotHoverSecIndex] = useState(null);
  const [secHoverByPointer, setSecHoverByPointer] = useState(null);
  const pointerMoveHandlerRef = React.useRef(null);

  const [isDraggingSection, setIsDraggingSection] = useState(false);
  const [combineTargetId, setCombineTargetId] = useState(null);

  useEffect(() => {
    const nudge = () => {
      if (!isDraggingSub) return;
      // 実スクロールでDnDの最終処理を強制（1px動かして戻す）
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;
      window.scrollTo(0, y + 1);
      requestAnimationFrame(() => window.scrollTo(0, y));
    };
    window.addEventListener('pointerup', nudge, true);
    window.addEventListener('touchend', nudge, true);
    return () => {
      window.removeEventListener('pointerup', nudge, true);
      window.removeEventListener('touchend', nudge, true);
    };
  }, [isDraggingSub]);

  const onDragStart = start => {
    if (start?.type === 'SUBSECTION') {
      setIsDraggingSub(true);
      if (start?.source?.droppableId === 'tops-list') {
        setSubTransMode('smooth');
      } else {
        // H3ドラッグ時もsmoothに変更（アニメーション改善）
        setSubTransMode('smooth');
      }
      // グループ範囲を計算して、元リスト側の子要素を非表示にする
      const srcId = start?.source?.droppableId || '';
      const secIndex = getSectionIndexByDroppableId(srcId);
      const startIndex = start?.source?.index ?? -1;
      if (secIndex >= 0 && startIndex >= 0) {
        const subs = sections[secIndex]?.subsections || [];
        let endIndex = startIndex;
        const headType = subs[startIndex]?.type;
        if (headType === 'heading' || headType === 'h4') {
          for (let i = startIndex + 1; i < subs.length; i += 1) {
            const nx = subs[i];
            if (nx?.type === 'heading') break; // H3が来たら終了
            if (headType === 'h4' && nx?.type === 'h4') break; // 次のH4が来たら終了
            endIndex = i;
          }
        }
        setDragGroup({ secIndex, start: startIndex, end: endIndex });
      }
    }
    if (start?.type === 'SECTION') {
      setIsDraggingSection(true);
      // H2カードのRect一覧を取り、pointer座標でHoverセクションを特定
      const onMove = e => {
        try {
          const cards = Array.from(
            document.querySelectorAll('[data-sec-card-index]'),
          );
          const y = e.clientY;
          let found = null;
          for (const el of cards) {
            const rect = el.getBoundingClientRect();
            if (y >= rect.top && y <= rect.bottom) {
              const idx = parseInt(el.getAttribute('data-sec-card-index'), 10);
              if (!Number.isNaN(idx)) {
                found = idx;
                break;
              }
            }
          }
          setSecHoverByPointer(found);
          // setH3SlotHoverSecIndexはonDragUpdateのcombineで管理するため、ここでは不要
        } catch (err) {
          // noop
        }
      };
      pointerMoveHandlerRef.current = onMove;
      window.addEventListener('pointermove', onMove, { passive: true });
    }
  };

  const onDragEnd = result => {
    // プレビュー状態をクリア
    setH3SlotHoverSecIndex(null);
    setCombineTargetId(null);

    const { source, destination, combine, type } = result;

    // トピックからのドラッグ処理（source.droppableIdで判定）
    if (
      type === 'SUBSECTION' &&
      source &&
      source.droppableId &&
      source.droppableId.startsWith('topic-group-') &&
      result.draggableId &&
      result.draggableId.startsWith('topic-')
    ) {
      if (!destination) return;

      // トピック情報を取得
      const topicName = result.draggableId.replace('topic-', '');
      let topicData = null;
      for (const group of groupedTopics || []) {
        const found = (group.items || []).find(
          item => (item.topicId || item.name || item.keyword) === topicName,
        );
        if (found) {
          topicData = found;
          break;
        }
      }

      if (!topicData) {
        message.warning('トピック情報が見つかりません');
        return;
      }

      const topicTitle =
        topicData.name || topicData.topic || topicData.keyword || 'トピック';

      // destination.droppableIdから対象セクションを特定
      if (
        destination &&
        destination.droppableId &&
        destination.droppableId.startsWith('sec-slot-')
      ) {
        // H2間スロットへドロップ→新しいH2として追加
        const secIndex = parseInt(
          destination.droppableId.replace('sec-slot-', ''),
          10,
        );
        const newSection = {
          id: genId('sec'),
          title: topicTitle,
          locked: false,
          memo: '',
          subsections: [],
        };
        const newSections = [...sections];
        newSections.splice(secIndex, 0, newSection);
        setSections(newSections);
        message.success(`トピック「${topicTitle}」をH2として追加しました`);
      } else if (
        destination &&
        destination.droppableId &&
        (destination.droppableId.startsWith('section-') ||
          destination.droppableId.startsWith('section:'))
      ) {
        // セクション内へドロップ→H3として追加
        const dstSecIndex = getSectionIndexByDroppableId(
          destination.droppableId,
        );
        if (dstSecIndex !== -1) {
          const newSub = {
            id: genId('sub'),
            title: topicTitle,
            type: 'heading',
            locked: false,
            memo: '',
          };
          const newSections = [...sections];
          const dstSubs = Array.from(
            newSections[dstSecIndex].subsections || [],
          );
          dstSubs.splice(destination.index, 0, newSub);
          newSections[dstSecIndex].subsections = dstSubs;
          setSections(newSections);
          message.success(`トピック「${topicTitle}」をH3として追加しました`);
        }
      }
      return;
    }

    // ドラッグ状態解除とtransition再有効化は次フレームで
    if (type === 'SUBSECTION') {
      setIsDraggingSub(false);
      // トッピングドラッグ/見出しドラッグどちらも終了時に基準へ戻す
      setTimeout(() => setSubTransMode('baseline'), 0);
    }
    // setH3SlotHoverSecIndexは既にonDragEndの最初でクリアしているので、ここでは不要
    if (pointerMoveHandlerRef.current) {
      window.removeEventListener('pointermove', pointerMoveHandlerRef.current);
      pointerMoveHandlerRef.current = null;
    }
    if (type === 'SUBSECTION')
      setDragGroup({ secIndex: null, start: null, end: null });
    if (type === 'SECTION') setIsDraggingSection(false);

    // SECTION 並び替え
    if (type === 'SECTION') {
      // combineで他H2に重ねた場合→その配下のH3へ降格（combineがある場合は優先）
      if (combine && combine.draggableId) {
        const list = Array.from(sections);
        const targetIdx = list.findIndex(s => s.id === combine.draggableId);
        if (targetIdx >= 0) {
          const [sec] = list.splice(source.index, 1);
          // source.indexの削除後、targetIdxを調整
          const adjustedTargetIdx =
            source.index < targetIdx ? targetIdx - 1 : targetIdx;
          if (sec && list[adjustedTargetIdx]) {
            const demoted = {
              id: genId('sub'),
              title: sec.title,
              type: 'heading',
              locked: false,
              memo: sec.memo || '',
            };
            const childSubs = (sec.subsections || []).map(s =>
              s.type === 'heading'
                ? { ...s, id: genId('sub'), type: 'h4' }
                : { ...s, id: genId('sub') },
            );
            const dstSubs = Array.from(
              list[adjustedTargetIdx].subsections || [],
            );
            // 末尾に追加（統一化）
            dstSubs.push(demoted, ...childSubs);
            list[adjustedTargetIdx] = {
              ...list[adjustedTargetIdx],
              subsections: dstSubs,
            };
            flushSync(() => setSections([...list]));
            return;
          }
        }
      }

      // H2 → 他H2配下のH3として配置（h3-slot-*）
      // combine処理が既に実行されていれば、ここには到達しない
      if (
        !combine &&
        (combineTargetId || typeof secHoverByPointer === 'number')
      ) {
        const list = Array.from(sections);
        const dstSecIndex =
          typeof secHoverByPointer === 'number'
            ? secHoverByPointer
            : list.findIndex(s => s.id === combineTargetId);
        const [sec] = list.splice(source.index, 1);
        if (!sec || !list[dstSecIndex]) {
          console.error('[DND] H2→H3エラー: secまたはdstSecIndexが無効', {
            hasSec: !!sec,
            dstSecIndex,
            listLength: list.length,
          });
          return;
        }
        const demoted = {
          id: genId('sub'),
          title: sec.title,
          type: 'heading',
          locked: false,
          memo: sec.memo || '',
        };
        const childSubs = (sec.subsections || []).map(s => {
          if (s.type === 'heading') {
            return { ...s, id: genId('sub'), type: 'h4' };
          }
          return { ...s, id: genId('sub') };
        });
        const dstSubs = Array.from(list[dstSecIndex].subsections || []);
        // 末尾にH3として挿入（指定どおり末尾）
        dstSubs.push(demoted, ...childSubs);
        list[dstSecIndex] = { ...list[dstSecIndex], subsections: dstSubs };
        flushSync(() => setSections([...list]));
        setSecHoverByPointer(null);
        setCombineTargetId(null);
        return;
      }

      // H2の並び替え（destinationがある場合のみ）
      if (destination) {
        const list = Array.from(sections);
        const [moved] = list.splice(source.index, 1);
        list.splice(destination.index, 0, moved);
        flushSync(() => setSections([...list]));
        return;
      }

      // destination も combine もない場合は何もしない
      return;
    }

    // 右ペイン（独自情報）内での並び替え
    if (
      source &&
      source.droppableId === 'tops-list' &&
      destination &&
      destination.droppableId === 'tops-list'
    ) {
      const list = Array.from(rightToppings);
      const [moved] = list.splice(source.index, 1);
      list.splice(destination.index, 0, moved);
      setRightToppings(list);
      return;
    }

    // 右→左 への独自情報ドロップ（挿入）
    if (
      source &&
      source.droppableId === 'tops-list' &&
      destination &&
      destination.droppableId &&
      (destination.droppableId.startsWith('section-') ||
        destination.droppableId.startsWith('section:'))
    ) {
      const dstSecIndex = getSectionIndexByDroppableId(destination.droppableId);
      if (dstSecIndex < 0) return;
      const list = Array.from(sections);
      const dstSubs = Array.from(list[dstSecIndex].subsections);
      const top = rightToppings[source.index];
      // H4に属する場合の挿入位置調整：
      // ドロップ位置の直前にH4がある場合、そのH4ブロック（H4見出し～次の見出し/H4の直前）
      // の末尾に配置して"子要素"として見えるようにする
      let insertAt = Math.max(0, Math.min(destination.index, dstSubs.length));
      const findNearestH4Index = idx => {
        const start = Math.min(idx, Math.max(0, dstSubs.length - 1));
        for (let i = start; i >= 0; i -= 1) {
          if (dstSubs[i]?.type === 'h4') return i;
          if (dstSubs[i]?.type === 'heading') break; // H3を跨いだら終了
        }
        return -1;
      };
      const h4Idx = findNearestH4Index(insertAt - 1);
      if (h4Idx >= 0) {
        let end = h4Idx + 1;
        while (
          end < dstSubs.length &&
          dstSubs[end]?.type !== 'heading' &&
          dstSubs[end]?.type !== 'h4'
        ) {
          end += 1;
        }
        insertAt = end;
      }
      // 挿入位置の直前の要素を確認して、placementLevelを決定
      let placementLevel = 'h3'; // デフォルトはH3レベル

      // 直前の要素を確認（insertAt - 1）
      const prevItem = insertAt > 0 ? dstSubs[insertAt - 1] : null;

      if (h4Idx >= 0) {
        // H4配下に置かれる場合（H4直後の└）
        placementLevel = 'h4_child';
      } else if (prevItem) {
        // H4配下でない場合、直前の要素を確認
        if (prevItem.type === 'heading') {
          // H3直後の場合（H3直後の└）
          placementLevel = 'h3_child';
        } else if (prevItem.type === 'h4') {
          // H4直後の場合（H4直後の└）- 念のため
          placementLevel = 'h4_child';
        }
        // prevItem.type === 'topping' の場合は、既存の独自情報の後なので、H3レベルを維持
      }

      const payload = {
        id: genId('sub'),
        title: top.title,
        type: 'topping',
        locked: false,
        memo: '',
        toppingData: top,
        placementLevel: placementLevel,
      };
      dstSubs.splice(insertAt, 0, payload);
      list[dstSecIndex] = {
        ...list[dstSecIndex],
        subsections: dstSubs,
      };
      flushSync(() => setSections([...list]));
      // 独自情報挿入時はH3の移動をスムーズに
      setSubTransMode('smooth');
      setTimeout(() => setSubTransMode('baseline'), 200);
      return;
    }

    // H2間スロット/最後尾スロットへのドロップ（新規H2 もしくは 展開モードH2）
    if (
      destination &&
      destination.droppableId &&
      (destination.droppableId.startsWith('sec-slot-') ||
        destination.droppableId.startsWith('sec-slot:') ||
        destination.droppableId === 'sec-slot-end')
    ) {
      // 右ペインの独自情報 → 新規H2（展開モード）
      if (source && source.droppableId === 'tops-list') {
        const top = rightToppings[source.index];
        if (!top) return;
        const list = Array.from(sections);
        const insertAt =
          destination.droppableId === 'sec-slot-end'
            ? list.length
            : Math.max(
                0,
                Math.min(
                  getSectionIndexByDroppableId(destination.droppableId),
                  list.length,
                ),
              );
        const newSec = {
          id: genId('sec'),
          title: top.title,
          locked: false,
          memo: '',
          isExpansionSection: true,
          expansionTopping: top,
          autoExpansion: {
            enabled: true,
            sourceToppingId: top.id,
            count: 5, // デフォルト展開数
            basis: 'top', // デフォルト: 重要順
            memo: '',
          },
          subsections: [],
        };
        list.splice(insertAt, 0, newSec);
        flushSync(() => setSections([...list]));
        return;
      }

      // 左ペインの見出し（H3/H4）をドロップ → 新規H2として配置
      if (
        source &&
        source.droppableId &&
        (source.droppableId.startsWith('section-') ||
          source.droppableId.startsWith('section:'))
      ) {
        const srcSecIndex = getSectionIndexByDroppableId(source.droppableId);
        if (srcSecIndex < 0) {
          console.error('[DND] H3→H2エラー: srcSecIndexが無効', {
            srcSecIndex,
          });
          return;
        }
        const list = Array.from(sections);
        const srcSubs = Array.from(list[srcSecIndex].subsections);
        const moved = srcSubs[source.index];
        if (!moved) return;
        // グループ（H3＋子）をまとめて取り除く
        const group = [moved];
        if (moved.type === 'heading') {
          for (let i = source.index + 1; i < srcSubs.length; i += 1) {
            const nx = srcSubs[i];
            if (nx.type === 'heading') break;
            group.push(nx);
          }
        }
        srcSubs.splice(source.index, group.length);
        list[srcSecIndex] = { ...list[srcSecIndex], subsections: srcSubs };

        const insertAt =
          destination.droppableId === 'sec-slot-end'
            ? list.length
            : Math.max(
                0,
                Math.min(
                  getSectionIndexByDroppableId(destination.droppableId),
                  list.length,
                ),
              );

        if (moved.type === 'topping') {
          const top = moved.toppingData;
          const newSec = {
            id: genId('sec'),
            title: top?.title || 'セクション',
            locked: false,
            memo: '',
            isExpansionSection: true,
            expansionTopping: top || null,
            autoExpansion: {
              enabled: true,
              sourceToppingId: top?.id,
              count: 5, // デフォルト展開数
              basis: 'top', // デフォルト: 重要順
              memo: '',
            },
            subsections: [],
          };
          list.splice(insertAt, 0, newSec);
          flushSync(() => setSections([...list]));
        } else {
          // H3/H4 → 新規H2。H3の場合は子要素を昇格して配下に連結。
          const newSec = {
            id: genId('sec'),
            title: moved.title,
            locked: false,
            memo: moved.memo || '',
            subsections: [],
          };
          list.splice(insertAt, 0, newSec);
          if (moved.type === 'heading' && group.length > 1) {
            const elevates = group.slice(1).map(g => {
              if (g.type === 'h4')
                return { ...g, id: genId('sub'), type: 'heading' };
              return { ...g, id: genId('sub') };
            });
            list[insertAt] = { ...list[insertAt], subsections: elevates };
          }
        }
        flushSync(() => setSections([...list]));
        return;
      }
    }

    // SUBSECTION（左ペイン）内/間の移動 + 入れ子位置でレベル変更（H3/H4）
    if (
      source &&
      source.droppableId &&
      destination &&
      destination.droppableId &&
      (source.droppableId.startsWith('section-') ||
        source.droppableId.startsWith('section:')) &&
      (destination.droppableId.startsWith('section-') ||
        destination.droppableId.startsWith('section:'))
    ) {
      const srcSecIndex = getSectionIndexByDroppableId(source.droppableId);
      const dstSecIndex = getSectionIndexByDroppableId(destination.droppableId);
      if (srcSecIndex < 0 || dstSecIndex < 0) return;
      const list = Array.from(sections);
      const srcSubs = Array.from(list[srcSecIndex].subsections);
      const moved = srcSubs[source.index];
      if (!moved) return;

      let group = [moved];
      if (moved.type === 'heading') {
        for (let i = source.index + 1; i < srcSubs.length; i += 1) {
          const nxt = srcSubs[i];
          if (nxt.type === 'heading') break;
          group.push(nxt);
        }
      }
      srcSubs.splice(source.index, group.length);
      list[srcSecIndex] = { ...list[srcSecIndex], subsections: srcSubs };

      const dstSubs = Array.from(list[dstSecIndex].subsections);
      const insertAt = Math.min(Math.max(destination.index, 0), dstSubs.length);
      dstSubs.splice(insertAt, 0, ...group);
      list[dstSecIndex] = { ...list[dstSecIndex], subsections: dstSubs };
      flushSync(() => setSections([...list]));
      return;
    }
  };

  const onDragUpdate = update => {
    const { destination, combine, type } = update || {};

    if (type === 'SECTION') {
      // destination がある場合はH2並び替えなので、プレビューをクリア
      if (destination) {
        if (h3SlotHoverSecIndex !== null) {
          setH3SlotHoverSecIndex(null);
        }
        setCombineTargetId(null);
      } else if (combine && combine.draggableId) {
        setCombineTargetId(combine.draggableId);
        // combine対象のsecIndexを見つけてプレビュー表示
        const targetSecIndex = sections.findIndex(
          s => s.id === combine.draggableId,
        );
        if (targetSecIndex >= 0) {
          setH3SlotHoverSecIndex(targetSecIndex);
        }
      } else if (combine === null && !destination) {
        // combineがnullで、destinationもない場合のみプレビューを保持
        setCombineTargetId(null);
      }
    }
  };

  const duplicateSection = index => {
    const list = Array.from(sections);
    const base = list[index];
    const copy = {
      ...base,
      id: generateHeadingId(), // ⭐ D&D + アクションプラン用の一意ID
      title: `${base.title}（複製）`,
      subsections: base.subsections.map(s => ({
        ...s,
        id: generateHeadingId(), // ⭐ D&D + アクションプラン用の一意ID
      })),
    };
    list.splice(index + 1, 0, copy);
    setSections(list);
  };

  const updateSectionAutoExpansion = (secIndex, partial) => {
    const list = Array.from(sections);
    const current = list[secIndex].autoExpansion || {};
    list[secIndex].autoExpansion = { ...current, ...partial };
    setSections(list);
  };
  const deleteSection = index => {
    const list = Array.from(sections);
    list.splice(index, 1);
    setSections(list);
  };
  const updateSectionTitle = (index, value) => {
    const list = Array.from(sections);
    list[index] = { ...list[index], title: value };
    setSections(list);
  };
  const toggleLockSection = index => {
    const list = Array.from(sections);
    const nextLocked = !list[index].locked;
    list[index] = { ...list[index], locked: nextLocked };
    // 子要素側の個別overrideは一旦リセットし、親に連動させる
    list[index].subsections = (list[index].subsections || []).map(sub => {
      const { lockOverride, ...rest } = sub;
      return { ...rest };
    });
    setSections(list);
  };
  const duplicateSubsection = (secIndex, subIndex) => {
    const list = Array.from(sections);
    const base = list[secIndex].subsections[subIndex];
    const copy = { ...base, id: generateHeadingId() }; // ⭐ 新規IDを生成
    list[secIndex].subsections.splice(subIndex + 1, 0, copy);
    setSections(list);
  };
  const addSubToSection = (index, level = 'heading') => {
    const list = Array.from(sections);
    list[index].subsections.push({
      id: generateHeadingId(), // ⭐ ランダムIDを付与
      title: level === 'h4' ? '新しい小見出し（H4）' : '新しい小見出し',
      type: level,
      locked: false,
      memo: '',
    });
    setSections(list);
  };
  const addSubAfter = (secIndex, afterSubIndex, level = 'heading') => {
    const list = Array.from(sections);
    const payload = {
      id: generateHeadingId(), // ⭐ ランダムIDを付与
      title: level === 'h4' ? '新しい小見出し（H4）' : '新しい小見出し',
      type: level,
      locked: false,
      memo: '',
    };
    const insertAt =
      typeof afterSubIndex === 'number'
        ? afterSubIndex + 1
        : list[secIndex].subsections.length;
    list[secIndex].subsections.splice(insertAt, 0, payload);
    setSections(list);
  };
  const insertTopping = (secIndex, topping) => {
    const list = Array.from(sections);
    const subsections = list[secIndex].subsections;

    // 末尾に追加する場合、最後の要素を確認してplacementLevelを決定
    let placementLevel = 'h3'; // デフォルトはH3レベル

    if (subsections && subsections.length > 0) {
      const lastItem = subsections[subsections.length - 1];
      if (lastItem.type === 'heading') {
        // H3直後の場合（H3直後の└）
        placementLevel = 'h3_child';
      } else if (lastItem.type === 'h4') {
        // H4直後の場合（H4直後の└）
        placementLevel = 'h4_child';
      }
      // lastItem.type === 'topping' の場合は、既存の独自情報の後なので、H3レベルを維持
    }

    list[secIndex].subsections.push({
      id: genId('sub'),
      title: topping.title,
      type: 'topping',
      locked: false,
      memo: '',
      toppingData: topping,
      placementLevel: placementLevel,
    });
    setSections(list);
    message.success('独自情報を挿入しました');
  };

  const insertRaw = (secIndex, { html, text }, afterIndex) => {
    const list = Array.from(sections);
    const payload = {
      id: genId('sub'),
      title: text
        ? text.slice(0, 30) + (text.length > 30 ? '…' : '')
        : '直接配置',
      type: 'raw',
      locked: false,
      memo: '',
      raw: { html: html || '', text: text || '' },
    };
    if (typeof afterIndex === 'number') {
      list[secIndex].subsections.splice(afterIndex + 1, 0, payload);
    } else {
      list[secIndex].subsections.push(payload);
    }
    setSections(list);
    message.success('直接配置を挿入しました');
  };

  // 追加メニュー（中見出し/H4/独自情報）
  const [toppingConfig, setToppingConfig] = useState({
    mode: 'section_embed',
    title: '',
  });
  const AddMenu = ({ secIndex }) => (
    <Menu
      onClick={({ key }) => {
        if (key === 'h3') addSubToSection(secIndex, 'heading');
        else if (key === 'h4') addSubToSection(secIndex, 'h4');
        else if (key === 'topping') setAddModal({ open: true, secIndex });
      }}
      items={[
        { key: 'h3', label: '中見出し（H3）を追加' },
        { key: 'h4', label: '小見出し（H4）を追加' },
        { key: 'topping', label: '独自情報を追加' },
      ]}
    />
  );
  const AddMenuSub = ({ secIndex, subIndex }) => (
    <Menu
      onClick={({ key }) => {
        if (key === 'h3') addSubAfter(secIndex, subIndex, 'heading');
        else if (key === 'h4') addSubAfter(secIndex, subIndex, 'h4');
        else if (key === 'topping') {
          setAddModal({ open: true, secIndex });
          setAddModalAfterIndex(subIndex);
        }
      }}
      items={[
        { key: 'h3', label: '中見出し（H3）を追加' },
        { key: 'h4', label: '小見出し（H4）を追加' },
        { key: 'topping', label: '独自情報を追加' },
      ]}
    />
  );

  const computeScores = useMemo(() => {
    const relatedKeywords = ['方法', '効果', '費用', '事例', '比較', 'ツール'];
    const usedRel = sections.reduce(
      (acc, s) =>
        acc +
        relatedKeywords.filter(
          k =>
            s.title.includes(k) ||
            s.subsections.some(sub => sub.title.includes(k)),
        ).length,
      0,
    );
    const relScore = Math.min(
      Math.round((usedRel / relatedKeywords.length) * 100),
      100,
    );
    const lsi = ['戦略', 'KPI', 'ROI', '最適化'];
    const usedLsi = sections.reduce(
      (acc, s) =>
        acc +
        lsi.filter(k => s.title.includes(k) || s.supplement?.includes(k))
          .length,
      0,
    );
    const lsiScore = Math.min(Math.round((usedLsi / lsi.length) * 100), 100);
    const toppingBonus = sections.reduce(
      (acc, s) =>
        acc + s.subsections.filter(sub => sub.type === 'topping').length * 5,
      0,
    );
    const topicBase = Math.min(70, sections.length * 10);
    const topicScore = Math.min(topicBase + toppingBonus, 100);
    return { relScore, lsiScore, topicScore };
  }, [sections]);

  const openExpansionForSection = secIndex => {
    const sec = sections[secIndex];
    const cfg = (sec && sec.autoExpansion) || {};
    setExpansionConfig({
      count: cfg.count ?? 5,
      basis: cfg.basis || 'top',
      memo: cfg.memo || '',
      h2Title: sec?.title || '',
    });
    setExpansionModal({ open: true, secIndex });
  };

  /**
   * アクションの例が適用可能かどうかを判定
   * @param {Object} action - アクションオブジェクト
   * @param {string|null} headingId - 対象の見出しID（nullの場合は全体アクション）
   * @returns {boolean} 適用可能ならtrue
   */
  const canApplyExample = (action, headingId) => {
    if (!action.concrete_example) return false;
    if (!action.action_type) return false;

    // action_typeが見出し追加系のアクションのみ対応
    return ['add_h2', 'add_h3', 'add_h4'].includes(action.action_type);
  };

  /**
   * アクションの例を適用する
   * @param {Object} action - アクションオブジェクト
   * @param {string|null} headingId - 対象の見出しID（nullの場合は全体アクション）
   */
  const applyActionExample = async (action, headingId) => {
    if (!action.action_type || !action.new_heading_text) {
      message.warning('この例は自動適用できません。');
      return;
    }

    const newTitle = action.new_heading_text;
    const actionType = action.action_type;
    const insertionPoint = action.insertion_point;
    const targetHeadings = action.target_headings || [];

    // ターゲット見出しIDを取得（配列の最初の要素）
    const targetId = targetHeadings[0] || insertionPoint?.target_id;

    if (actionType === 'add_h2') {
      // H2を追加
      if (!targetId) {
        message.warning('対象の見出しが指定されていません。');
        return;
      }

      const targetSecIndex = sections.findIndex(s => s.id === targetId);
      if (targetSecIndex === -1) {
        message.warning('対象の見出しが見つかりません。');
        return;
      }

      const newSection = {
        id: genId('sec'),
        title: newTitle,
        locked: false,
        memo: '',
        subsections: [],
      };

      const newSections = [...sections];
      if (insertionPoint?.position === 'before') {
        newSections.splice(targetSecIndex, 0, newSection);
      } else {
        newSections.splice(targetSecIndex + 1, 0, newSection);
      }
      setSections(newSections);
      message.success(`H2「${newTitle}」を追加しました`);

      // 見出しを即座に保存（オートセーブを待たずに）
      try {
        await v2ArticlesApi.saveOutlineDraft(uuid, {
          title: titleH1,
          sections: newSections,
        });
      } catch (error) {
        console.error('[ActionPlanCheck] 例を適用 - 見出し保存失敗:', error);
      }

      // アクションプランのチェックをONにする
      if (action.id) {
        setActionPlanChecks({
          ...actionPlanChecks,
          [action.id]: true,
        });
        // バックグラウンドでDBに保存
        try {
          await v2ArticlesApi.updateActionPlanCheck(
            uuid,
            action.id,
            true,
          );
        } catch (error) {
          console.error('[ActionPlanCheck] 例を適用 - 保存失敗:', error);
        }
      }
      return;
    }

    if (actionType === 'add_h3' || actionType === 'add_h4') {
      // H3/H4を追加
      if (!targetId) {
        message.warning('対象の見出しが指定されていません。');
        return;
      }

      const targetSecIndex = sections.findIndex(s => s.id === targetId);
      if (targetSecIndex === -1) {
        message.warning('対象の見出しが見つかりません。');
        return;
      }

      const newSub = {
        id: genId('sub'),
        title: newTitle,
        type: actionType === 'add_h4' ? 'h4' : 'heading',
        locked: false,
        memo: '',
      };

      const newSections = [...sections];
      const targetSubs = newSections[targetSecIndex].subsections || [];

      if (insertionPoint?.position === 'before') {
        newSections[targetSecIndex].subsections = [newSub, ...targetSubs];
      } else if (insertionPoint?.position === 'last') {
        newSections[targetSecIndex].subsections = [...targetSubs, newSub];
      } else {
        newSections[targetSecIndex].subsections = [...targetSubs, newSub];
      }

      setSections(newSections);
      message.success(
        `${actionType === 'add_h4' ? 'H4' : 'H3'}「${newTitle}」を追加しました`,
      );

      // 見出しを即座に保存（オートセーブを待たずに）
      try {
        await v2ArticlesApi.saveOutlineDraft(uuid, {
          title: titleH1,
          sections: newSections,
        });
      } catch (error) {
        console.error('[ActionPlanCheck] 例を適用 - 見出し保存失敗:', error);
      }

      // アクションプランのチェックをONにする
      if (action.id) {
        setActionPlanChecks({
          ...actionPlanChecks,
          [action.id]: true,
        });
        // バックグラウンドでDBに保存
        try {
          await v2ArticlesApi.updateActionPlanCheck(
            uuid,
            action.id,
            true,
          );
        } catch (error) {
          console.error('[ActionPlanCheck] 例を適用 - 保存失敗:', error);
        }
      }
      return;
    }

    message.warning('この例は自動適用できません。');
  };

  const handleTempSave = useCallback(async () => {
    const payload = {
      title: titleH1,
      sections,
      memo: outlineMemo,
    };
    try {
      if (uuid) {
        await v2ArticlesApi.saveOutlineDraft(uuid, payload);
        message.success('一時保存しました');
      } else {
        // UUIDが無い場合はローカルにフォールバック
        localStorage.setItem(
          'step3_temp_sections',
          JSON.stringify({ ...payload, savedAt: new Date().toISOString() }),
        );
        message.success('一時保存しました（ローカル）');
      }
    } catch (e) {
      console.error('[Step3] Failed to save draft:', e);
      message.error(`一時保存に失敗しました: ${e.message || e}`);
    }
  }, [sections, titleH1, outlineMemo, uuid]);

  const rightReady = Array.isArray(fxTopics) && fxTopics.length > 0;

  // Tourステップ定義（micro/fullを切り替え）
  const tourSteps = React.useMemo(() => {
    const configKey = isTourMicro ? 'generation_step3_micro' : 'generation_step3';
    if (tourLoading || !TOUR_CONFIGS[configKey]) return [];

    const allSteps = TOUR_CONFIGS[configKey].steps.map(step => {
      // tourConfigsで定義されたtarget関数をそのまま使う
      return {
        ...step,
        // target関数が既に定義されている場合はそのまま、なければnull
        target: step.target || null,
      };
    });

    // 分析がない場合、スコアと分析関連のステップを除外
    if (!hasAnalysis) {
      return allSteps.filter(
        step =>
          step.target_key !== 'score_section' &&
          step.target_key !== 'analysis_modal',
      );
    }

    return allSteps;
  }, [tourLoading, hasAnalysis, tourOpen]);

  return (
    <div className="v2-form-narrow">
      {/* チュートリアルTour */}
      {!tourLoading && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          onClose={() => completeTour()}
          onFinish={() => completeTour()}
          onSkip={() => skipTour()}
          current={tourCurrentStep}
          onChange={current => {
            updateTourCurrentStep(current);
            // ステップ7（詳細な分析を確認）に到達したらモーダルを開く
            const step = tourSteps[current];
            if (step && step.target_key === 'analysis_modal' && hasAnalysis) {
              setAnalysisOpen(true);
            }
            // 他のステップではモーダルを閉じる
            if (step && step.target_key !== 'analysis_modal' && analysisOpen) {
              setAnalysisOpen(false);
            }
          }}
          tourTitle="STEP3: 構成編集"
        />
      )}

      {/* TIPS（STEP3用） */}
      <div style={{ marginBottom: 16 }}>
        <TipsRotator
          tips={generationStep3Tips}
          intervalMs={7000}
          fadeMs={300}
        />
      </div>

      {!isReadOnly && !localStorage.getItem('step3_edit_hint_dismissed') && (
        <Alert
          type="info"
          showIcon
          closable
          message="見出しをクリックして編集、左側のアイコンをドラッグで並び替え、右側の「…」アイコンから各種操作を行えます。"
          style={{ marginBottom: 12 }}
          onClose={() => localStorage.setItem('step3_edit_hint_dismissed', '1')}
        />
      )}

      <DragDropContext
        onDragEnd={
          scoreLoading || scoringStatus === 'processing' || isReadOnly
            ? () => {}
            : onDragEnd
        }
        onDragUpdate={
          scoreLoading || scoringStatus === 'processing' || isReadOnly
            ? () => {}
            : onDragUpdate
        }
        onDragStart={
          scoreLoading || scoringStatus === 'processing' || isReadOnly
            ? () => {}
            : onDragStart
        }
      >
        <Row gutter={16}>
          <Col xs={24} lg={14}>
            {/* スコアリング中のメッセージ */}
            {scoringStatus === 'processing' && (
              <div style={{ marginBottom: 12 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  ⚙️ スコアリング中です...
                </Typography.Text>
              </div>
            )}
            <div
              className="tour-action-bar"
              style={{
                marginBottom: 12,
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                opacity: scoreLoading ? 0.6 : 1,
                pointerEvents: scoreLoading ? 'none' : 'auto',
              }}
            >
              <Button
                className="tour-temp-save-button"
                icon={<SaveOutlined />}
                onClick={handleTempSave}
                disabled={scoreLoading || isReadOnly}
              >
                一時保存
              </Button>
              <div
                className="tour-edit-mode-toggle"
                style={{ marginLeft: 'auto' }}
              >
                <Radio.Group
                  value={editorMode}
                  onChange={e => {
                    const next = e.target.value;
                    setEditorMode(next);
                    if (next === 'editor') {
                      // パズル → テキスト
                      setEditorText(serializeSectionsToText());
                    } else {
                      // テキスト → パズル
                      const parsed = parseEditorTextToSections(
                        editorText,
                        sections,
                      ); // 既存データを渡す

                      // parsedは配列を直接返す（parsed.sectionsではない）
                      if (
                        parsed &&
                        Array.isArray(parsed) &&
                        parsed.length > 0
                      ) {
                        // parseEditorTextToSectionsが既に既存データをマージしているので、そのまま使用
                        setSections(parsed);

                        // 切り替え後に一時保存を実行
                        if (uuid) {
                          const payload = {
                            title: titleH1,
                            sections: parsed, // 既にマージ済みのデータを使用
                          };
                          v2ArticlesApi
                            .saveOutlineDraft(uuid, payload)
                            .catch(err => {
                              console.error(
                                '[Step3] モード切り替え後の一時保存失敗:',
                                err,
                              );
                            });
                        }
                      } else {
                        console.warn(
                          '[Step3] parsedが空または配列ではありません:',
                          parsed,
                        );
                      }
                    }
                  }}
                  optionType="button"
                  options={[
                    { label: 'パズル', value: 'puzzle' },
                    { label: 'テキスト', value: 'editor' },
                  ]}
                  style={{}}
                  size="small"
                  className="v2-mode-toggle"
                />
              </div>
            </div>
            {editorMode === 'editor' && (
              <Card size="small" style={{ marginBottom: 12 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Typography.Text type="secondary">
                    インデント位置によってH2/H3/H4を設定できます。(インデントはTabキーで追加／Shift
                    + Tabキーで削除)
                  </Typography.Text>
                  <Input.TextArea
                    ref={textAreaRef}
                    value={editorText}
                    onChange={e => setEditorText(e.target.value)}
                    onKeyDown={handleEditorKeyDown}
                    autoSize={{ minRows: 14 }}
                    disabled={scoreLoading || scoringStatus === 'processing'}
                    placeholder="- H1: ...\n- H2\n  - H3\n    - H4\n  - @top:xxx [INLINE] タイトル\n  - @raw タイトル"
                  />
                </Space>
              </Card>
            )}
            <Card
              className="tour-article-title"
              size="small"
              style={{
                marginBottom: 12,
                border: '1px solid #d9d9d9',
                display: editorMode === 'editor' ? 'none' : undefined,
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}
              >
                <Tag
                  color="red"
                  style={{
                    marginRight: 0,
                    borderRadius: 999,
                    flexShrink: 0,
                    marginTop: 4,
                  }}
                >
                  H1
                </Tag>
                <Input.TextArea
                  value={titleH1}
                  onChange={e => setTitleH1(e.target.value)}
                  placeholder="記事タイトル"
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  disabled={scoreLoading || scoringStatus === 'processing'}
                  style={{
                    border: 'none',
                    background:
                      scoringStatus === 'processing'
                        ? 'transparent'
                        : undefined,
                    fontWeight: 700,
                    resize: 'none',
                    flex: 1,
                  }}
                />
                {/* 全体に関わるアクション（"all"タイプ）のヒントアイコン */}
                {uncheckedActionsMap.all &&
                  uncheckedActionsMap.all.length > 0 && (
                    <Tooltip
                      title={
                        <div>
                          {uncheckedActionsMap.all.map((action, idx) => {
                            const priorityConfig = {
                              critical: { text: '最重要', color: '#cf1322' },
                              high: { text: '高', color: '#ff4d4f' },
                              medium: { text: '中', color: '#faad14' },
                              low: { text: '低', color: '#52c41a' },
                              minor: { text: '微調整', color: '#d9d9d9' },
                            };
                            const config =
                              priorityConfig[action.priority] ||
                              priorityConfig.medium;
                            return (
                              <div key={idx} style={{ marginBottom: 12 }}>
                                <div style={{ marginBottom: 4 }}>
                                  <Tag
                                    color={config.color}
                                    style={{ marginRight: 4 }}
                                  >
                                    {config.text}
                                  </Tag>
                                  <Tag color="blue" style={{ marginRight: 4 }}>
                                    {action.category}
                                  </Tag>
                                </div>
                                <div
                                  style={{ marginBottom: 2, fontWeight: 500 }}
                                >
                                  {action.improvement_direction ||
                                    action.action ||
                                    ''}
                                </div>
                                {(action.concrete_example || action.reason) && (
                                  <div style={{ fontSize: 12, color: '#999' }}>
                                    {action.concrete_example || action.reason}
                                  </div>
                                )}
                                {/* 例を適用するボタン（全体アクション） */}
                                {canApplyExample(action, null) && (
                                  <Button
                                    size="small"
                                    style={{
                                      marginTop: 8,
                                      padding: '2px 8px',
                                      height: 'auto',
                                      fontSize: 12,
                                      color: '#fff',
                                      backgroundColor: 'transparent',
                                      border:
                                        '1px solid rgba(255, 255, 255, 0.6)',
                                    }}
                                    onClick={() =>
                                      applyActionExample(action, null)
                                    }
                                  >
                                    例を適用する
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      }
                      placement="topLeft"
                      overlayStyle={{ maxWidth: 400 }}
                    >
                      <BulbOutlined
                        style={{
                          color: getPriorityColor(
                            getHighestPriority(
                              uncheckedActionsMap.all.map(a => a.priority),
                            ),
                          ),
                          fontSize: 16,
                          marginTop: 4,
                          cursor: 'help',
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  )}
              </div>
            </Card>
            <LoggedDroppable
              droppableId="sections"
              type="SECTION"
              isCombineEnabled
              direction="vertical"
            >
              {provided => (
                <div
                  className="tour-sections-list"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    display: editorMode === 'editor' ? 'none' : undefined,
                    opacity:
                      scoreLoading || scoringStatus === 'processing' ? 0.5 : 1,
                    pointerEvents:
                      scoreLoading || scoringStatus === 'processing'
                        ? 'none'
                        : 'auto',
                    transition: 'opacity 0.3s ease',
                  }}
                  key={`sections-${droppableVersion._sections || 0}`}
                >
                  {/* 先頭ドロップエリアは削除（要求により非表示） */}
                  {sections.map((sec, secIndex) => (
                    <Draggable
                      key={`${sec.id}`}
                      draggableId={`${sec.id}`}
                      index={secIndex}
                    >
                      {dragProvided => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={{
                            ...dragProvided.draggableProps.style,
                            marginBottom: 6,
                          }}
                        >
                          {/* H2間スロット（ドロップで新規H2/展開モード） */}
                          <LoggedDroppable
                            droppableId={`sec-slot-${secIndex}`}
                            type="SUBSECTION"
                          >
                            {(slotProvided, slotSnapshot) => (
                              <div
                                ref={slotProvided.innerRef}
                                {...slotProvided.droppableProps}
                                style={{
                                  background: slotSnapshot.isDraggingOver
                                    ? '#fafafa'
                                    : 'transparent',
                                  borderRadius: 6,
                                  padding: slotSnapshot.isDraggingOver
                                    ? '12px 0'
                                    : 0,
                                  minHeight: slotSnapshot.isDraggingOver
                                    ? 40
                                    : 4,
                                  marginTop: 0,
                                  marginBottom: 6,
                                  transition: 'all 120ms ease-in-out',
                                }}
                              >
                                {slotProvided.placeholder}
                              </div>
                            )}
                          </LoggedDroppable>
                          {/* 余分なオーバーレイは削除。カード内のオーバーレイのみ使用 */}
                          <div
                            style={{ position: 'relative' }}
                            data-sec-card-index={secIndex}
                          ></div>
                          <Card
                            size="small"
                            style={{
                              border:
                                '1px solid ' +
                                (sec.locked ? '#91d5ff' : '#f0f0f0'),
                              background: (() => {
                                const isPreview =
                                  h3SlotHoverSecIndex === secIndex;
                                return isPreview
                                  ? 'rgba(0, 0, 0, 0.04)'
                                  : sec.locked
                                    ? '#f0f9ff'
                                    : hoverMode === 'section' &&
                                        hoveredSectionId === sec.id
                                      ? '#f5f5f5'
                                      : '#fff';
                              })(),
                              transition: 'all 120ms ease-in-out',
                              position: 'relative',
                            }}
                          >
                            {/* H2→H3降格の受け側ハイライト（Card全体覆う） */}
                            <LoggedDroppable
                              droppableId={`h3-slot-${secIndex}`}
                              type="SECTION"
                            >
                              {(h3Provided, h3Snapshot) => {
                                const shouldShowPreview =
                                  h3Snapshot.isDraggingOver ||
                                  h3SlotHoverSecIndex === secIndex;
                                return (
                                  <div
                                    ref={h3Provided.innerRef}
                                    {...h3Provided.droppableProps}
                                    style={{
                                      position: 'absolute',
                                      inset: 0,
                                      pointerEvents: 'none',
                                      zIndex: 100,
                                    }}
                                  >
                                    {/* H2→H3ドロップ時のプレビュー - Card自体の背景で表示するため不要 */}
                                  </div>
                                );
                              }}
                            </LoggedDroppable>
                            {sec.isExpansionSection ? (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                              >
                                <div
                                  {...dragProvided.dragHandleProps}
                                  style={{ cursor: 'grab', color: '#999' }}
                                >
                                  ≡
                                </div>
                                <div
                                  style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    background:
                                      'linear-gradient(135deg, rgba(158,130,216,0.06) 0%, rgba(217,70,239,0.06) 100%)',
                                    borderRadius: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                    }}
                                  >
                                    <Tag color="purple">展開モード</Tag>
                                    <Input
                                      value={sec.title}
                                      onChange={e => {
                                        const list = Array.from(sections);
                                        list[secIndex] = {
                                          ...list[secIndex],
                                          title: e.target.value,
                                        };
                                        setSections(list);
                                      }}
                                      style={{
                                        flex: 1,
                                        fontWeight: 600,
                                      }}
                                      placeholder="見出しを入力"
                                      bordered={false}
                                    />
                                    <Space style={{ marginLeft: 'auto' }}>
                                      <Button
                                        size="small"
                                        icon={<SettingOutlined />}
                                        onClick={() =>
                                          openExpansionForSection(secIndex)
                                        }
                                      ></Button>
                                      <Button
                                        size="small"
                                        danger
                                        icon={
                                          pendingDelSec === sec.id ? (
                                            <ExclamationCircleOutlined />
                                          ) : (
                                            <DeleteOutlined />
                                          )
                                        }
                                        onClick={() => {
                                          if (pendingDelSec === sec.id) {
                                            deleteSection(secIndex);
                                            setPendingDelSec(null);
                                          } else {
                                            setPendingDelSec(sec.id);
                                            setTimeout(
                                              () => setPendingDelSec(null),
                                              2000,
                                            );
                                          }
                                        }}
                                      ></Button>
                                    </Space>
                                  </div>
                                  <div
                                    style={{
                                      marginTop: 6,
                                      display: 'grid',
                                      gridTemplateColumns: '1fr',
                                      rowGap: 4,
                                    }}
                                  >
                                    <Typography.Text
                                      type="secondary"
                                      style={{ fontSize: 12 }}
                                    >
                                      情報源:{' '}
                                      {sec.expansionTopping?.title || '未指定'}
                                    </Typography.Text>
                                    <Typography.Text
                                      type="secondary"
                                      style={{ fontSize: 12 }}
                                    >
                                      {`展開数 ${sec.autoExpansion?.count ?? 5}／基準 ${sec.autoExpansion?.basis || 'top'}`}
                                    </Typography.Text>
                                    {sec.autoExpansion?.memo ? (
                                      <Typography.Text
                                        type="secondary"
                                        style={{ fontSize: 12 }}
                                      >
                                        {sec.autoExpansion?.memo}
                                      </Typography.Text>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                              >
                                <div
                                  {...dragProvided.dragHandleProps}
                                  style={{ cursor: 'grab', color: '#999' }}
                                >
                                  ≡
                                </div>
                                <Tag
                                  color="orange"
                                  style={{ marginRight: 0, borderRadius: 999 }}
                                >
                                  H2
                                </Tag>
                                <Input
                                  value={sec.title}
                                  onChange={e =>
                                    updateSectionTitle(secIndex, e.target.value)
                                  }
                                  placeholder="セクションタイトル"
                                  disabled={sec.locked}
                                  style={{
                                    border: 'none',
                                    fontWeight: 600,
                                    background: sec.locked
                                      ? 'transparent'
                                      : undefined,
                                    cursor: sec.locked
                                      ? 'not-allowed'
                                      : undefined,
                                  }}
                                />
                                {/* ヒントアイコン: 未チェックアクションがある場合のみ表示 */}
                                {uncheckedActionsMap.byHeading[sec.id] &&
                                  uncheckedActionsMap.byHeading[sec.id].length >
                                    0 && (
                                    <Tooltip
                                      title={
                                        <div>
                                          {uncheckedActionsMap.byHeading[
                                            sec.id
                                          ].map((action, idx) => {
                                            const priorityConfig = {
                                              critical: '最重要',
                                              high: '高',
                                              medium: '中',
                                              low: '低',
                                              minor: '微調整',
                                            };
                                            return (
                                              <div
                                                key={idx}
                                                style={{ marginBottom: 12 }}
                                              >
                                                <div
                                                  style={{ marginBottom: 4 }}
                                                >
                                                  <Tag
                                                    color={
                                                      action.priority ===
                                                      'critical'
                                                        ? '#cf1322'
                                                        : action.priority ===
                                                            'high'
                                                          ? '#ff4d4f'
                                                          : action.priority ===
                                                              'medium'
                                                            ? '#faad14'
                                                            : action.priority ===
                                                                'low'
                                                              ? '#52c41a'
                                                              : '#d9d9d9'
                                                    }
                                                    style={{ marginRight: 4 }}
                                                  >
                                                    {priorityConfig[
                                                      action.priority
                                                    ] || '中'}
                                                  </Tag>
                                                  <Tag
                                                    color="blue"
                                                    style={{ marginRight: 4 }}
                                                  >
                                                    {action.category}
                                                  </Tag>
                                                </div>
                                                <div
                                                  style={{
                                                    marginBottom: 2,
                                                    fontWeight: 500,
                                                  }}
                                                >
                                                  {action.improvement_direction ||
                                                    action.action ||
                                                    ''}
                                                </div>
                                                {(action.concrete_example ||
                                                  action.reason) && (
                                                  <div
                                                    style={{
                                                      fontSize: 12,
                                                      color: '#999',
                                                    }}
                                                  >
                                                    {action.concrete_example ||
                                                      action.reason}
                                                  </div>
                                                )}
                                                {/* 例を適用するボタン */}
                                                {canApplyExample(
                                                  action,
                                                  sec.id,
                                                ) && (
                                                  <Button
                                                    size="small"
                                                    style={{
                                                      marginTop: 8,
                                                      padding: '2px 8px',
                                                      height: 'auto',
                                                      fontSize: 12,
                                                      color: '#fff',
                                                      backgroundColor:
                                                        'transparent',
                                                      border:
                                                        '1px solid rgba(255, 255, 255, 0.6)',
                                                    }}
                                                    onClick={() =>
                                                      applyActionExample(
                                                        action,
                                                        sec.id,
                                                      )
                                                    }
                                                  >
                                                    例を適用する
                                                  </Button>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      }
                                      placement="topLeft"
                                      overlayStyle={{ maxWidth: 400 }}
                                    >
                                      <BulbOutlined
                                        style={{
                                          color: getPriorityColor(
                                            getHighestPriority(
                                              uncheckedActionsMap.byHeading[
                                                sec.id
                                              ].map(a => a.priority),
                                            ),
                                          ),
                                          fontSize: 16,
                                          marginLeft: 8,
                                          cursor: 'help',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                <div
                                  className="action-toolbar"
                                  style={isReadOnly ? { display: 'none' } : {}}
                                >
                                  <Button
                                    size="small"
                                    className="action-toolbar__more"
                                    onClick={e => {
                                      try {
                                        if (
                                          window.matchMedia &&
                                          window.matchMedia('(hover: none)')
                                            .matches
                                        ) {
                                          const el =
                                            e.currentTarget.closest(
                                              '.action-toolbar',
                                            );
                                          if (el)
                                            el.classList.toggle('is-open');
                                        }
                                      } catch (err) {
                                        // no-op
                                      }
                                    }}
                                  >
                                    ⋯
                                  </Button>
                                  <div className="action-toolbar__icons">
                                    <Space style={{ marginLeft: 'auto' }}>
                                      <Dropdown
                                        overlay={
                                          <AddMenu secIndex={secIndex} />
                                        }
                                        trigger={['click']}
                                      >
                                        <Button
                                          size="small"
                                          icon={<PlusCircleOutlined />}
                                        />
                                      </Dropdown>
                                      <Button
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() =>
                                          setMemoModal({
                                            open: true,
                                            type: 'section',
                                            secIndex,
                                            subIndex: null,
                                            text: sections[secIndex].memo || '',
                                          })
                                        }
                                      />
                                      <Button
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={() =>
                                          duplicateSection(secIndex)
                                        }
                                      />
                                      {/* TODO: ロック機能は後々実装 */}
                                      {/* 
                                      <Button
                                        size="small"
                                        icon={
                                          sec.locked ? (
                                            <UnlockOutlined
                                              style={{ color: '#1890ff' }}
                                            />
                                          ) : (
                                            <LockOutlined />
                                          )
                                        }
                                        onClick={() =>
                                          toggleLockSection(secIndex)
                                        }
                                      />
                                      */}
                                      <Button
                                        size="small"
                                        icon={
                                          pendingDelSec === sec.id ? (
                                            <ExclamationCircleOutlined />
                                          ) : (
                                            <DeleteOutlined />
                                          )
                                        }
                                        danger={pendingDelSec === sec.id}
                                        onClick={() => {
                                          if (pendingDelSec === sec.id) {
                                            deleteSection(secIndex);
                                            setPendingDelSec(null);
                                          } else {
                                            setPendingDelSec(sec.id);
                                            clearConfirmLater();
                                          }
                                        }}
                                      />
                                    </Space>
                                  </div>
                                </div>
                              </div>
                            )}
                            {sec.memo && (
                              <div style={{ marginTop: 6, paddingLeft: 28 }}>
                                <Typography.Text
                                  type="secondary"
                                  style={{ fontSize: 12 }}
                                >
                                  {sec.memo}
                                </Typography.Text>
                              </div>
                            )}
                            <LoggedDroppable
                              droppableId={`section-${secIndex}`}
                              type="SUBSECTION"
                              renderClone={(
                                cloneProvided,
                                cloneSnapshot,
                                rubric,
                              ) => {
                                const start = rubric.source.index;
                                const subs =
                                  sections[secIndex]?.subsections || [];
                                const head = subs[start];
                                const group = [];
                                if (head) {
                                  group.push(head);
                                  if (
                                    head.type === 'heading' ||
                                    head.type === 'h4'
                                  ) {
                                    for (
                                      let i = start + 1;
                                      i < subs.length;
                                      i += 1
                                    ) {
                                      const nx = subs[i];
                                      if (nx.type === 'heading') break; // H3が来たら終了
                                      if (
                                        head.type === 'h4' &&
                                        nx.type === 'h4'
                                      )
                                        break; // 次のH4が来たら終了
                                      group.push(nx);
                                    }
                                  }
                                }
                                // クローン幅をセクションのサブDroppable幅に合わせる
                                let cloneWidth;
                                try {
                                  const container = document.querySelector(
                                    `[data-rbd-droppable-id="section-${secIndex}"]`,
                                  );
                                  if (container)
                                    cloneWidth =
                                      container.getBoundingClientRect().width;
                                } catch (e) {
                                  cloneWidth = undefined;
                                }
                                // クローンのインデント（先頭要素に合わせる）
                                const headIndent =
                                  head?.type === 'h4' ? 36 : 24;
                                if (!cloneWidth || Number.isNaN(cloneWidth))
                                  cloneWidth = 560;
                                return (
                                  <div
                                    ref={cloneProvided.innerRef}
                                    {...cloneProvided.draggableProps}
                                    {...cloneProvided.dragHandleProps}
                                    style={{
                                      ...cloneProvided.draggableProps.style,
                                      background: '#fff',
                                      border: '1px solid #f0f0f0',
                                      borderRadius: 8,
                                      boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                                      padding: '8px 8px 0 8px',
                                      marginLeft: headIndent,
                                      width: `${Math.max(240, cloneWidth - headIndent)}px`,
                                      maxWidth: `${Math.max(240, cloneWidth - headIndent)}px`,
                                      boxSizing: 'border-box',
                                      height: 'auto',
                                      // 行の高さ36px + 行間gap 8px * (n-1) + 上パディング8px
                                      minHeight: `${Math.max(36, group.length * 36 + Math.max(0, group.length - 1) * 8 + 8)}px`,
                                    }}
                                  >
                                    {group.map((g, i) => {
                                      if (g.type === 'topping') {
                                        return (
                                          <div
                                            key={i}
                                            style={{
                                              background:
                                                'linear-gradient(135deg, rgba(158,130,216,0.06) 0%, rgba(217,70,239,0.06) 100%)',
                                              borderRadius: 6,
                                              padding: '8px 12px',
                                              marginLeft: 24,
                                              marginBottom: 6,
                                              overflow: 'hidden',
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                              }}
                                            >
                                              <Typography.Text
                                                style={{
                                                  marginRight: 'auto',
                                                  fontWeight: 400,
                                                }}
                                              >
                                                <Typography.Text
                                                  style={{
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    fontWeight: 400,
                                                  }}
                                                >
                                                  {g.title}
                                                </Typography.Text>
                                              </Typography.Text>
                                              <Tag
                                                color="magenta"
                                                style={{
                                                  marginRight: 0,
                                                  borderRadius: 999,
                                                }}
                                              >
                                                ラベル
                                              </Tag>
                                            </div>
                                          </div>
                                        );
                                      }
                                      const isH4 = g.type === 'h4';
                                      return (
                                        <div
                                          key={i}
                                          style={{
                                            marginLeft: isH4 ? 36 : 24,
                                            marginBottom: 8,
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 8,
                                              lineHeight: 1.6,
                                              height: 36,
                                            }}
                                          >
                                            <Tag
                                              color={isH4 ? 'green' : 'yellow'}
                                              style={{
                                                marginRight: 4,
                                                borderRadius: 999,
                                              }}
                                            >
                                              {isH4 ? 'H4' : 'H3'}
                                            </Tag>
                                            <Typography.Text
                                              style={{
                                                marginRight: 'auto',
                                                fontWeight: 400,
                                              }}
                                            >
                                              <Typography.Text
                                                style={{
                                                  whiteSpace: 'normal',
                                                  wordBreak: 'break-word',
                                                  fontWeight: 400,
                                                }}
                                              >
                                                {g.title}
                                              </Typography.Text>
                                            </Typography.Text>
                                          </div>
                                          {g.memo ? (
                                            <div
                                              style={{
                                                marginTop: 0,
                                                background: '#fafafa',
                                                paddingLeft: '1em',
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              <Typography.Text
                                                type="secondary"
                                                style={{ fontSize: 12 }}
                                              >
                                                {g.memo}
                                              </Typography.Text>
                                            </div>
                                          ) : null}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              }}
                            >
                              {(subProvided, subSnapshot) => (
                                <div
                                  ref={subProvided.innerRef}
                                  {...subProvided.droppableProps}
                                  style={{
                                    marginTop: 8,
                                    borderRadius: 6,
                                    // サブ領域のハイライトは無し
                                  }}
                                >
                                  {sec.subsections.map((sub, subIndex) => (
                                    <Draggable
                                      key={`${sub.id}`}
                                      draggableId={`${sub.id}`}
                                      index={subIndex}
                                    >
                                      {(subDrag, subSnapshot) => {
                                        const prevForBelonging = sections[
                                          secIndex
                                        ].subsections
                                          .slice(0, subIndex)
                                          .reverse()
                                          .find(
                                            s =>
                                              s.type === 'heading' ||
                                              s.type === 'h4',
                                          );
                                        const lastHeading = sections[
                                          secIndex
                                        ].subsections
                                          .slice(0, subIndex)
                                          .reverse()
                                          .find(s => s.type === 'heading');
                                        const lastH4 = sections[
                                          secIndex
                                        ].subsections
                                          .slice(0, subIndex)
                                          .reverse()
                                          .find(s => s.type === 'h4');
                                        const parentHeadingLocked = Boolean(
                                          lastHeading && lastHeading.locked,
                                        );
                                        const parentForToppingLocked = Boolean(
                                          (lastH4 && lastH4.locked) ||
                                            parentHeadingLocked,
                                        );
                                        const lockedByParent =
                                          sec.locked ||
                                          (sub.type === 'h4'
                                            ? parentHeadingLocked
                                            : sub.type === 'topping'
                                              ? parentForToppingLocked
                                              : false);
                                        const effectiveLocked =
                                          sub.lockOverride === 'unlock'
                                            ? false
                                            : sub.lockOverride === 'lock'
                                              ? true
                                              : lockedByParent || !!sub.locked;
                                        const parentBaseIndent =
                                          prevForBelonging &&
                                          prevForBelonging.type === 'h4'
                                            ? 36
                                            : 24;
                                        const belongingIndent =
                                          parentBaseIndent + 12;
                                        return (
                                          <div
                                            ref={subDrag.innerRef}
                                            {...subDrag.draggableProps}
                                            style={{
                                              ...subDrag.draggableProps.style,
                                              transition:
                                                subSnapshot.isDragging ||
                                                subTransMode === 'suppress'
                                                  ? 'none'
                                                  : subTransMode === 'smooth'
                                                    ? 'transform 180ms ease, background-color 120ms ease'
                                                    : 'transform 60ms linear, background-color 60ms linear',
                                              marginBottom: 8,
                                              marginLeft:
                                                sub.type === 'h4'
                                                  ? 36
                                                  : sub.type === 'topping'
                                                    ? sub.placementLevel ===
                                                      'h4'
                                                      ? 36
                                                      : 24
                                                    : 24,
                                              background:
                                                sub.type !== 'topping' &&
                                                (hoverMode === 'sub' &&
                                                hoveredSubId === sub.id
                                                  ? '#fafafa'
                                                  : effectiveLocked
                                                    ? '#f0f9ff'
                                                    : undefined),
                                              border:
                                                sub.type !== 'topping' &&
                                                (effectiveLocked
                                                  ? '1px solid #91d5ff'
                                                  : undefined),
                                              opacity:
                                                dragGroup.secIndex ===
                                                  secIndex &&
                                                dragGroup.start != null &&
                                                dragGroup.end != null &&
                                                subIndex >= dragGroup.start &&
                                                subIndex <= dragGroup.end &&
                                                subIndex !== dragGroup.start
                                                  ? 0.05
                                                  : 1,
                                              visibility:
                                                dragGroup.secIndex ===
                                                  secIndex &&
                                                dragGroup.start != null &&
                                                dragGroup.end != null &&
                                                subIndex >= dragGroup.start &&
                                                subIndex <= dragGroup.end &&
                                                subIndex !== dragGroup.start
                                                  ? 'hidden'
                                                  : 'visible',
                                              pointerEvents:
                                                dragGroup.secIndex ===
                                                  secIndex &&
                                                dragGroup.start != null &&
                                                dragGroup.end != null &&
                                                subIndex >= dragGroup.start &&
                                                subIndex <= dragGroup.end &&
                                                subIndex !== dragGroup.start
                                                  ? 'none'
                                                  : undefined,
                                              willChange: subSnapshot.isDragging
                                                ? 'transform'
                                                : undefined,
                                              // 背景のtransitionは上のtransitionで包括
                                            }}
                                          >
                                            {/* H3/H4のドロップ位置ハイライトは無し */}
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                flexWrap: 'wrap',
                                              }}
                                            >
                                              {/* 見出し行（topping以外） */}
                                              {sub.type !== 'topping' ? (
                                                <>
                                                  <div
                                                    className="h3h4-inline"
                                                    {...subDrag.dragHandleProps}
                                                    style={{
                                                      cursor: 'grab',
                                                      color: '#bbb',
                                                      width: 24,
                                                      height: 24,
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      borderRadius: 4,
                                                      userSelect: 'none',
                                                    }}
                                                    title="ドラッグで移動"
                                                  >
                                                    ⋮
                                                  </div>
                                                  <Tag
                                                    color={
                                                      sub.type === 'h4'
                                                        ? 'green'
                                                        : 'yellow'
                                                    }
                                                    style={{
                                                      marginRight: 4,
                                                      borderRadius: 999,
                                                    }}
                                                    onClick={e => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      const nextType =
                                                        sub.type === 'h4'
                                                          ? 'heading'
                                                          : 'h4';
                                                      const list =
                                                        Array.from(sections);
                                                      list[
                                                        secIndex
                                                      ].subsections[subIndex] =
                                                        {
                                                          ...list[secIndex]
                                                            .subsections[
                                                            subIndex
                                                          ],
                                                          type: nextType,
                                                        };
                                                      setSections(list);
                                                    }}
                                                  >
                                                    {sub.type === 'h4'
                                                      ? 'H4'
                                                      : 'H3'}
                                                  </Tag>
                                                  <Input
                                                    value={sub.title}
                                                    onChange={e => {
                                                      const list =
                                                        Array.from(sections);
                                                      list[
                                                        secIndex
                                                      ].subsections[subIndex] =
                                                        {
                                                          ...list[secIndex]
                                                            .subsections[
                                                            subIndex
                                                          ],
                                                          title: e.target.value,
                                                        };
                                                      setSections(list);
                                                    }}
                                                    disabled={effectiveLocked}
                                                    style={{
                                                      border: 'none',
                                                      background:
                                                        effectiveLocked
                                                          ? 'transparent'
                                                          : hoverMode ===
                                                                'sub' &&
                                                              hoveredSubId ===
                                                                sub.id
                                                            ? 'transparent'
                                                            : undefined,
                                                      marginLeft: 0,
                                                      flex: 1,
                                                      minWidth: 0,
                                                      width: 'auto',
                                                    }}
                                                  />
                                                  {/* ヒントアイコン: H3/H4の未チェックアクション */}
                                                  {uncheckedActionsMap
                                                    .byHeading[sub.id] &&
                                                    uncheckedActionsMap
                                                      .byHeading[sub.id]
                                                      .length > 0 && (
                                                      <Tooltip
                                                        title={
                                                          <div>
                                                            {uncheckedActionsMap.byHeading[
                                                              sub.id
                                                            ].map(
                                                              (action, idx) => {
                                                                const priorityConfig =
                                                                  {
                                                                    critical:
                                                                      '最重要',
                                                                    high: '高',
                                                                    medium:
                                                                      '中',
                                                                    low: '低',
                                                                    minor:
                                                                      '微調整',
                                                                  };
                                                                return (
                                                                  <div
                                                                    key={idx}
                                                                    style={{
                                                                      marginBottom: 8,
                                                                    }}
                                                                  >
                                                                    <Tag
                                                                      color={
                                                                        action.priority ===
                                                                        'critical'
                                                                          ? '#cf1322'
                                                                          : action.priority ===
                                                                              'high'
                                                                            ? '#ff4d4f'
                                                                            : action.priority ===
                                                                                'medium'
                                                                              ? '#faad14'
                                                                              : action.priority ===
                                                                                  'low'
                                                                                ? '#52c41a'
                                                                                : '#d9d9d9'
                                                                      }
                                                                      style={{
                                                                        marginRight: 4,
                                                                      }}
                                                                    >
                                                                      {priorityConfig[
                                                                        action
                                                                          .priority
                                                                      ] || '中'}
                                                                    </Tag>
                                                                    <div
                                                                      style={{
                                                                        marginBottom: 2,
                                                                        fontWeight: 500,
                                                                      }}
                                                                    >
                                                                      {action.improvement_direction ||
                                                                        action.action ||
                                                                        ''}
                                                                    </div>
                                                                    {(action.concrete_example ||
                                                                      action.reason) && (
                                                                      <div
                                                                        style={{
                                                                          fontSize: 12,
                                                                          color:
                                                                            '#999',
                                                                        }}
                                                                      >
                                                                        {action.concrete_example ||
                                                                          action.reason}
                                                                      </div>
                                                                    )}
                                                                    {/* 例を適用するボタン（H3/H4） */}
                                                                    {canApplyExample(
                                                                      action,
                                                                      sub.id,
                                                                    ) && (
                                                                      <Button
                                                                        size="small"
                                                                        style={{
                                                                          marginTop: 8,
                                                                          padding:
                                                                            '2px 8px',
                                                                          height:
                                                                            'auto',
                                                                          fontSize: 12,
                                                                          color:
                                                                            '#fff',
                                                                          backgroundColor:
                                                                            'transparent',
                                                                          border:
                                                                            '1px solid rgba(255, 255, 255, 0.6)',
                                                                        }}
                                                                        onClick={() =>
                                                                          applyActionExample(
                                                                            action,
                                                                            sub.id,
                                                                          )
                                                                        }
                                                                      >
                                                                        例を適用する
                                                                      </Button>
                                                                    )}
                                                                  </div>
                                                                );
                                                              },
                                                            )}
                                                          </div>
                                                        }
                                                        placement="topLeft"
                                                        overlayStyle={{
                                                          maxWidth: 400,
                                                        }}
                                                      >
                                                        <BulbOutlined
                                                          style={{
                                                            color:
                                                              getPriorityColor(
                                                                getHighestPriority(
                                                                  uncheckedActionsMap.byHeading[
                                                                    sub.id
                                                                  ].map(
                                                                    a =>
                                                                      a.priority,
                                                                  ),
                                                                ),
                                                              ),
                                                            fontSize: 14,
                                                            marginLeft: 4,
                                                            cursor: 'help',
                                                          }}
                                                        />
                                                      </Tooltip>
                                                    )}
                                                  <div
                                                    className="action-toolbar"
                                                    style={
                                                      isReadOnly
                                                        ? { display: 'none' }
                                                        : {}
                                                    }
                                                  >
                                                    <Button
                                                      size="small"
                                                      className="action-toolbar__more"
                                                      onClick={e => {
                                                        try {
                                                          if (
                                                            window.matchMedia &&
                                                            window.matchMedia(
                                                              '(hover: none)',
                                                            ).matches
                                                          ) {
                                                            const el =
                                                              e.currentTarget.closest(
                                                                '.action-toolbar',
                                                              );
                                                            if (el)
                                                              el.classList.toggle(
                                                                'is-open',
                                                              );
                                                          }
                                                        } catch (err) {
                                                          // no-op
                                                        }
                                                      }}
                                                    >
                                                      ⋯
                                                    </Button>
                                                    <div className="action-toolbar__icons">
                                                      <Space
                                                        style={{
                                                          marginLeft: 'auto',
                                                          flexShrink: 0,
                                                        }}
                                                        onMouseEnter={() => {
                                                          if (isDraggingSub)
                                                            return;
                                                          setHoverMode('sub');
                                                          setHoveredSubId(
                                                            sub.id,
                                                          );
                                                        }}
                                                        onMouseLeave={() => {
                                                          if (isDraggingSub)
                                                            return;
                                                          setHoverMode(null);
                                                          setHoveredSubId(null);
                                                        }}
                                                      >
                                                        <Dropdown
                                                          overlay={
                                                            <AddMenuSub
                                                              secIndex={
                                                                secIndex
                                                              }
                                                              subIndex={
                                                                subIndex
                                                              }
                                                            />
                                                          }
                                                          trigger={['click']}
                                                        >
                                                          <Button
                                                            size="small"
                                                            icon={
                                                              <PlusCircleOutlined />
                                                            }
                                                            disabled={
                                                              effectiveLocked
                                                            }
                                                          />
                                                        </Dropdown>
                                                        <Button
                                                          size="small"
                                                          icon={
                                                            <EditOutlined />
                                                          }
                                                          onClick={() =>
                                                            setMemoModal({
                                                              open: true,
                                                              type: 'sub',
                                                              secIndex,
                                                              subIndex,
                                                              text:
                                                                sections[
                                                                  secIndex
                                                                ].subsections[
                                                                  subIndex
                                                                ].memo || '',
                                                            })
                                                          }
                                                          disabled={
                                                            effectiveLocked
                                                          }
                                                        />
                                                        <Button
                                                          size="small"
                                                          icon={
                                                            <CopyOutlined />
                                                          }
                                                          onClick={() =>
                                                            duplicateSubsection(
                                                              secIndex,
                                                              subIndex,
                                                            )
                                                          }
                                                          disabled={
                                                            effectiveLocked
                                                          }
                                                        />
                                                        {/* TODO: ロック機能は後々実装 */}
                                                        {/*
                                                        <Button
                                                          size="small"
                                                          icon={
                                                            effectiveLocked ||
                                                            sub.lockOverride ===
                                                              'lock' ? (
                                                              <UnlockOutlined
                                                                style={{
                                                                  color:
                                                                    '#1890ff',
                                                                }}
                                                              />
                                                            ) : (
                                                              <LockOutlined />
                                                            )
                                                          }
                                                          onClick={() => {
                                                            const list =
                                                              Array.from(
                                                                sections,
                                                              );
                                                            const current =
                                                              list[secIndex]
                                                                .subsections[
                                                                subIndex
                                                              ];
                                                            // 現在の有効ロック状態を再計算
                                                            const currentLockedByParent =
                                                              list[secIndex]
                                                                .locked ||
                                                              (current.type ===
                                                              'h4'
                                                                ? parentHeadingLocked
                                                                : current.type ===
                                                                    'topping'
                                                                  ? parentForToppingLocked
                                                                  : false);
                                                            const currentEffective =
                                                              current.lockOverride ===
                                                              'unlock'
                                                                ? false
                                                                : current.lockOverride ===
                                                                    'lock'
                                                                  ? true
                                                                  : currentLockedByParent ||
                                                                    !!current.locked;
                                                            // トグル: 現在有効なら『個別解除』、無効なら『個別ロック』
                                                            const nextOverride =
                                                              currentEffective
                                                                ? 'unlock'
                                                                : 'lock';
                                                            list[
                                                              secIndex
                                                            ].subsections[
                                                              subIndex
                                                            ] = {
                                                              ...current,
                                                              lockOverride:
                                                                nextOverride,
                                                            };
                                                            setSections(list);
                                                          }}
                                                        />
                                                        */}
                                                        <Button
                                                          size="small"
                                                          icon={
                                                            pendingDelSub.sec ===
                                                              sec.id &&
                                                            pendingDelSub.sub ===
                                                              sub.id ? (
                                                              <ExclamationCircleOutlined />
                                                            ) : (
                                                              <DeleteOutlined />
                                                            )
                                                          }
                                                          danger={
                                                            pendingDelSub.sec ===
                                                              sec.id &&
                                                            pendingDelSub.sub ===
                                                              sub.id
                                                          }
                                                          disabled={
                                                            effectiveLocked
                                                          }
                                                          onClick={() => {
                                                            if (
                                                              pendingDelSub.sec ===
                                                                sec.id &&
                                                              pendingDelSub.sub ===
                                                                sub.id
                                                            ) {
                                                              const list =
                                                                Array.from(
                                                                  sections,
                                                                );
                                                              list[
                                                                secIndex
                                                              ].subsections.splice(
                                                                subIndex,
                                                                1,
                                                              );
                                                              setSections(list);
                                                              setPendingDelSub({
                                                                sec: null,
                                                                sub: null,
                                                              });
                                                            } else {
                                                              setPendingDelSub({
                                                                sec: sec.id,
                                                                sub: sub.id,
                                                              });
                                                              clearConfirmLater();
                                                            }
                                                          }}
                                                        />
                                                      </Space>
                                                    </div>
                                                  </div>
                                                  {sub.memo ? (
                                                    <div
                                                      style={{
                                                        marginTop: 0,
                                                        marginLeft:
                                                          sub.type === 'h4'
                                                            ? 36
                                                            : 24,
                                                        background: '#fafafa',
                                                        paddingLeft: '1em',
                                                        flexBasis: '100%',
                                                        width: '100%',
                                                      }}
                                                    >
                                                      <Typography.Text
                                                        type="secondary"
                                                        style={{ fontSize: 12 }}
                                                      >
                                                        {sub.memo}
                                                      </Typography.Text>
                                                    </div>
                                                  ) : null}
                                                </>
                                              ) : (
                                                /* 独自情報（topping）行 */
                                                <div
                                                  style={{
                                                    background:
                                                      'linear-gradient(135deg, rgba(158,130,216,0.06) 0%, rgba(217,70,239,0.06) 100%)',
                                                    border: 'none',
                                                    borderRadius: 4,
                                                    width: '100%',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                  }}
                                                  onClick={() => {}}
                                                >
                                                  <div
                                                    style={{
                                                      padding:
                                                        !sub.placementLevel ||
                                                        sub.placementLevel ===
                                                          'h3'
                                                          ? '8px 12px 8px 0'
                                                          : '8px 12px',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent:
                                                        'space-between',
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        flex: 1,
                                                        gap: 8,
                                                      }}
                                                    >
                                                      <div
                                                        {...subDrag.dragHandleProps}
                                                        style={{}}
                                                        title="ドラッグで移動"
                                                      >
                                                        <span
                                                          style={{
                                                            cursor: 'grab',
                                                            color: '#999',
                                                            width: 24,
                                                            height: 24,
                                                            display:
                                                              'inline-flex',
                                                            alignItems:
                                                              'center',
                                                            justifyContent:
                                                              'center',
                                                            userSelect: 'none',
                                                          }}
                                                        >
                                                          ⋮
                                                        </span>
                                                      </div>
                                                      {/* H3/└/H4の3状態切り替えボタン */}
                                                      <Tag
                                                        color={
                                                          sub.placementLevel ===
                                                          'h4'
                                                            ? 'green'
                                                            : sub.placementLevel ===
                                                                  'h3_child' ||
                                                                sub.placementLevel ===
                                                                  'h4_child' ||
                                                                sub.placementLevel ===
                                                                  'h2_child'
                                                              ? undefined
                                                              : 'purple'
                                                        }
                                                        style={{
                                                          marginRight: 4,
                                                          borderRadius: 999,
                                                          cursor: 'pointer',
                                                          ...((sub.placementLevel ===
                                                            'h3_child' ||
                                                            sub.placementLevel ===
                                                              'h4_child' ||
                                                            sub.placementLevel ===
                                                              'h2_child') && {
                                                            background:
                                                              'transparent',
                                                            border: 'none',
                                                            color: '#999',
                                                          }),
                                                        }}
                                                        onClick={e => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          // H3 → └（直上の見出し_child） → H4 → H3 の循環
                                                          let nextLevel = 'h3';
                                                          if (
                                                            !sub.placementLevel ||
                                                            sub.placementLevel ===
                                                              'h3'
                                                          ) {
                                                            // H3 → └（直上の見出しを自動判定）
                                                            // 直前の見出しを探す
                                                            const prevHeading =
                                                              sections[
                                                                secIndex
                                                              ].subsections
                                                                .slice(
                                                                  0,
                                                                  subIndex,
                                                                )
                                                                .reverse()
                                                                .find(
                                                                  s =>
                                                                    s.type ===
                                                                      'heading' ||
                                                                    s.type ===
                                                                      'h4',
                                                                );
                                                            if (
                                                              prevHeading?.type ===
                                                              'h4'
                                                            ) {
                                                              nextLevel =
                                                                'h4_child';
                                                            } else if (
                                                              prevHeading?.type ===
                                                              'heading'
                                                            ) {
                                                              nextLevel =
                                                                'h3_child';
                                                            } else {
                                                              // H2直下の場合
                                                              nextLevel =
                                                                'h2_child';
                                                            }
                                                          } else if (
                                                            sub.placementLevel ===
                                                              'h3_child' ||
                                                            sub.placementLevel ===
                                                              'h4_child' ||
                                                            sub.placementLevel ===
                                                              'h2_child'
                                                          ) {
                                                            nextLevel = 'h4';
                                                          } else {
                                                            nextLevel = 'h3';
                                                          }
                                                          const list =
                                                            Array.from(
                                                              sections,
                                                            );
                                                          list[
                                                            secIndex
                                                          ].subsections[
                                                            subIndex
                                                          ] = {
                                                            ...list[secIndex]
                                                              .subsections[
                                                              subIndex
                                                            ],
                                                            placementLevel:
                                                              nextLevel,
                                                          };
                                                          setSections(list);
                                                        }}
                                                      >
                                                        {sub.placementLevel ===
                                                        'h4'
                                                          ? 'H4'
                                                          : sub.placementLevel ===
                                                              'h4_child'
                                                            ? '└'
                                                            : sub.placementLevel ===
                                                                'h3_child'
                                                              ? '└'
                                                              : 'H3'}
                                                      </Tag>
                                                      <Input
                                                        value={sub.title}
                                                        onChange={e => {
                                                          const list =
                                                            Array.from(
                                                              sections,
                                                            );
                                                          list[
                                                            secIndex
                                                          ].subsections[
                                                            subIndex
                                                          ] = {
                                                            ...list[secIndex]
                                                              .subsections[
                                                              subIndex
                                                            ],
                                                            title:
                                                              e.target.value,
                                                          };
                                                          setSections(list);
                                                        }}
                                                        style={{
                                                          flex: 1,
                                                          marginRight: 'auto',
                                                          fontWeight: 400,
                                                        }}
                                                        placeholder="見出しを入力"
                                                        bordered={false}
                                                      />
                                                    </div>
                                                    <Space>
                                                      <Tag
                                                        color="magenta"
                                                        style={{
                                                          marginRight: 0,
                                                          borderRadius: 999,
                                                        }}
                                                      >
                                                        {sub.placementMode ===
                                                        'inline_part'
                                                          ? 'パーツ'
                                                          : '適宜'}
                                                      </Tag>
                                                      <Button
                                                        size="small"
                                                        icon={
                                                          <SettingOutlined />
                                                        }
                                                        onClick={() => {
                                                          // contentの初期値を取得
                                                          let initialContent =
                                                            '';
                                                          const toppingData =
                                                            sub.toppingData;

                                                          if (toppingData) {
                                                            // toppingData.contentが文字列ならそのまま使用
                                                            if (
                                                              typeof toppingData.content ===
                                                              'string'
                                                            ) {
                                                              initialContent =
                                                                toppingData.content;
                                                            }
                                                            // contentがオブジェクトの場合
                                                            else if (
                                                              typeof toppingData.content ===
                                                                'object' &&
                                                              toppingData.content !==
                                                                null
                                                            ) {
                                                              initialContent =
                                                                toppingData
                                                                  .content
                                                                  .html ||
                                                                toppingData
                                                                  .content
                                                                  .body ||
                                                                toppingData
                                                                  .content
                                                                  .text ||
                                                                toppingData
                                                                  .content
                                                                  .summary ||
                                                                '';
                                                            }
                                                            // その他のフィールドを試す
                                                            else {
                                                              initialContent =
                                                                toppingData.text ||
                                                                toppingData.body ||
                                                                toppingData.html ||
                                                                '';
                                                            }
                                                          }

                                                          setToppingModal({
                                                            open: true,
                                                            secIndex,
                                                            subIndex,
                                                            data: sub,
                                                            mode:
                                                              sub.placementMode ||
                                                              'section_embed',
                                                            memo:
                                                              sub.memo || '',
                                                            content:
                                                              initialContent,
                                                          });
                                                        }}
                                                      />
                                                      <Button
                                                        size="small"
                                                        icon={<CopyOutlined />}
                                                        onClick={() =>
                                                          duplicateSubsection(
                                                            secIndex,
                                                            subIndex,
                                                          )
                                                        }
                                                      />
                                                      <Button
                                                        size="small"
                                                        icon={
                                                          pendingDelSub.sec ===
                                                            sec.id &&
                                                          pendingDelSub.sub ===
                                                            sub.id ? (
                                                            <ExclamationCircleOutlined />
                                                          ) : (
                                                            <DeleteOutlined />
                                                          )
                                                        }
                                                        danger={
                                                          pendingDelSub.sec ===
                                                            sec.id &&
                                                          pendingDelSub.sub ===
                                                            sub.id
                                                        }
                                                        onClick={() => {
                                                          if (
                                                            pendingDelSub.sec ===
                                                              sec.id &&
                                                            pendingDelSub.sub ===
                                                              sub.id
                                                          ) {
                                                            const list =
                                                              Array.from(
                                                                sections,
                                                              );
                                                            list[
                                                              secIndex
                                                            ].subsections.splice(
                                                              subIndex,
                                                              1,
                                                            );
                                                            setSections(list);
                                                            setPendingDelSub({
                                                              sec: null,
                                                              sub: null,
                                                            });
                                                          } else {
                                                            setPendingDelSub({
                                                              sec: sec.id,
                                                              sub: sub.id,
                                                            });
                                                            clearConfirmLater();
                                                          }
                                                        }}
                                                      />
                                                    </Space>
                                                  </div>
                                                  {/* 独自情報のデータ名とmemoを表示 */}
                                                  {(sub.toppingData?.title ||
                                                    sub.memo) && (
                                                    <div
                                                      style={{
                                                        padding: '0 12px 8px',
                                                        marginLeft:
                                                          sub.type === 'h4'
                                                            ? 36
                                                            : sub.type ===
                                                                'topping'
                                                              ? belongingIndent
                                                              : 24,
                                                      }}
                                                    >
                                                      {sub.toppingData
                                                        ?.title && (
                                                        <Typography.Text
                                                          style={{
                                                            fontSize: 12,
                                                            color: '#722ed1',
                                                            fontWeight: 500,
                                                            display: 'block',
                                                            marginBottom:
                                                              sub.memo ? 4 : 0,
                                                          }}
                                                        >
                                                          情報源:{' '}
                                                          {
                                                            sub.toppingData
                                                              .title
                                                          }
                                                        </Typography.Text>
                                                      )}
                                                      {sub.memo && (
                                                        <Typography.Text
                                                          type="secondary"
                                                          style={{
                                                            fontSize: 12,
                                                          }}
                                                        >
                                                          {sub.memo}
                                                        </Typography.Text>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      }}
                                    </Draggable>
                                  ))}
                                  {subProvided.placeholder}
                                </div>
                              )}
                            </LoggedDroppable>
                          </Card>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}

                  {/* 最後尾のドロップエリア（H3をH2として追加） */}
                  <LoggedDroppable droppableId="sec-slot-end" type="SUBSECTION">
                    {(slotProvided, slotSnapshot) => (
                      <div
                        ref={slotProvided.innerRef}
                        {...slotProvided.droppableProps}
                        style={{
                          background: slotSnapshot.isDraggingOver
                            ? '#fafafa'
                            : 'transparent',
                          borderRadius: 6,
                          padding: slotSnapshot.isDraggingOver ? '12px 0' : 0,
                          minHeight: slotSnapshot.isDraggingOver ? 40 : 4,
                          marginTop: 6,
                          marginBottom: 0,
                          transition: 'all 120ms ease-in-out',
                        }}
                      >
                        {slotProvided.placeholder}
                      </div>
                    )}
                  </LoggedDroppable>

                  {/* H2追加ボタン */}
                  <div
                    style={{
                      marginTop: 16,
                      marginBottom: 16,
                      textAlign: 'center',
                    }}
                  >
                    <Button
                      type="dashed"
                      icon={<PlusCircleOutlined />}
                      size="large"
                      onClick={() => {
                        const newSection = {
                          id: generateHeadingId(),
                          type: 'heading',
                          level: 'h2',
                          title: '新しいセクション',
                          subsections: [],
                          locked: false,
                          memo: '',
                        };
                        setSections([...sections, newSection]);
                        message.success('H2を追加しました');
                      }}
                      style={{
                        borderColor: '#d9d9d9',
                        color: '#595959',
                      }}
                    >
                      H2を追加
                    </Button>
                  </div>
                </div>
              )}
            </LoggedDroppable>

            {/* 左カラム下部フル幅アクション（移動後） */}
            <div
              id="step3-bottom-action"
              className="v2-form-narrow"
              style={{ marginTop: 16 }}
            >
              {/* 自動保存状態インジケーター */}
              {autoSaveStatus !== 'idle' && (
                <div style={{ marginBottom: 8, textAlign: 'right' }}>
                  <Typography.Text
                    type={autoSaveStatus === 'saved' ? 'success' : 'secondary'}
                    style={{ fontSize: 12 }}
                  >
                    {autoSaveStatus === 'saving' && '保存中...'}
                    {autoSaveStatus === 'saved' && '✓ 保存しました'}
                  </Typography.Text>
                </div>
              )}

              {/* メモ欄（チーム間共有用） */}
              <div style={{ marginBottom: 36 }}>
                <Typography.Text
                  style={{ display: 'block', marginBottom: 6, color: '#666' }}
                >
                  メモ（※生成には影響しません）
                </Typography.Text>
                <Input.TextArea
                  value={outlineMemo}
                  onChange={e => setOutlineMemo(e.target.value)}
                  placeholder="構成案に関するメモを記入してください（生成には使用されません）"
                  rows={4}
                  maxLength={1000}
                  showCount
                  style={{ resize: 'none' }}
                />
              </div>

              <Button
                className="tour-confirm-next-button"
                size="large"
                type="primary"
                block
                onClick={async () => {
                  try {
                    let finalSections = sections;

                    // テキストモードの場合、editorTextをsectionsに反映
                    if (editorMode === 'editor') {
                      const parsed = parseEditorTextToSections(editorText);
                      // parsedは配列を直接返す（parsed.sectionsではない）
                      if (
                        parsed &&
                        Array.isArray(parsed) &&
                        parsed.length > 0
                      ) {
                        const normalized = parsed.map(sec => ({
                          id: genId('sec'),
                          title: sec.title || 'セクション',
                          locked: false,
                          memo: sec.memo || '',
                          subsections: (sec.subsections || []).map(s => ({
                            id: genId('sub'),
                            title: s.title || '',
                            type: s.type || 'heading',
                            locked: false,
                            memo: s.memo || '',
                            toppingData: s.toppingData,
                            placementMode: s.placementMode,
                            content: s.content,
                            raw: s.raw,
                          })),
                        }));
                        finalSections = normalized;
                        setSections(normalized);
                      }
                    }

                    // 確定前に一時保存を実行（finalSectionsを使用）
                    if (uuid) {
                      const payload = {
                        title: titleH1,
                        sections: finalSections,
                        memo: outlineMemo, // メモを追加
                      };
                      await v2ArticlesApi.saveOutlineDraft(uuid, payload);
                    }

                    // current_stepを4.1に更新
                    if (uuid) {
                      await v2ArticlesApi.updateCurrentStep(uuid, '4.1');
                    }

                    // その後、次のステップへ（finalSectionsを渡す）
                    onComplete(finalSections);
                  } catch (error) {
                    console.error(
                      '[Step3] Failed to update current_step:',
                      error,
                    );
                    message.error('ステップの更新に失敗しました');
                  }
                }}
                disabled={isReadOnly || scoringStatus === 'processing'}
                loading={scoringStatus === 'processing'}
              >
                構成を確定して本文へ
              </Button>
            </div>
          </Col>
          <Col xs={24} lg={10}>
            <div
              style={{
                display: 'block',
                overflow: 'visible',
              }}
            >
              {/* スコア（上部）：2カラム（Coverage / Uniqueness） - 親カードを廃止 */}
              {hasAnalysis && (
                <div
                  className="tour-score-section"
                  style={{ marginBottom: 20 }}
                >
                  <div style={{ textAlign: 'right', marginBottom: 4 }}>
                    <HelpIcon
                      tooltip={generationStep3Help.scoring.tooltip}
                      title={generationStep3Help.scoring.title}
                      content={generationStep3Help.scoring.content}
                    />
                  </div>
                  <Row gutter={8}>
                    <Col span={12}>
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '10px 8px',
                          borderRadius: 8,
                          background:
                            'linear-gradient(180deg, rgba(255, 77, 79, 0.10) 0%, rgba(250, 173, 20, 0.10) 100%)',
                        }}
                      >
                        <Typography.Text
                          style={{ color: '#fa8c16', fontWeight: 600 }}
                        >
                          カバレッジスコア
                        </Typography.Text>
                        <div
                          style={{
                            marginTop: 8,
                            marginBottom: 0,
                            lineHeight: 0,
                          }}
                        >
                          {scoringStatus === 'processing' ||
                          (scoringStatus !== 'completed' &&
                            coverageScore == null) ? (
                            <div
                              style={{ 
                                textAlign: 'center', 
                                padding: '16px 0',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  border: '3px solid #f0f0f0',
                                  borderTopColor: '#fa8c16',
                                  animation: 'spin 1s linear infinite',
                                }}
                              />
                              <Typography.Text
                                style={{ fontSize: 12, color: '#888' }}
                              >
                                分析中...
                              </Typography.Text>
                              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            </div>
                          ) : (
                            <Progress
                              type="dashboard"
                              gapDegree={120}
                              gapPosition="bottom"
                              steps={10}
                              percent={
                                coverageScore != null
                                  ? Math.max(0, Math.min(100, coverageScore))
                                  : 0
                              }
                              format={() => (
                                <span
                                  style={{
                                    fontSize: 24,
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                    background:
                                      'linear-gradient(180deg, #ff4d4f 0%, #faad14 100%)',
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                    color: 'transparent',
                                  }}
                                >
                                  {coverageScore != null
                                    ? Math.round(coverageScore)
                                    : '-'}
                                </span>
                              )}
                              width={120}
                              strokeColor="#faad14"
                              trailColor="#ffffff"
                              strokeWidth={12}
                            />
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '10px 8px',
                          borderRadius: 8,
                          background:
                            'linear-gradient(180deg, rgba(0, 212, 255, 0.10) 0%, rgba(0, 212, 255, 0.05) 100%)',
                        }}
                      >
                        <Typography.Text
                          style={{ color: '#13c2c2', fontWeight: 600 }}
                        >
                          ユニークネススコア
                        </Typography.Text>
                        <div
                          style={{
                            marginTop: 8,
                            marginBottom: 0,
                            lineHeight: 0,
                          }}
                        >
                          {scoringStatus === 'processing' ||
                          (scoringStatus !== 'completed' &&
                            uniquenessScore == null) ? (
                            <div
                              style={{ 
                                textAlign: 'center', 
                                padding: '16px 0',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  border: '3px solid #f0f0f0',
                                  borderTopColor: '#13c2c2',
                                  animation: 'spin 1s linear infinite',
                                }}
                              />
                              <Typography.Text
                                style={{ fontSize: 12, color: '#888' }}
                              >
                                分析中...
                              </Typography.Text>
                            </div>
                          ) : (
                            <Progress
                              type="dashboard"
                              gapDegree={120}
                              gapPosition="bottom"
                              steps={10}
                              percent={
                                uniquenessScore != null
                                  ? Math.max(0, Math.min(100, uniquenessScore))
                                  : 0
                              }
                              format={() => (
                                <span
                                  style={{
                                    fontSize: 24,
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                    background:
                                      'linear-gradient(180deg, #00d4ff 0%, #13c2c2 100%)',
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                    color: 'transparent',
                                  }}
                                >
                                  {uniquenessScore != null
                                    ? Math.round(uniquenessScore)
                                    : '-'}
                                </span>
                              )}
                              width={120}
                              strokeColor="#00d4ff"
                              trailColor="#ffffff"
                              strokeWidth={12}
                            />
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <div style={{ marginTop: 8, textAlign: 'center' }}>
                    <Button
                      className="tour-analysis-open-button analysis-open-btn"
                      ghost
                      style={{
                        color: '#1677ff',
                        borderColor: '#1677ff',
                        width: '100%',
                        padding: '10px',
                      }}
                      onClick={() => setAnalysisOpen(true)}
                      icon={<SearchOutlined />}
                    >
                      分析画面を開く
                    </Button>
                  </div>
                </div>
              )}

              <Affix offsetTop={16}>
                <div ref={stickyWrapRef}>
                  <Card
                    className="tour-right-column-panel"
                    size="small"
                    bodyStyle={{
                      padding: 0,
                      display: 'flex',
                      backgroundColor: '#fafafa',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      ref={stickyHeaderRef}
                      style={{ padding: '12px 12px 0' }}
                    >
                      <Segmented
                        value={rightActive}
                        onChange={setRightActive}
                        options={
                          hasAnalysis
                            ? [
                                { label: 'トピック', value: 'topic' },
                                // { label: 'AI編集', value: 'ai' }, // 将来実装予定
                                { label: '独自情報', value: 'tops' },
                                { label: '参考データ', value: 'refs' },
                              ]
                            : [
                                // 分析結果なしの場合はトピックタブを非表示
                                { label: '独自情報', value: 'tops' },
                                { label: '参考データ', value: 'refs' },
                              ]
                        }
                        block
                      />
                    </div>
                    {rightActive === 'ai' ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: stickyBodyHeight,
                          padding: 12,
                        }}
                      >
                        {/* AI編集のタイトル行は削除し、下部入力左に設定アイコンのみ配置 */}
                        <div
                          ref={messagesWrapRef}
                          style={{
                            flex: 1,
                            overflowY: 'auto',
                            marginBottom: 8,
                          }}
                        >
                          {aiMessages.length === 0 && (
                            <Typography.Text type="secondary">
                              ここにAIとのやり取りが表示されます
                            </Typography.Text>
                          )}
                          {aiMessages.map((m, i) => (
                            <div
                              key={i}
                              className={`ai-chat-row ${m.role === 'user' ? 'ai-chat-row--user' : ''}`}
                            >
                              <div
                                className={`ai-chat-bubble ${m.role === 'user' ? 'ai-chat-bubble--user' : 'ai-chat-bubble--assistant'}`}
                              >
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                  {m.text}
                                </div>
                                {m.outline && (
                                  <Card size="small" style={{ marginTop: 6 }}>
                                    <div
                                      className={`ai-outline-preview-blur ${outlinePreviewOpen === i ? 'open' : ''}`}
                                    >
                                      <div className="ai-outline-lines">
                                        {(m.outline.outline || []).map(
                                          (h, idx) => {
                                            const level = Number(h.level || 2);
                                            const ml =
                                              level === 2
                                                ? 0
                                                : level === 3
                                                  ? 12
                                                  : 24;
                                            return (
                                              <div
                                                key={idx}
                                                className={`line ${outlinePreviewOpen === i ? '' : idx >= 3 ? 'blur' : ''}`}
                                                style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 6,
                                                  marginLeft: ml,
                                                }}
                                              >
                                                <Tag
                                                  color={
                                                    level === 2
                                                      ? 'orange'
                                                      : level === 3
                                                        ? 'gold'
                                                        : 'green'
                                                  }
                                                >{`H${level}`}</Tag>
                                                <Typography.Text
                                                  style={{ fontSize: 13 }}
                                                >
                                                  {h.text}
                                                </Typography.Text>
                                              </div>
                                            );
                                          },
                                        )}
                                      </div>
                                      {outlinePreviewOpen !== i && (
                                        <div className="ai-fade-mask" />
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 8,
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginTop: 8,
                                      }}
                                    >
                                      <Button
                                        size="small"
                                        type="text"
                                        icon={
                                          outlinePreviewOpen !== i ? (
                                            <CaretDownOutlined />
                                          ) : (
                                            <CaretUpOutlined />
                                          )
                                        }
                                        onClick={() =>
                                          setOutlinePreviewOpen(
                                            outlinePreviewOpen !== i ? i : null,
                                          )
                                        }
                                      >
                                        {outlinePreviewOpen !== i
                                          ? '展開'
                                          : '折りたたむ'}
                                      </Button>
                                      <Button
                                        size="small"
                                        type="primary"
                                        onClick={() => {
                                          // 適用: 右AIの構成案を左のsectionsへ反映（シンプル変換）
                                          const arr = m.outline.outline || [];
                                          const newSecs = [];
                                          let cur = null;
                                          arr.forEach(h => {
                                            if (h.level === 2) {
                                              cur = {
                                                title: h.text,
                                                subsections: [],
                                              };
                                              newSecs.push(cur);
                                            } else if (h.level === 3) {
                                              if (!cur) {
                                                cur = {
                                                  title: h.text,
                                                  subsections: [],
                                                };
                                                newSecs.push(cur);
                                              }
                                              cur.subsections.push({
                                                title: h.text,
                                                type: 'heading',
                                              });
                                            } else if (h.level === 4) {
                                              if (!cur) {
                                                cur = {
                                                  title: h.text,
                                                  subsections: [],
                                                };
                                                newSecs.push(cur);
                                              }
                                              cur.subsections.push({
                                                title: h.text + ' (H4)',
                                                type: 'h4',
                                                level: 4,
                                              });
                                            }
                                          });
                                          if (newSecs.length > 0)
                                            setSections(newSecs);
                                        }}
                                      >
                                        適用する
                                      </Button>
                                    </div>
                                  </Card>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'flex-end',
                          }}
                        >
                          <Button
                            type="text"
                            icon={<SettingOutlined />}
                            onClick={() => setContextModal({ open: true })}
                          />
                          <Input.TextArea
                            value={aiInput}
                            onChange={e => setAiInput(e.target.value)}
                            placeholder="AIに指示（Enterで改行、Ctrl+Enterで送信）"
                            autoSize={{ minRows: 1, maxRows: 6 }}
                            onPressEnter={e => {
                              if (e.ctrlKey || e.metaKey) {
                                e.preventDefault();
                                const v = aiInput.trim();
                                if (!v) return;
                                setAiMessages(prev => [
                                  ...prev,
                                  { role: 'user', text: v },
                                ]);
                                // ダミー応答（構成案モックアップ判定）
                                const dummyOutline = {
                                  outline: [
                                    {
                                      level: 2,
                                      text: '導入：本記事のポイントと前提',
                                    },
                                    { level: 3, text: '前提条件' },
                                    { level: 3, text: 'この記事で分かること' },
                                    { level: 2, text: '基本：概念と仕組み' },
                                    { level: 3, text: '用語の整理' },
                                    { level: 4, text: '関連概念との違い' },
                                    { level: 2, text: '実践：手順と注意点' },
                                    { level: 3, text: '手順の全体像' },
                                    { level: 3, text: '失敗しやすいポイント' },
                                    { level: 4, text: 'チェックリスト' },
                                    {
                                      level: 2,
                                      text: '応用：活用パターンと事例',
                                    },
                                    { level: 3, text: '小規模での適用' },
                                    { level: 3, text: '大規模での適用' },
                                    { level: 4, text: 'ケーススタディ' },
                                    {
                                      level: 2,
                                      text: 'まとめ：次のアクション',
                                    },
                                  ],
                                };
                                setAiMessages(prev => [
                                  ...prev,
                                  {
                                    role: 'assistant',
                                    text: '変更後の構成案（プレビュー）',
                                    outline: dummyOutline,
                                  },
                                ]);
                                setAiInput('');
                                try {
                                  setTimeout(() => {
                                    const wrap = messagesWrapRef.current;
                                    if (wrap)
                                      wrap.scrollTop = wrap.scrollHeight;
                                  }, 0);
                                } catch (_) {
                                  void 0;
                                }
                              }
                            }}
                          />
                          <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={() => {
                              const v = aiInput.trim();
                              if (!v) return;
                              setAiMessages(prev => [
                                ...prev,
                                { role: 'user', text: v },
                              ]);
                              const dummyOutline = {
                                outline: [
                                  {
                                    level: 2,
                                    text: '導入：本記事のポイントと前提',
                                  },
                                  { level: 3, text: '前提条件' },
                                  { level: 3, text: 'この記事で分かること' },
                                  { level: 2, text: '基本：概念と仕組み' },
                                  { level: 3, text: '用語の整理' },
                                  { level: 4, text: '関連概念との違い' },
                                  { level: 2, text: '実践：手順と注意点' },
                                  { level: 3, text: '手順の全体像' },
                                  { level: 3, text: '失敗しやすいポイント' },
                                  { level: 4, text: 'チェックリスト' },
                                  {
                                    level: 2,
                                    text: '応用：活用パターンと事例',
                                  },
                                  { level: 3, text: '小規模での適用' },
                                  { level: 3, text: '大規模での適用' },
                                  { level: 4, text: 'ケーススタディ' },
                                  { level: 2, text: 'まとめ：次のアクション' },
                                ],
                              };
                              setAiMessages(prev => [
                                ...prev,
                                {
                                  role: 'assistant',
                                  text: '変更後の構成案（プレビュー）',
                                  outline: dummyOutline,
                                },
                              ]);
                              setAiInput('');
                              try {
                                setTimeout(() => {
                                  const wrap = messagesWrapRef.current;
                                  if (wrap) wrap.scrollTop = wrap.scrollHeight;
                                }, 0);
                              } catch (_) {
                                void 0;
                              }
                            }}
                          />
                        </div>
                      </div>
                    ) : rightActive === 'tops' ? (
                      <div
                        style={{
                          height: stickyBodyHeight,
                          overflowY: 'auto',
                          padding: 12,
                        }}
                      >
                        {toppingsLoading ? (
                          <div
                            style={{ textAlign: 'center', padding: '40px 0' }}
                          >
                            <Skeleton active />
                            <Skeleton active />
                          </div>
                        ) : rightToppings.length === 0 ? (
                          <div
                            style={{
                              padding: '20px 12px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: 6,
                              textAlign: 'center',
                            }}
                          >
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 13 }}
                            >
                              独自情報がありません
                            </Typography.Text>
                          </div>
                        ) : (
                          <LoggedDroppable
                            droppableId="tops-list"
                            type="SUBSECTION"
                          >
                            {provided => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {rightToppings.map((item, index) => (
                                  <Draggable
                                    key={item.id}
                                    draggableId={`top-${item.id}`}
                                    index={index}
                                  >
                                    {(drag, snap) => (
                                      <div
                                        ref={drag.innerRef}
                                        {...drag.draggableProps}
                                        {...drag.dragHandleProps}
                                        style={{
                                          ...drag.draggableProps.style,
                                          padding: '8px 12px',
                                          marginBottom: 8,
                                          background:
                                            'linear-gradient(135deg, rgba(158,130,216,0.06) 0%, rgba(217,70,239,0.06) 100%)',
                                          border:
                                            '1px solid rgba(217,70,239,0.12)',
                                          borderRadius: 8,
                                          cursor: 'grab',
                                          transition: snap?.isDragging
                                            ? 'none'
                                            : 'transform 120ms ease',
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <div style={{ width: '100%' }}>
                                            <Typography.Text strong>
                                              {item.title}
                                            </Typography.Text>
                                            <div style={{ marginTop: 4 }}>
                                              <Tag
                                                color="purple"
                                                style={{ fontSize: 11 }}
                                              >
                                                {item.category}
                                              </Tag>
                                            </div>
                                            {item.displaySummary && (
                                              <Typography.Text
                                                type="secondary"
                                                style={{
                                                  fontSize: 12,
                                                  display: 'block',
                                                  marginTop: 6,
                                                  lineHeight: 1.4,
                                                }}
                                              >
                                                {item.displaySummary}
                                              </Typography.Text>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </LoggedDroppable>
                        )}
                      </div>
                    ) : rightActive === 'refs' ? (
                      <div
                        style={{
                          height: stickyBodyHeight,
                          overflowY: 'auto',
                          padding: 12,
                        }}
                      >
                        {researchFindings.length === 0 ? (
                          <div
                            style={{
                              padding: '20px 12px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: 6,
                              textAlign: 'center',
                            }}
                          >
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 13 }}
                            >
                              参考データがありません
                            </Typography.Text>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px', fontWeight: 600 }}>
                              参考データ ({researchFindings.length}件)
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {researchFindings.map((finding, i) => {
                                const refId = i + 1;
                                const claimText = finding.claim || finding.finding || finding.content || '';
                                const truncatedClaim = claimText.length > 80 ? claimText.substring(0, 80) + '…' : claimText;
                                const sourceUrl = finding.source_url || '';
                                
                                return (
                                  <div
                                    key={i}
                                    style={{
                                      padding: '10px 12px',
                                      background: '#fff',
                                      borderRadius: 6,
                                      border: '1px solid #e8e8e8',
                                      display: 'flex',
                                      gap: 10,
                                    }}
                                  >
                                    {/* 参照番号 */}
                                    <div
                                      style={{
                                        minWidth: 24,
                                        height: 24,
                                        borderRadius: 12,
                                        background: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: '#666',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {refId}
                                    </div>
                                    {/* クレーム + ドメイン */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        style={{
                                          fontSize: '12px',
                                          lineHeight: 1.5,
                                          color: '#333',
                                          marginBottom: 4,
                                        }}
                                      >
                                        {truncatedClaim}
                                      </div>
                                      {sourceUrl && (
                                        <a
                                          href={sourceUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            fontSize: '11px',
                                            color: '#1677ff',
                                            textDecoration: 'none',
                                          }}
                                        >
                                          {extractDomain(sourceUrl)}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          height: stickyBodyHeight,
                          overflowY: 'auto',
                          padding: 12,
                        }}
                      >
                        <div style={{ marginBottom: 12, padding: '8px 10px', background: '#f0f5ff', borderRadius: 6, fontSize: 12, lineHeight: 1.6, color: '#595959' }}>
                          リサーチで発見されたトピックです。ゲージが高い＝構成案で十分カバーできている、ゲージが低い＝カバーが不足しているトピックです。カバーが不足しているトピックを見出しに追加するか検討しましょう。
                        </div>
                        {/* スコア範囲スライダー */}
                        <div style={{ marginBottom: 16, padding: '0 4px' }}>
                          <Typography.Text
                            style={{
                              display: 'block',
                              marginBottom: 8,
                              fontSize: 12,
                              color: '#666',
                            }}
                          >
                            表示するトピックをカバー度合いで絞り込み（左に寄せると、現在の構成案でカバーできていないトピックだけを確認できます）
                          </Typography.Text>
                          <Slider
                            range
                            min={0}
                            max={10}
                            step={0.5}
                            value={scoreRange}
                            onChange={setScoreRange}
                            marks={{
                              0: '0',
                              5: '5',
                              10: '10',
                            }}
                            tooltip={{
                              formatter: value => `${value}`,
                            }}
                          />
                        </div>

                        <Collapse
                          accordion
                          size="small"
                          className="v2-topic-accordion"
                          expandIconPosition="end"
                          activeKey={topicActiveKey}
                          onChange={setTopicActiveKey}
                        >
                          {(filteredTopicGroups || []).map(
                            (group, groupIdx) => (
                              <Collapse.Panel
                                key={group.key}
                                header={
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'inline-block',
                                        marginRight: 2,
                                      }}
                                    >
                                      {scoringStatus !== 'completed' ||
                                      Object.keys(topicScores).length === 0 ? (
                                        <Skeleton.Button
                                          active
                                          size="small"
                                          style={{
                                            width: 60,
                                            height: 8,
                                            minWidth: 60,
                                          }}
                                        />
                                      ) : (
                                        <Progress
                                          size="small"
                                          percent={Math.round(
                                            (Math.max(
                                              0,
                                              Math.min(
                                                10,
                                                (group.items || []).reduce(
                                                  (a, t) =>
                                                    a +
                                                    (topicScores[
                                                      t.topicId || t.name
                                                    ] || 0),
                                                  0,
                                                ) /
                                                  Math.max(
                                                    1,
                                                    (group.items || []).length,
                                                  ),
                                              ),
                                            ) /
                                              10) *
                                              100,
                                          )}
                                          steps={5}
                                          showInfo={false}
                                          strokeColor="#00d4ff"
                                          trailColor="#f0f0f0"
                                        />
                                      )}
                                    </div>
                                    <Typography.Text strong>
                                      {group.label}
                                    </Typography.Text>
                                  </div>
                                }
                              >
                                <LoggedDroppable
                                  droppableId={`topic-group-${group.key}`}
                                  type="SUBSECTION"
                                  isDropDisabled={true}
                                >
                                  {provided => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.droppableProps}
                                    >
                                      {(group.items || []).map((t, idx) => {
                                        const key =
                                          t.topicId ||
                                          t.name ||
                                          `${group.key}-${idx}`;
                                        const score = topicScores[key] ?? 5.0;
                                        const statusRaw = (t.status || '')
                                          .toString()
                                          .toLowerCase();
                                        const labels = [];
                                        if (statusRaw.includes('must'))
                                          labels.push({
                                            text: '必須',
                                            color: 'magenta',
                                          });
                                        if (statusRaw.includes('important'))
                                          labels.push({
                                            text: '重要',
                                            color: 'gold',
                                          });
                                        if (statusRaw.includes('unique'))
                                          labels.push({
                                            text: '独自',
                                            color: 'cyan',
                                          });
                                        if (labels.length === 0) {
                                          const cls = classifyTopic(t);
                                          if (cls)
                                            labels.push({
                                              text: cls.jp,
                                              color: cls.color,
                                            });
                                        }
                                        const limited = labels.slice(0, 2);
                                        const present = presentNameSet.has(
                                          t.topic || t.name || t.label || '',
                                        );
                                        return (
                                          <Draggable
                                            key={key}
                                            draggableId={`topic-${key}`}
                                            index={idx}
                                          >
                                            {(provided, snapshot) => (
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={{
                                                  ...provided.draggableProps
                                                    .style,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 12,
                                                  padding: '6px 4px',
                                                  borderBottom:
                                                    '1px dashed #f0f0f0',
                                                  backgroundColor:
                                                    snapshot.isDragging
                                                      ? '#e6f7ff'
                                                      : 'transparent',
                                                  cursor: snapshot.isDragging
                                                    ? 'grabbing'
                                                    : 'grab',
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: 'inline-block',
                                                    marginRight: 4,
                                                  }}
                                                >
                                                  {scoringStatus !==
                                                    'completed' ||
                                                  Object.keys(topicScores)
                                                    .length === 0 ? (
                                                    <Skeleton.Button
                                                      active
                                                      size="small"
                                                      style={{
                                                        width: 60,
                                                        height: 8,
                                                        minWidth: 60,
                                                      }}
                                                    />
                                                  ) : (
                                                    <Tooltip
                                                      title={
                                                        <div
                                                          style={{
                                                            padding: '4px 0',
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              marginBottom: 8,
                                                              fontSize: 12,
                                                              color: '#bbb',
                                                            }}
                                                          >
                                                            構成案でのカバー状況をAIが算出したスコアをゲージ化しています。
                                                          </div>
                                                          <div
                                                            style={{
                                                              display: 'flex',
                                                              justifyContent:
                                                                'space-around',
                                                              gap: 16,
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                textAlign:
                                                                  'center',
                                                              }}
                                                            >
                                                              <div
                                                                style={{
                                                                  fontSize: 10,
                                                                  color: '#bbb',
                                                                  marginBottom: 4,
                                                                }}
                                                              >
                                                                スコア
                                                              </div>
                                                              <div
                                                                style={{
                                                                  fontSize: 18,
                                                                  fontWeight:
                                                                    'bold',
                                                                  color:
                                                                    '#00d4ff',
                                                                }}
                                                              >
                                                                {score.toFixed(
                                                                  1,
                                                                )}
                                                              </div>
                                                            </div>
                                                            <div
                                                              style={{
                                                                textAlign:
                                                                  'center',
                                                              }}
                                                            >
                                                              <div
                                                                style={{
                                                                  fontSize: 10,
                                                                  color: '#bbb',
                                                                  marginBottom: 4,
                                                                }}
                                                              >
                                                                AVG
                                                              </div>
                                                              <div
                                                                style={{
                                                                  fontSize: 18,
                                                                  fontWeight:
                                                                    'bold',
                                                                  color:
                                                                    '#00d4ff',
                                                                }}
                                                              >
                                                                {(
                                                                  avgScores[
                                                                    key
                                                                  ] ?? 5.0
                                                                ).toFixed(1)}
                                                              </div>
                                                            </div>
                                                          </div>
                                                        </div>
                                                      }
                                                      overlayInnerStyle={{
                                                        width: 260,
                                                      }}
                                                    >
                                                      <Progress
                                                        size="small"
                                                        percent={Math.round(
                                                          (Math.max(
                                                            0,
                                                            Math.min(10, score),
                                                          ) /
                                                            10) *
                                                            100,
                                                        )}
                                                        steps={5}
                                                        showInfo={false}
                                                        strokeColor="#00d4ff"
                                                        trailColor="#f0f0f0"
                                                      />
                                                    </Tooltip>
                                                  )}
                                                </div>
                                                <span
                                                  style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                  }}
                                                  title={t.name}
                                                >
                                                  <Typography.Text
                                                    style={{
                                                      whiteSpace: 'normal',
                                                      wordBreak: 'break-word',
                                                      lineHeight: 1.5,
                                                      display: 'block',
                                                    }}
                                                  >
                                                    {t.name ||
                                                      t.topic ||
                                                      t.keyword}
                                                  </Typography.Text>
                                                </span>
                                                <span
                                                  style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 4,
                                                  }}
                                                >
                                                  {limited.map((lb, i) => {
                                                    const labelTooltips = {
                                                      '必須': '上位サイトの多くが扱っているトピック。構成案に含めることを強く推奨します',
                                                      '重要': '一定数の上位サイトが扱っているトピック。含めると記事の網羅性が向上します',
                                                      '独自': '上位サイトではあまり扱われていないトピック。差別化ポイントになり得ます',
                                                    };
                                                    return (
                                                      <Tooltip key={i} title={labelTooltips[lb.text] || ''}>
                                                        <Tag
                                                          color={lb.color}
                                                          style={{
                                                            marginRight: 0,
                                                            border: 'none',
                                                            fontSize: 12,
                                                            cursor: 'help',
                                                          }}
                                                        >
                                                          {lb.text}
                                                        </Tag>
                                                      </Tooltip>
                                                    );
                                                  })}
                                                </span>
                                              </div>
                                            )}
                                          </Draggable>
                                        );
                                      })}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </LoggedDroppable>
                              </Collapse.Panel>
                            ),
                          )}
                        </Collapse>
                      </div>
                    )}
                    {/* コンテキスト設定モーダル */}
                    <Modal
                      title="コンテキスト設定"
                      open={contextModal.open}
                      onCancel={() => setContextModal({ open: false })}
                      footer={null}
                      width={820}
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Row gutter={12}>
                          <Col span={8}>
                            <Card
                              size="small"
                              title="トピック一覧"
                              bodyStyle={{ maxHeight: 240, overflowY: 'auto' }}
                            >
                              <Checkbox
                                checked={contextSelections.topics.size > 0}
                                onChange={e => {
                                  setContextSelections(prev => ({
                                    ...prev,
                                    topics: e.target.checked
                                      ? new Set(['__any__'])
                                      : new Set(),
                                  }));
                                }}
                              >
                                トピックを使う
                              </Checkbox>
                            </Card>
                          </Col>
                          <Col span={8}>
                            <Card
                              size="small"
                              title="重要語句/関連KW/LSI"
                              bodyStyle={{ maxHeight: 240, overflowY: 'auto' }}
                            >
                              <Checkbox
                                checked={contextSelections.keywords.size > 0}
                                onChange={e => {
                                  setContextSelections(prev => ({
                                    ...prev,
                                    keywords: e.target.checked
                                      ? new Set(['__any__'])
                                      : new Set(),
                                  }));
                                }}
                              >
                                キーワード群を使う
                              </Checkbox>
                            </Card>
                          </Col>
                          <Col span={8}>
                            <Card
                              size="small"
                              title="競合見出し"
                              bodyStyle={{ maxHeight: 240, overflowY: 'auto' }}
                            >
                              <Checkbox
                                checked={contextSelections.competitors.size > 0}
                                onChange={e => {
                                  setContextSelections(prev => ({
                                    ...prev,
                                    competitors: e.target.checked
                                      ? new Set(['__any__'])
                                      : new Set(),
                                  }));
                                }}
                              >
                                競合見出しを使う
                              </Checkbox>
                            </Card>
                          </Col>
                        </Row>
                        <Card size="small" title="過去の構成案">
                          <Space style={{ width: '100%', marginBottom: 8 }}>
                            <Input
                              placeholder="生成結果を検索"
                              prefix={<SearchOutlined />}
                              value={pastOutlineSearch}
                              onChange={e =>
                                setPastOutlineSearch(e.target.value)
                              }
                              style={{ maxWidth: 360 }}
                            />
                            <Switch
                              checkedChildren="プレビューOn"
                              unCheckedChildren="プレビューOff"
                              checked={pastPreviewOn}
                              onChange={setPastPreviewOn}
                            />
                          </Space>
                          <Row gutter={12}>
                            {[
                              {
                                id: 'prev-1',
                                title: '前回構成案A',
                                outline: [
                                  { level: 2, text: 'H2 前回A' },
                                  { level: 3, text: 'H3 補足' },
                                  { level: 4, text: 'H4 詳細' },
                                ],
                              },
                              {
                                id: 'prev-2',
                                title: '前回構成案B',
                                outline: [
                                  { level: 2, text: 'H2 別案' },
                                  { level: 3, text: 'H3 解説' },
                                ],
                              },
                            ]
                              .filter(
                                o =>
                                  !pastOutlineSearch ||
                                  o.title.includes(pastOutlineSearch),
                              )
                              .map((o, k) => {
                                const checked =
                                  contextSelections.pastOutlines.has(o.id);
                                const open = !!pastOutlinePreview[o.id];
                                return (
                                  <Col span={12} key={o.id}>
                                    <Card
                                      size="small"
                                      style={{ marginBottom: 8 }}
                                    >
                                      <div
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Checkbox
                                          checked={checked}
                                          onChange={e => {
                                            setContextSelections(prev => {
                                              const next = {
                                                ...prev,
                                                pastOutlines: new Set(
                                                  prev.pastOutlines,
                                                ),
                                              };
                                              if (e.target.checked)
                                                next.pastOutlines.add(o.id);
                                              else
                                                next.pastOutlines.delete(o.id);
                                              return next;
                                            });
                                          }}
                                        >
                                          {o.title}
                                        </Checkbox>
                                        <Button
                                          size="small"
                                          type="link"
                                          onClick={() =>
                                            setPastOutlinePreview(prev => ({
                                              ...prev,
                                              [o.id]: !open,
                                            }))
                                          }
                                        >
                                          {open
                                            ? 'プレビューを閉じる'
                                            : 'プレビューを表示'}
                                        </Button>
                                      </div>
                                      {open && (
                                        <div style={{ marginTop: 6 }}>
                                          {o.outline.map((h, j) => (
                                            <div
                                              key={j}
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                              }}
                                            >
                                              <Tag
                                                color={
                                                  h.level === 2
                                                    ? 'orange'
                                                    : h.level === 3
                                                      ? 'gold'
                                                      : 'green'
                                                }
                                              >{`H${h.level}`}</Tag>
                                              <Typography.Text>
                                                {h.text}
                                              </Typography.Text>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </Card>
                                  </Col>
                                );
                              })}
                          </Row>
                        </Card>
                      </Space>
                    </Modal>
                  </Card>
                </div>
              </Affix>
            </div>
          </Col>
        </Row>
      </DragDropContext>

      {/* 展開モード設定モーダル */}
      <Modal
        title="展開モードの設定"
        open={expansionModal.open}
        onCancel={() => setExpansionModal({ open: false, secIndex: null })}
        onOk={() => {
          if (typeof expansionModal.secIndex === 'number') {
            const sec = sections[expansionModal.secIndex];
            const sourceToppingId =
              sec?.autoExpansion?.sourceToppingId || sec?.expansionTopping?.id;

            // H2見出し名を保存
            const list = Array.from(sections);
            if (expansionConfig.h2Title) {
              list[expansionModal.secIndex].title = expansionConfig.h2Title;
            }
            list[expansionModal.secIndex].autoExpansion = {
              ...list[expansionModal.secIndex].autoExpansion,
              enabled: true,
              count: Number(expansionConfig.count) || 1,
              basis: expansionConfig.basis || 'top',
              memo: expansionConfig.memo || '',
              sourceToppingId,
            };
            setSections(list);
          }
          setExpansionModal({ open: false, secIndex: null });
        }}
        okText="保存"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Typography.Text
              strong
              style={{ display: 'block', marginBottom: 8 }}
            >
              H2見出し名
            </Typography.Text>
            <Input
              value={
                expansionConfig.h2Title ||
                sections[expansionModal.secIndex || 0]?.title ||
                ''
              }
              onChange={e =>
                setExpansionConfig(cfg => ({ ...cfg, h2Title: e.target.value }))
              }
              placeholder="H2見出しを入力"
            />
          </div>

          <div>
            <Typography.Text
              strong
              style={{ display: 'block', marginBottom: 8 }}
            >
              展開数
            </Typography.Text>
            <InputNumber
              min={1}
              max={20}
              value={expansionConfig.count}
              onChange={v =>
                setExpansionConfig(cfg => ({ ...cfg, count: Number(v) || 1 }))
              }
              style={{ width: 120 }}
            />
          </div>

          <div>
            <Typography.Text
              strong
              style={{ display: 'block', marginBottom: 8 }}
            >
              展開基準
            </Typography.Text>
            <Select
              value={expansionConfig.basis}
              style={{ width: 240 }}
              options={[
                { value: 'top', label: '重要度順' },
                { value: 'new', label: '新規優先' },
              ]}
              onChange={v => setExpansionConfig(cfg => ({ ...cfg, basis: v }))}
            />
          </div>

          <div>
            <Typography.Text
              strong
              style={{ display: 'block', marginBottom: 8 }}
            >
              方針・展開イメージ
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
            >
              この独自情報をどのように展開してコンテンツ化するか記載してください
            </Typography.Text>
            <Input.TextArea
              rows={5}
              placeholder="例：各商品の特徴を比較表形式で展開し、選び方のポイントも併記する"
              value={expansionConfig.memo}
              onChange={e =>
                setExpansionConfig(cfg => ({ ...cfg, memo: e.target.value }))
              }
            />
          </div>
        </Space>
      </Modal>
      <Modal
        title={null}
        open={analysisOpen}
        onCancel={() => setAnalysisOpen(false)}
        footer={null}
        width="90vw"
        style={{ top: 16, paddingTop: 0 }}
        bodyStyle={{
          height: '86vh',
          overflow: 'hidden',
          paddingTop: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Tabs
          defaultActiveKey="score"
          tabBarGutter={0}
          tabBarStyle={{ position: 'sticky', top: 0, zIndex: 5, width: '100%' }}
          className="analysis-tabs-full"
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minWidth: 0,
          }}
          items={[
            {
              key: 'score',
              label: 'スコア',
              children: (() => {
                // 内訳の簡易算出（要件仕様ベースの概算）
                const depthOf = lvl =>
                  lvl === 2 ? 1.0 : lvl === 3 ? 0.8 : lvl >= 4 ? 0.6 : 0.0;
                const topics = Array.isArray(fxTopics) ? fxTopics : [];
                const classifyWeight = t => {
                  const avg = Number(
                    t?.avg ||
                      t?.average_normalized_score ||
                      t?.normalized_score ||
                      0,
                  );
                  if (avg >= 7) return 1.0; // must
                  if (avg >= 5) return 0.7; // important
                  if (avg > 0) return 0.5; // unique candidate or others
                  return 0.3;
                };
                // 現在構成案におけるPresent/Depthを推定
                const presentDepthByName = new Map();
                try {
                  sections.forEach(sec => {
                    const h2 = String(sec.title || '').trim();
                    if (h2)
                      presentDepthByName.set(
                        h2,
                        Math.max(presentDepthByName.get(h2) || 0, depthOf(2)),
                      );
                    (sec.subsections || []).forEach(sub => {
                      const ttl = String(sub.title || '')
                        .replace(/\s*\(H4\)\s*$/i, '')
                        .trim();
                      const lvl =
                        sub?.type === 'h4' || sub?.level === 4 ? 4 : 3;
                      if (ttl)
                        presentDepthByName.set(
                          ttl,
                          Math.max(
                            presentDepthByName.get(ttl) || 0,
                            depthOf(lvl),
                          ),
                        );
                    });
                  });
                } catch (e) {
                  // ignore
                }
                // 主要/候補などを雑に分配しCore/Rec/Miss等を概算
                let coreNum = 0,
                  coreDen = 0,
                  recNum = 0,
                  recDen = 0,
                  missNum = 0,
                  missDen = 0;
                topics.forEach(t => {
                  const name = t.topic || t.name || t.label || '';
                  const w = classifyWeight(t);
                  const depth = presentDepthByName.get(name) || 0;
                  const isMustImp = w >= 0.7;
                  const isUniqueCand = w === 0.5;
                  if (isMustImp) {
                    coreNum += w * depth;
                    coreDen += w;
                    missDen += w;
                    if (depth <= 0) missNum += 0.3 * w;
                  } else if (isUniqueCand) {
                    recNum += 0.5 * w * depth;
                    recDen += 0.5 * w;
                  }
                });
                const Core = coreDen > 0 ? coreNum / coreDen : 0;
                const Rec = recDen > 0 ? recNum / recDen : 0;
                const Miss = missDen > 0 ? missNum / missDen : 0;
                // 深さの薄さ（中央値近似: H2:1, H3:0.8, H4:0.6 の単純平均）
                const allDepths = [];
                sections.forEach(s => {
                  allDepths.push(1.0);
                  (s.subsections || []).forEach(sub =>
                    allDepths.push(
                      depthOf(sub?.type === 'h4' || sub?.level === 4 ? 4 : 3),
                    ),
                  );
                });
                const med = allDepths.length
                  ? allDepths.reduce((a, b) => a + b, 0) / allDepths.length
                  : 0.6;
                const Thin = Math.max(0, 0.2 - (med - 0.6));
                // 冗長ペナルティ（簡易版）
                const L = sections.reduce(
                  (acc, s) =>
                    acc +
                    1 +
                    (s.subsections || []).reduce(
                      (a, sub) =>
                        a +
                        (sub?.type === 'h4' || sub?.level === 4 ? 0.4 : 0.6),
                      0,
                    ),
                  0,
                );
                const Lref = Math.max(1, L * 0.9);
                const Verbose = Math.max(0, (L - 1.15 * Lref) / (0.85 * Lref));
                const Coverage =
                  Math.max(
                    0,
                    Math.min(
                      1,
                      (0.8 * (Core - Miss) + 0.2 * Rec) *
                        (1 - 0.5 * Thin) *
                        (1 - 0.4 * Verbose),
                    ),
                  ) * 100;

                // ユニークネスの概算
                const uniqueCover = recDen > 0 ? recNum / recDen : 0; // UniqueCandidateのPresent率相当
                const JSdiv = 0.5; // ダミー（本番は競合分布とJensen–Shannon）
                const JSnorm = JSdiv * 0.7;
                const PenMiss = Math.min(0.3, Miss * 0.33);
                const Uniqueness =
                  Math.max(
                    0,
                    Math.min(
                      1,
                      (0.6 * uniqueCover + 0.4 * JSnorm) * (1 - PenMiss),
                    ),
                  ) * 100;

                // 定性評価（簡易）
                const qual = [];
                if (Core < 0.7)
                  qual.push('必須・重要トピックの深さ/網羅が不足しています');
                if (Rec < 0.5)
                  qual.push('ユニーク候補トピックの取り込みが弱いです');
                if (Thin > 0.05)
                  qual.push('全体として見出しの深さが浅い傾向です');
                if (Verbose > 0.1)
                  qual.push('見出し数が多く冗長です。統合を検討してください');
                if (qual.length === 0)
                  qual.push('バランス良くカバーできています');

                const actions = [];
                if (Core < 0.7)
                  actions.push('H2直下に不足トピックのH3を2つ以上追加');
                if (Rec < 0.5)
                  actions.push(
                    'ユニーク候補から差別化できるトピックを1〜2件採用',
                  );
                if (Thin > 0.05)
                  actions.push('重要H3の下にH4で詳細説明を1段追加');
                if (Verbose > 0.1)
                  actions.push('重複するH3/H4を統合し情報密度を改善');

                return (
                  <>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {scoringStatus === 'completed' && scoringData ? (
                            <Typography.Text
                              type="secondary"
                              style={{ display: 'block', textAlign: 'center' }}
                            >
                              スコアリング日時：
                              {new Date().toLocaleString('ja-JP', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography.Text>
                          ) : null}
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: 8,
                              alignItems: 'stretch',
                            }}
                          >
                            <Card
                              size="small"
                              className="score-gauge-card"
                              bodyStyle={{ padding: 8, textAlign: 'center' }}
                            >
                              <Typography.Text
                                style={{ color: '#fa8c16', fontWeight: 600 }}
                              >
                                カバレッジスコア
                              </Typography.Text>
                              <div style={{ marginTop: 6 }}>
                                {scoringStatus !== 'completed' ? (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px 0',
                                    }}
                                  >
                                    <Typography.Text
                                      style={{ fontSize: 32, color: '#fa8c16' }}
                                    >
                                      ???
                                    </Typography.Text>
                                  </div>
                                ) : (
                                  <Progress
                                    type="dashboard"
                                    percent={Math.round(coverageScore || 0)}
                                    width={120}
                                    strokeColor="#faad14"
                                    trailColor="#ffffff"
                                    strokeWidth={12}
                                    gapDegree={120}
                                    gapPosition="bottom"
                                    steps={10}
                                    format={() => (
                                      <span
                                        style={{
                                          fontSize: 22,
                                          fontWeight: 700,
                                          background:
                                            'linear-gradient(180deg, #ff4d4f 0%, #faad14 100%)',
                                          WebkitBackgroundClip: 'text',
                                          backgroundClip: 'text',
                                          color: 'transparent',
                                        }}
                                      >
                                        {Math.round(coverageScore || 0)}
                                      </span>
                                    )}
                                  />
                                )}
                              </div>
                            </Card>
                            <Card
                              size="small"
                              className="score-gauge-card score-gauge-card--unique"
                              bodyStyle={{ padding: 8, textAlign: 'center' }}
                            >
                              <Typography.Text
                                style={{ color: '#13c2c2', fontWeight: 600 }}
                              >
                                ユニークネススコア
                              </Typography.Text>
                              <div style={{ marginTop: 6 }}>
                                {scoringStatus !== 'completed' ? (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px 0',
                                    }}
                                  >
                                    <Typography.Text
                                      style={{ fontSize: 32, color: '#13c2c2' }}
                                    >
                                      ???
                                    </Typography.Text>
                                  </div>
                                ) : (
                                  <Progress
                                    type="dashboard"
                                    percent={Math.round(uniquenessScore || 0)}
                                    width={120}
                                    strokeColor="#00d4ff"
                                    trailColor="#ffffff"
                                    strokeWidth={12}
                                    gapDegree={120}
                                    gapPosition="bottom"
                                    steps={10}
                                    format={() => (
                                      <span
                                        style={{
                                          fontSize: 22,
                                          fontWeight: 700,
                                          background:
                                            'linear-gradient(180deg, #00d4ff 0%, #13c2c2 100%)',
                                          WebkitBackgroundClip: 'text',
                                          backgroundClip: 'text',
                                          color: 'transparent',
                                        }}
                                      >
                                        {Math.round(uniquenessScore || 0)}
                                      </span>
                                    )}
                                  />
                                )}
                              </div>
                            </Card>
                          </div>
                          <div className="breakdowns-grid">
                            <Card
                              size="small"
                              title="カバレッジ内訳"
                              bodyStyle={{ padding: 12 }}
                            >
                              {scoringStatus !== 'completed' ||
                              !scoringData?.coverage_breakdown ? (
                                <div className="score-breakdown">
                                  {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="score-chip">
                                      <Skeleton.Input
                                        active
                                        size="small"
                                        style={{
                                          width: '100%',
                                          marginBottom: 8,
                                        }}
                                      />
                                      <Skeleton.Button
                                        active
                                        size="small"
                                        block
                                        style={{ height: 8 }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="score-breakdown">
                                  {[
                                    {
                                      key: 'required',
                                      label: '必須トピック',
                                      value:
                                        scoringData.coverage_breakdown
                                          ?.required_topics || 0,
                                      tip: '競合の多くが言及する必須トピックのカバー状況',
                                    },
                                    {
                                      key: 'important',
                                      label: '重要トピック',
                                      value:
                                        scoringData.coverage_breakdown
                                          ?.important_topics || 0,
                                      tip: '一部競合が強く扱う重要トピックのカバー状況',
                                    },
                                    {
                                      key: 'keywords',
                                      label: 'キーワード含有',
                                      value:
                                        scoringData.coverage_breakdown
                                          ?.keywords || 0,
                                      tip: '重要語句・関連KW・LSIの含有状況（競合比較）',
                                    },
                                    {
                                      key: 'mece',
                                      label: 'MECE度',
                                      value:
                                        scoringData.coverage_breakdown?.mece ||
                                        0,
                                      tip: '網羅性と相互排他性のバランス',
                                    },
                                  ].map(item => (
                                    <Tooltip
                                      key={item.key}
                                      title={item.tip}
                                      placement="top"
                                    >
                                      <div className="score-chip coverage-chip">
                                        <div className="score-chip__label">
                                          {item.label}
                                        </div>
                                        <div className="score-chip__value">
                                          {Math.round(item.value)}
                                        </div>
                                        <Progress
                                          percent={Math.min(
                                            100,
                                            Math.max(0, item.value),
                                          )}
                                          showInfo={false}
                                          steps={5}
                                          strokeColor="#fa8c16"
                                          style={{ marginTop: 6 }}
                                        />
                                      </div>
                                    </Tooltip>
                                  ))}
                                </div>
                              )}
                            </Card>
                            <Card
                              size="small"
                              title="ユニーク内訳"
                              bodyStyle={{ padding: 12 }}
                            >
                              {scoringStatus !== 'completed' ||
                              !scoringData?.uniqueness_breakdown ? (
                                <div className="score-breakdown">
                                  {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="score-chip">
                                      <Skeleton.Input
                                        active
                                        size="small"
                                        style={{
                                          width: '100%',
                                          marginBottom: 8,
                                        }}
                                      />
                                      <Skeleton.Button
                                        active
                                        size="small"
                                        block
                                        style={{ height: 8 }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="score-breakdown">
                                  {[
                                    {
                                      key: 'unique_topics',
                                      label: 'ユニークトピック',
                                      value:
                                        scoringData.uniqueness_breakdown
                                          ?.unique_topics || 0,
                                      tip: '独自トピックの取り込み度・競合が言及していないトピック・トピックリストにない完全に新しい視点の総合評価',
                                    },
                                    {
                                      key: 'differentiation',
                                      label: '競合との違い',
                                      value:
                                        scoringData.uniqueness_breakdown
                                          ?.differentiation || 0,
                                      tip: '競合平均と比べた文字列的違い度（構成/語彙/配列）',
                                    },
                                    {
                                      key: 'intent_alignment',
                                      label: '検索意図対応',
                                      value:
                                        scoringData.uniqueness_breakdown
                                          ?.intent_alignment || 0,
                                      tip: '検索者ニーズに応えた内容か',
                                    },
                                    {
                                      key: 'attractiveness',
                                      label: '魅力性',
                                      value:
                                        scoringData.uniqueness_breakdown
                                          ?.attractiveness || 0,
                                      tip: '読みたくなる見出し・直感理解のしやすさ',
                                    },
                                  ].map(item => (
                                    <Tooltip
                                      key={item.key}
                                      title={item.tip}
                                      placement="top"
                                    >
                                      <div className="score-chip unique-chip">
                                        <div className="score-chip__label">
                                          {item.label}
                                        </div>
                                        <div className="score-chip__value">
                                          {Math.round(item.value)}
                                        </div>
                                        <Progress
                                          percent={Math.min(
                                            100,
                                            Math.max(0, item.value),
                                          )}
                                          showInfo={false}
                                          steps={5}
                                          strokeColor="#00d4ff"
                                          style={{ marginTop: 6 }}
                                        />
                                      </div>
                                    </Tooltip>
                                  ))}
                                </div>
                              )}
                            </Card>
                          </div>
                        </Space>
                      </Col>
                      <Col span={12}>
                        {/* 右カラム: 評価サマリー */}
                        <Card
                          size="small"
                          title="評価サマリー"
                          bodyStyle={{ padding: 12 }}
                        >
                          {scoringStatus !== 'completed' ||
                          !scoringData?.summary_and_action ? (
                            <div>
                              <Skeleton active paragraph={{ rows: 4 }} />
                            </div>
                          ) : (
                            <Space
                              direction="vertical"
                              style={{ width: '100%' }}
                              size={12}
                            >
                              {(() => {
                                const summary =
                                  scoringData.summary_and_action?.summary || {};
                                const items = [
                                  {
                                    title: 'トピックカバレッジ',
                                    content: summary.topic_coverage_summary,
                                  },
                                  {
                                    title: 'キーワード最適化',
                                    content:
                                      summary.keyword_optimization_summary,
                                  },
                                  {
                                    title: '独自性と読者体験',
                                    content:
                                      summary.uniqueness_and_appeal_summary,
                                  },
                                  {
                                    title: '構成の質',
                                    content: summary.structure_quality_summary,
                                  },
                                ];
                                return items
                                  .filter(item => item.content)
                                  .map((item, idx) => (
                                    <div key={idx}>
                                      <Typography.Text
                                        strong
                                        style={{
                                          fontSize: 13,
                                          color: '#1890ff',
                                        }}
                                      >
                                        {item.title}
                                      </Typography.Text>
                                      <Typography.Paragraph
                                        style={{
                                          marginTop: 4,
                                          marginBottom: 0,
                                          fontSize: 12,
                                          color: '#595959',
                                          lineHeight: 1.6,
                                        }}
                                      >
                                        {item.content}
                                      </Typography.Paragraph>
                                    </div>
                                  ));
                              })()}
                            </Space>
                          )}
                        </Card>
                      </Col>
                    </Row>
                    <Row style={{ marginTop: 16 }}>
                      <Col span={24}>
                        {/* 全幅: 改善アイデア（v19） */}
                        <Card
                          size="small"
                          title="改善アイデア"
                          bodyStyle={{ padding: 0 }}
                        >
                          <div style={{ padding: '8px 12px', background: '#fffbe6', borderBottom: '1px solid #f0f0f0', fontSize: 12, color: '#8c6d1f', lineHeight: 1.6 }}>
                            AIが分析した構成改善のアイデアです。参考情報としてご活用ください。チェックボックスはメモ用で、チェックしても自動で反映はされません。具体例の「適用」ボタンで見出しに追加できます。
                          </div>
                        
                          {scoringStatus !== 'completed' ||
                          !scoringData?.summary_and_action ? (
                            <div style={{ padding: 12 }}>
                              <Skeleton active paragraph={{ rows: 5 }} />
                            </div>
                          ) : (
                            <Table
                              dataSource={(() => {
                                // ⭐ v19: improvement_ideas（後方互換性でaction_planもサポート）
                                const improvementIdeas = Array.isArray(
                                  scoringData.summary_and_action
                                    ?.improvement_ideas,
                                )
                                  ? scoringData.summary_and_action
                                      .improvement_ideas
                                  : Array.isArray(
                                        scoringData.summary_and_action
                                          ?.action_plan,
                                      )
                                    ? scoringData.summary_and_action.action_plan
                                    : [];
                                return improvementIdeas.map((item, idx) => ({
                                  key: item.id || `action_${idx}`,
                                  ...item,
                                }));
                              })()}
                              columns={[
                                {
                                  title: <Tooltip title="確認済みマーク（メモ用）">✓</Tooltip>,
                                  dataIndex: 'checked',
                                  key: 'checked',
                                  width: 40,
                                  render: (checked, record) => (
                                    <Checkbox
                                      checked={
                                        actionPlanChecks[record.key] || false
                                      }
                                      onChange={async e => {
                                        const checked = e.target.checked;
                                        const actionId = record.key;

                                        // 楽観的更新（即座にUIに反映）
                                        setActionPlanChecks({
                                          ...actionPlanChecks,
                                          [actionId]: checked,
                                        });

                                        // API呼び出し（バックグラウンドで保存）
                                        try {
                                          await v2ArticlesApi.updateActionPlanCheck(
                                            uuid,
                                            actionId,
                                            checked,
                                          );
                                        } catch (error) {
                                          console.error(
                                            '[ActionPlanCheck] 保存失敗:',
                                            error,
                                          );
                                          // エラー時は元に戻す
                                          setActionPlanChecks({
                                            ...actionPlanChecks,
                                            [actionId]: !checked,
                                          });
                                        }
                                      }}
                                    />
                                  ),
                                },
                                {
                                  title: '優先度',
                                  dataIndex: 'priority',
                                  key: 'priority',
                                  width: 85,
                                  render: priority => {
                                    const config = {
                                      critical: {
                                        text: '最重要',
                                        color: '#cf1322',
                                      },
                                      high: {
                                        text: '高',
                                        color: '#ff4d4f',
                                      },
                                      medium: {
                                        text: '中',
                                        color: '#faad14',
                                      },
                                      low: {
                                        text: '低',
                                        color: '#52c41a',
                                      },
                                      minor: {
                                        text: '微調整',
                                        color: '#d9d9d9',
                                      },
                                    };
                                    const p = config[priority] || config.medium;
                                    return (
                                      <Tag
                                        color={p.color}
                                        style={{ margin: 0 }}
                                      >
                                        {p.text}
                                      </Tag>
                                    );
                                  },
                                },
                                {
                                  title: '種別',
                                  dataIndex: 'category',
                                  key: 'category',
                                  width: 130,
                                  render: category => (
                                    <Tag color="blue" style={{ margin: 0 }}>
                                      {category}
                                    </Tag>
                                  ),
                                },
                                {
                                  title: '改善案',
                                  dataIndex: 'improvement_direction',
                                  key: 'improvement_direction',
                                  width: '35%',
                                  render: (text, record) => {
                                    const isChecked =
                                      actionPlanChecks[record.key] || false;
                                    // ⭐ v19: 新フィールド、後方互換性でactionも表示
                                    const content = text || record.action || '';
                                    return (
                                      <span
                                        style={{
                                          display: 'block',
                                          textDecoration: isChecked
                                            ? 'line-through'
                                            : 'none',
                                          color: isChecked ? '#999' : 'inherit',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {content}
                                      </span>
                                    );
                                  },
                                },
                                {
                                  title: '具体例',
                                  dataIndex: 'concrete_example',
                                  key: 'concrete_example',
                                  render: (text, record) => {
                                    const isChecked =
                                      actionPlanChecks[record.key] || false;
                                    // ⭐ v19: 新フィールド、後方互換性でreasonも表示
                                    const content = text || record.reason || '';
                                    return (
                                      <span
                                        style={{
                                          display: 'block',
                                          textDecoration: isChecked
                                            ? 'line-through'
                                            : 'none',
                                          color: isChecked ? '#999' : 'inherit',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {content}
                                      </span>
                                    );
                                  },
                                },
                              ]}
                              pagination={false}
                              size="small"
                              bordered
                              rowClassName={record =>
                                actionPlanChecks[record.key]
                                  ? 'action-plan-row-checked'
                                  : ''
                              }
                            />
                          )}
                        </Card>
                      </Col>
                    </Row>
                  </>
                );
              })(),
            },
            {
              key: 'competitors',
              label: '競合見出し',
              children: (() => {
                const scrollerRef = React.useRef(null);
                const pan = React.useRef({
                  active: false,
                  startX: 0,
                  startLeft: 0,
                });
                const onDown = e => {
                  const el = scrollerRef.current;
                  if (!el) return;
                  pan.current = {
                    active: true,
                    startX: e.clientX,
                    startLeft: el.scrollLeft,
                  };
                  el.style.cursor = 'grabbing';
                  el.style.userSelect = 'none';
                  if (document && document.body)
                    document.body.style.userSelect = 'none';
                };
                const onMove = e => {
                  const el = scrollerRef.current;
                  if (!el || !pan.current.active) return;
                  const dx = e.clientX - pan.current.startX;
                  el.scrollLeft = pan.current.startLeft - dx;
                };
                const onUp = () => {
                  const el = scrollerRef.current;
                  pan.current.active = false;
                  if (el) {
                    el.style.cursor = 'grab';
                    el.style.userSelect = '';
                  }
                  if (document && document.body)
                    document.body.style.userSelect = '';
                };

                // 現在構成案 編集状態
                const [currentOutlineEdit, setCurrentOutlineEdit] =
                  React.useState(false);
                const [currentOutlineDraft, setCurrentOutlineDraft] =
                  React.useState([]);

                const renderHeadingItems = heads => (
                  <div style={{ padding: '8px 8px 0' }}>
                    {heads
                      .filter(h => {
                        const lvl =
                          h.level ||
                          (typeof h.tag === 'string' && /h(\d)/i.test(h.tag)
                            ? Number(/h(\d)/i.exec(h.tag)[1])
                            : 2);
                        return lvl >= 2 && lvl <= 4;
                      })
                      .map((h, idx) => {
                        const level =
                          typeof h.level === 'number'
                            ? h.level
                            : typeof h.tag === 'string' && /h(\d)/i.test(h.tag)
                              ? Number(/h(\d)/i.exec(h.tag)[1])
                              : 2;
                        const ml = level === 2 ? 0 : level === 3 ? 12 : 24;
                        const color =
                          level === 2
                            ? 'orange'
                            : level === 3
                              ? 'gold'
                              : 'green';
                        return (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '2px 0',
                              marginLeft: ml,
                            }}
                          >
                            <Tag color={color} style={{ borderRadius: 999 }}>
                              {`H${level}`}
                            </Tag>
                            <Typography.Text style={{ fontSize: 13 }}>
                              {h.text || h.title || ''}
                            </Typography.Text>
                          </div>
                        );
                      })}
                  </div>
                );

                const renderCard = (
                  c,
                  i,
                  sticky = false,
                  title = null,
                  url = null,
                  domain = null,
                  heads = [],
                ) => (
                  <Card
                    key={`comp-${sticky ? 'current' : i}`}
                    size="small"
                    style={{
                      position: sticky ? 'sticky' : undefined,
                      left: sticky ? 0 : undefined,
                      zIndex: sticky ? 3 : undefined,
                      flex: '0 0 320px',
                      minWidth: 320,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: '#fff',
                      border: sticky ? '2px solid #00d4ff' : undefined,
                      borderRight: sticky ? '2px solid #00d4ff' : undefined,
                    }}
                    bodyStyle={{
                      padding: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                    >
                      {/* favicon 左端配置（現在構成案はアプリのfavicon） */}
                      <img
                        src={
                          sticky
                            ? '/favicon.ico'
                            : `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain || c?.domain || c?.url || '')}&sz=32`
                        }
                        alt="icon"
                        width={16}
                        height={16}
                        style={{ borderRadius: 4 }}
                      />
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {/* タイトルは二行固定・若干幅を狭めて折り畳みを2文字分短縮 */}
                        <div
                          style={{
                            minHeight: '2.5em',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {sticky ? (
                            <Typography.Text
                              strong
                              style={{
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                              }}
                              title={title || domain || url || ''}
                            >
                              {title || domain || url}
                            </Typography.Text>
                          ) : (
                            <a
                              href={url || '#'}
                              target="_blank"
                              rel="noreferrer"
                              className="comp-card-title-link"
                              title={title || domain || url || ''}
                              style={{
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                                color: '#1677ff',
                                textDecoration: 'none',
                              }}
                            >
                              <strong>{title || domain || url}</strong>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* メトリクス（現在構成案は見出し数のみTagで） */}
                    {sticky ? (
                      <div
                        style={{
                          marginTop: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <Tag color="blue">{(heads || []).length}見出し</Tag>
                        </div>
                        <div>
                          <Button
                            size="small"
                            type={currentOutlineEdit ? 'primary' : 'default'}
                            onClick={() => {
                              if (!currentOutlineEdit) {
                                // 編集開始: 現在の構成案のヘッダ配列をドラフトに入れる
                                const draft = (
                                  Array.isArray(currentHeads)
                                    ? currentHeads
                                    : []
                                ).map(h => ({
                                  level:
                                    typeof h.level === 'number'
                                      ? h.level
                                      : typeof h.tag === 'string' &&
                                          /h(\d)/i.test(h.tag)
                                        ? Number(/h(\d)/i.exec(h.tag)[1])
                                        : 2,
                                  text: h.text || h.title || '',
                                }));
                                setCurrentOutlineDraft(draft);
                                {
                                  const txt = serializeSectionsToText();
                                  const noH1 = txt
                                    .split('\n')
                                    .filter(
                                      line => !/^\s*-\s*H1\s*:/i.test(line),
                                    )
                                    .join('\n');
                                  setEditorText(noH1);
                                }
                                setCurrentOutlineEdit(true);
                              } else {
                                // 保存: テキストモード文字列 -> sections へ反映（空配列でも反映）
                                const parsed =
                                  parseEditorTextToSections(editorText);
                                if (Array.isArray(parsed)) setSections(parsed);
                                setCurrentOutlineEdit(false);
                              }
                            }}
                          >
                            {currentOutlineEdit ? '反映する' : 'クイック編集'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          marginTop: 6,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                          }}
                        >
                          {/* Strengthラベル */}
                          {(() => {
                            const strengthData =
                              c?.headings_analysis?.strength || {};
                            const strengthValue =
                              strengthData.dominant_strength ||
                              strengthData.most_common_strength ||
                              null;

                            if (strengthValue) {
                              return (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    backgroundColor: '#fff0f6',
                                    border: '1px solid #ffadd2',
                                  }}
                                >
                                  <BarChartOutlined
                                    style={{ color: '#eb2f96', fontSize: 14 }}
                                  />
                                  <Typography.Text
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: '#eb2f96',
                                    }}
                                  >
                                    {strengthValue}
                                  </Typography.Text>
                                </span>
                              );
                            }
                            return null;
                          })()}
                          {/* 文字数 */}
                          <Tag>{(c?.word_count || 0).toLocaleString()}文字</Tag>
                          {/* 見出し数 */}
                          <Tag color="green">{(heads || []).length}見出し</Tag>
                          {/* サイト種別ラベル */}
                          {c?.site_type &&
                            (() => {
                              const siteTypeMap = {
                                owned_media: 'オウンドメディア',
                                service_site: 'サービスサイト',
                                portal: 'ポータル',
                                ec: 'EC',
                                news: 'ニュース',
                                corporate_site: 'コーポレートサイト',
                                government_site: '公的機関',
                                sns: 'SNS',
                                ugc: 'UGC',
                                others: 'その他',
                              };
                              const colorByType = {
                                owned_media: {
                                  bg: '#edf2ff',
                                  text: '#2f54eb',
                                },
                                service_site: {
                                  bg: '#e6fffb',
                                  text: '#13c2c2',
                                },
                                portal: { bg: '#fff7e6', text: '#ad6800' },
                                ec: { bg: '#fff1f0', text: '#cf1322' },
                                news: { bg: '#f9f0ff', text: '#531dab' },
                                corporate_site: {
                                  bg: '#e6f4ff',
                                  text: '#0958d9',
                                },
                                government_site: {
                                  bg: '#f6ffed',
                                  text: '#389e0d',
                                },
                                sns: { bg: '#fcffe6', text: '#7cb305' },
                                ugc: { bg: '#fff0f6', text: '#c41d7f' },
                                others: { bg: '#f5f5f5', text: '#595959' },
                              };
                              return (
                                <Tag
                                  style={{
                                    backgroundColor:
                                      (colorByType[c.site_type] || {}).bg ||
                                      '#f5f5f5',
                                    color:
                                      (colorByType[c.site_type] || {}).text ||
                                      '#595959',
                                    border: 'none',
                                  }}
                                >
                                  {siteTypeMap[c.site_type] || c.site_type}
                                </Tag>
                              );
                            })()}
                        </div>
                      </div>
                    )}
                    {/* ヘッダー/メトリクスと本文の区切り */}
                    <div
                      style={{
                        height: 1,
                        background: '#f0f0f0',
                        margin: '8px 0',
                      }}
                    />
                    {/* 見出しエリア */}
                    <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                      {sticky && currentOutlineEdit ? (
                        <Input.TextArea
                          ref={textAreaRef}
                          value={editorText}
                          onChange={e => setEditorText(e.target.value)}
                          onKeyDown={handleEditorKeyDown}
                          rows={12}
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 13,
                            lineHeight: 1.6,
                            height: '100%',
                            resize: 'none',
                          }}
                        />
                      ) : null}
                      {(!sticky || !currentOutlineEdit) &&
                      Array.isArray(heads) &&
                      heads.length > 0 ? (
                        renderHeadingItems(heads)
                      ) : !sticky || !currentOutlineEdit ? (
                        <Typography.Text type="secondary">
                          見出しデータなし
                        </Typography.Text>
                      ) : null}
                    </div>
                  </Card>
                );

                // 現在編集中の構成を見出しに変換（独自情報は見出しに含めない）
                const currentHeads = (() => {
                  try {
                    if (!Array.isArray(sections)) return [];
                    const list = [];
                    sections.forEach(sec => {
                      list.push({ level: 2, text: sec.title });
                      (sec.subsections || []).forEach(sub => {
                        if (sub?.type !== 'topping') {
                          const isH4 =
                            /\(H4\)/i.test(sub.title || '') ||
                            sub?.level === 4 ||
                            sub?.headingLevel === 4 ||
                            sub?.placementMode === 'h4_child' ||
                            sub?.type === 'h4';
                          list.push({
                            level: isH4 ? 4 : 3,
                            text: (
                              sub.title ||
                              sub?.toppingData?.title ||
                              ''
                            ).replace(/\s*\(H4\)\s*$/i, ''),
                          });
                        }
                      });
                    });
                    return list;
                  } catch (_) {
                    return [];
                  }
                })();

                return (
                  // スライダー全体の最大高さを更に低くし、縦スクロールは各カード内のみ
                  <div style={{ height: 'calc(-80px + 80vh)', padding: 8 }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 12,
                        height: '100%',
                        alignItems: 'stretch',
                      }}
                    >
                      {renderCard(
                        { position: 0, word_count: 0 },
                        0,
                        true,
                        titleH1 || '現在の構成案',
                        null,
                        window?.location?.host || '',
                        currentHeads,
                      )}
                      <div
                        ref={scrollerRef}
                        style={{
                          display: 'flex',
                          gap: 12,
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          height: '100%',
                          cursor: 'grab',
                        }}
                        onMouseDown={onDown}
                        onMouseMove={onMove}
                        onMouseLeave={onUp}
                        onMouseUp={onUp}
                      >
                        {compList.map((c, i) => {
                          const heads =
                            (c.headings_analysis &&
                              c.headings_analysis.headings) ||
                            c.headings ||
                            [];
                          return renderCard(
                            c,
                            i,
                            false,
                            c.title || c.domain || c.url,
                            c.url || '#',
                            c.domain || '',
                            heads,
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })(),
            },
            {
              key: 'topic-map',
              label: 'トピック構造',
              children: (
                <div style={{ padding: 8 }}>
                  {(() => {
                    const src = fullAnalysis || analysisFullFixture;
                    // keywordを確実に設定
                    const srcWithKeyword = src
                      ? { ...src, keyword: src.keyword || keyword }
                      : {
                          keyword,
                          topic_analysis: {
                            integrated_heatmap: {
                              formatted_heatmaps: {
                                topic_heatmap: { rows: [] },
                              },
                            },
                          },
                        };

                    const rows =
                      src?.topic_analysis?.integrated_heatmap
                        ?.formatted_heatmaps?.topic_heatmap?.rows ||
                      src?.topic_analysis?.site_topic_analysis?.heatmap_data
                        ?.rows ||
                      [];
                    const count = Array.isArray(rows) ? rows.length : 0;
                    const ready = count > 0;
                    const height = ready
                      ? Math.max(280, Math.round((count / 30) * 700))
                      : 280;
                    return (
                      <TopicSunburstMap
                        competitorData={srcWithKeyword}
                        loading={!ready}
                        height={height}
                        showStatistics={false}
                        enableAnimation={true}
                        onTopicClick={() => {}}
                      />
                    );
                  })()}
                </div>
              ),
            },
            {
              key: 'heatmap',
              label: 'ヒートマップ',
              children: (
                <div className="competitor-analysis heatmap-container">
                  {analysisFullFixture?.topic_analysis?.integrated_heatmap
                    ?.formatted_heatmaps?.topic_heatmap?.rows?.length > 0 ? (
                    <div style={{ overflowX: 'auto', padding: 8 }}>
                      <div style={{ paddingBottom: 8 }}>
                        <Segmented
                          size="small"
                          value={heatmapType}
                          onChange={setHeatmapType}
                          options={[
                            { label: 'トピック', value: 'topic' },
                            { label: '重要語句', value: 'terms' },
                            { label: '関連キーワード', value: 'related' },
                            { label: 'LSIキーワード', value: 'lsi' },
                          ]}
                        />
                      </div>
                      {/* ソート適用用の行配列 */}
                      {(() => {
                        const getVal = (row, ci) => {
                          const siteKey = `site${ci + 1}`;
                          const v =
                            row[siteKey] ??
                            (Array.isArray(row.values)
                              ? (() => {
                                  const found = row.values.find(
                                    v => v?.competitor_index === ci,
                                  );
                                  return typeof found?.topic_coverage_score ===
                                    'number'
                                    ? found.topic_coverage_score
                                    : typeof found?.term_coverage_score ===
                                        'number'
                                      ? found.term_coverage_score
                                      : found?.normalized_score;
                                })()
                              : undefined);
                          return typeof v === 'number' ? v : 0.0;
                        };
                        // 現在の構成案スコア（scoringDataから取得、なければ0）
                        const getCurrentPlanVal = row => {
                          if (scoringStatus !== 'completed' || !scoringData) {
                            return 0;
                          }

                          const key = String(
                            row.topic ||
                              row.term ||
                              row.keyword ||
                              row.name ||
                              row.label ||
                              '',
                          );

                          // ヒートマップタイプに応じてデータソースを選択
                          let analysis = null;
                          if (heatmapType === 'topic') {
                            analysis =
                              scoringData.topic_coverage?.topic_analysis || [];
                          } else if (heatmapType === 'terms') {
                            analysis =
                              scoringData.important_terms_coverage
                                ?.terms_analysis || [];
                          } else if (
                            heatmapType === 'related' ||
                            heatmapType === 'lsi'
                          ) {
                            analysis =
                              scoringData.keyword_coverage?.keyword_analysis ||
                              [];
                          }

                          // キーに一致するアイテムを検索
                          if (Array.isArray(analysis)) {
                            const item = analysis.find(
                              a =>
                                (a.topic_name ||
                                  a.topic ||
                                  a.term ||
                                  a.keyword ||
                                  a.name) === key,
                            );
                            if (item) {
                              // スコアを0-10スケールに正規化
                              const score =
                                item.coverage_score ??
                                item.score ??
                                item.relevance_score ??
                                item.normalized_score ??
                                0;
                              return score <= 1
                                ? score * 10
                                : score > 10
                                  ? score / 10
                                  : score;
                            }
                          }

                          return 0;
                        };
                        const fm =
                          analysisFullFixture.topic_analysis.integrated_heatmap
                            .formatted_heatmaps;
                        const rowsByType =
                          heatmapType === 'topic'
                            ? fm.topic_heatmap?.rows || []
                            : heatmapType === 'terms'
                              ? fm.important_terms_heatmap?.rows || []
                              : heatmapType === 'related'
                                ? fm.related_keywords_heatmap?.rows || []
                                : fm.lsi_keywords_heatmap?.rows || [];
                        const baseRows = rowsByType.slice();
                        if (
                          heatSort.col >= 0 ||
                          heatSort.col === -1 ||
                          heatSort.col === -2
                        ) {
                          baseRows.sort((a, b) => {
                            const va =
                              heatSort.col === -2
                                ? Number.isFinite(a?.average_score)
                                  ? a.average_score
                                  : Array.from({ length: compCount }).reduce(
                                      (s, _, idx) => s + getVal(a, idx),
                                      0,
                                    ) / Math.max(1, compCount)
                                : heatSort.col === -1
                                  ? getCurrentPlanVal(a)
                                  : getVal(a, heatSort.col);
                            const vb =
                              heatSort.col === -2
                                ? Number.isFinite(b?.average_score)
                                  ? b.average_score
                                  : Array.from({ length: compCount }).reduce(
                                      (s, _, idx) => s + getVal(b, idx),
                                      0,
                                    ) / Math.max(1, compCount)
                                : heatSort.col === -1
                                  ? getCurrentPlanVal(b)
                                  : getVal(b, heatSort.col);
                            const diff = vb - va;
                            return heatSort.dir === 'desc' ? diff : -diff;
                          });
                        }
                        const compCount = (
                          analysisFullFixture?.competitor_analysis
                            ?.competitor_details || []
                        ).length;
                        // 表示領域が狭い場合にデータ列を横スクロールできるよう、実列数からテーブル幅を算出
                        const tableWidth =
                          (heatmapType === 'topic'
                            ? 350
                            : 300) /* first col */ +
                          72 /* current */ +
                          56 /* AVG */ +
                          compCount * 56; /* competitors */
                        const header = (
                          <thead>
                            <tr>
                              <th
                                style={{
                                  position: 'sticky',
                                  left: 0,
                                  background: '#fff',
                                  zIndex: 2,
                                  textAlign: 'left',
                                  padding: '8px 12px',
                                  borderBottom: '1px solid #f0f0f0',
                                  width: heatmapType === 'topic' ? 350 : 300,
                                  maxWidth: heatmapType === 'topic' ? 350 : 300,
                                }}
                              >
                                {heatmapType === 'topic'
                                  ? 'トピック'
                                  : heatmapType === 'terms'
                                    ? '重要語句'
                                    : heatmapType === 'related'
                                      ? '関連キーワード'
                                      : 'LSIキーワード'}
                              </th>
                              {/* 現在編集中の構成案（favicon） */}
                              <th
                                key="current"
                                style={{
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  minWidth: 56,
                                  borderBottom: '1px solid #f0f0f0',
                                  cursor: 'pointer',
                                }}
                                title="現在編集中の構成案"
                                onClick={() =>
                                  setHeatSort(s =>
                                    s.col === -1
                                      ? {
                                          col: -1,
                                          dir:
                                            s.dir === 'desc' ? 'asc' : 'desc',
                                        }
                                      : { col: -1, dir: 'desc' },
                                  )
                                }
                              >
                                <div
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                  }}
                                >
                                  <img
                                    src={
                                      process.env.PUBLIC_URL + '/favicon.ico'
                                    }
                                    alt="current"
                                    width={16}
                                    height={16}
                                    style={{ borderRadius: 4 }}
                                  />
                                  {heatSort.col === -1 ? (
                                    heatSort.dir === 'desc' ? (
                                      <CaretDownOutlined />
                                    ) : (
                                      <CaretUpOutlined />
                                    )
                                  ) : (
                                    <SwapOutlined style={{ color: '#bbb' }} />
                                  )}
                                </div>
                              </th>
                              {/* 平均スコア（AVG） */}
                              <th
                                key="avg"
                                style={{
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  minWidth: 56,
                                  borderBottom: '1px solid #f0f0f0',
                                  cursor: 'pointer',
                                }}
                                title="平均スコア"
                                onClick={() =>
                                  setHeatSort(s =>
                                    s.col === -2
                                      ? {
                                          col: -2,
                                          dir:
                                            s.dir === 'desc' ? 'asc' : 'desc',
                                        }
                                      : { col: -2, dir: 'desc' },
                                  )
                                }
                              >
                                <Space size={4}>
                                  <span>AVG</span>
                                  {heatSort.col === -2 ? (
                                    heatSort.dir === 'desc' ? (
                                      <CaretDownOutlined />
                                    ) : (
                                      <CaretUpOutlined />
                                    )
                                  ) : (
                                    <SwapOutlined style={{ color: '#bbb' }} />
                                  )}
                                </Space>
                              </th>
                              {Array.from({ length: compCount }).map(
                                (_, ci) => (
                                  <th
                                    key={ci}
                                    onClick={() =>
                                      setHeatSort(s =>
                                        s.col === ci
                                          ? {
                                              col: ci,
                                              dir:
                                                s.dir === 'desc'
                                                  ? 'asc'
                                                  : 'desc',
                                            }
                                          : { col: ci, dir: 'desc' },
                                      )
                                    }
                                    style={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      minWidth: 56,
                                      borderBottom: '1px solid #f0f0f0',
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                    }}
                                    aria-sort={
                                      heatSort.col === ci
                                        ? heatSort.dir === 'desc'
                                          ? 'descending'
                                          : 'ascending'
                                        : 'none'
                                    }
                                    title={
                                      heatSort.col === ci
                                        ? heatSort.dir === 'desc'
                                          ? '降順'
                                          : '昇順'
                                        : 'ソート'
                                    }
                                  >
                                    <Space size={4}>
                                      <span>{ci + 1}</span>
                                      {heatSort.col === ci ? (
                                        heatSort.dir === 'desc' ? (
                                          <CaretDownOutlined />
                                        ) : (
                                          <CaretUpOutlined />
                                        )
                                      ) : (
                                        <SwapOutlined
                                          style={{ color: '#bbb' }}
                                        />
                                      )}
                                    </Space>
                                  </th>
                                ),
                              )}
                            </tr>
                          </thead>
                        );
                        return (
                          <div className="heatmap-table-wrapper">
                            <div className="heatmap-table-body">
                              <table
                                className="heatmap-table"
                                style={{
                                  minWidth: tableWidth,
                                  width: '100%',
                                }}
                              >
                                {header}
                                <tbody>
                                  {baseRows.map((r, ri) => (
                                    <tr
                                      key={ri}
                                      style={{
                                        background:
                                          ri % 2 === 0 ? '#ffffff' : '#fafafa',
                                      }}
                                    >
                                      <td
                                        style={{
                                          position: 'sticky',
                                          left: 0,
                                          zIndex: 1,
                                          background:
                                            ri % 2 === 0
                                              ? '#ffffff'
                                              : '#fafafa',
                                          padding: '6px 12px',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                          width:
                                            heatmapType === 'topic' ? 350 : 300,
                                          maxWidth:
                                            heatmapType === 'topic' ? 350 : 300,
                                          fontWeight: 500,
                                        }}
                                        title={
                                          r.topic ||
                                          r.term ||
                                          r.keyword ||
                                          r.name ||
                                          r.label
                                        }
                                      >
                                        {/* トピック列のコンテンツを右側ラベル付きで表示（リサーチ結果詳細と同じ） */}
                                        {(() => {
                                          const text =
                                            r.topic ||
                                            r.term ||
                                            r.keyword ||
                                            r.name ||
                                            r.label;

                                          // トピックヒートマップの場合のみ分類ラベルを表示（リサーチ結果詳細と同じロジック）
                                          if (heatmapType === 'topic') {
                                            // 平均スコアと各サイトのスコアを計算
                                            const collectSiteValues = rec => {
                                              const arr = [];
                                              if (
                                                rec?.values &&
                                                Array.isArray(rec.values)
                                              ) {
                                                rec.values.forEach(v => {
                                                  const raw =
                                                    v.normalized_score ??
                                                    v.average_score ??
                                                    v.score ??
                                                    v.topic_coverage_score ??
                                                    0;
                                                  if (
                                                    typeof raw === 'number' &&
                                                    !isNaN(raw)
                                                  )
                                                    arr.push({
                                                      value: raw,
                                                      idx:
                                                        typeof v.competitor_index ===
                                                        'number'
                                                          ? v.competitor_index
                                                          : null,
                                                    });
                                                });
                                              } else {
                                                for (let i = 1; i <= 9; i++) {
                                                  const raw = rec[`site${i}`];
                                                  if (
                                                    typeof raw === 'number' &&
                                                    !isNaN(raw)
                                                  )
                                                    arr.push({
                                                      value: raw,
                                                      idx: i - 1,
                                                    });
                                                }
                                              }
                                              return arr;
                                            };

                                            // avgScoresから取得（分析結果のaverage_scoreと同じ値）
                                            const topicKey =
                                              r?.topic ||
                                              r?.term ||
                                              r?.keyword ||
                                              r?.name ||
                                              r?.label;
                                            const avg =
                                              avgScores[topicKey] ??
                                              r?.average_score ??
                                              r?.average_normalized_score ??
                                              r?.score ??
                                              r?.normalized_score ??
                                              0;
                                            const avgRounded = Number.isFinite(
                                              avg,
                                            )
                                              ? Math.round(avg * 10) / 10
                                              : avg;
                                            const siteVals =
                                              collectSiteValues(r);
                                            const top3Vals = siteVals
                                              .filter(
                                                v =>
                                                  v.idx !== null && v.idx <= 2,
                                              )
                                              .map(v => v.value);
                                            const countTop3Over85 =
                                              top3Vals.filter(
                                                v => v >= 8.5,
                                              ).length;
                                            const countSitesOver75 =
                                              siteVals.filter(
                                                v => v.value >= 7.5,
                                              ).length;
                                            const countSitesOver85 =
                                              siteVals.filter(
                                                v => v.value >= 8.5,
                                              ).length;

                                            // リサーチ結果詳細と完全一致の分類ロジック
                                            let statusLabel = null;
                                            let statusColor = undefined;
                                            if (
                                              avgRounded >= 8 ||
                                              countTop3Over85 >= 2
                                            ) {
                                              statusLabel = '必須';
                                              statusColor = 'magenta';
                                            } else if (
                                              avgRounded <= 5.0 &&
                                              countSitesOver85 > 0 &&
                                              countSitesOver85 <= 2
                                            ) {
                                              statusLabel = '独自';
                                              statusColor = 'cyan';
                                            } else if (
                                              avgRounded >= 6 ||
                                              countSitesOver75 >= 3
                                            ) {
                                              statusLabel = '重要';
                                              statusColor = 'gold';
                                            }

                                            return (
                                              <div
                                                className="heatmap-topic"
                                                style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                  }}
                                                >
                                                  {text}
                                                </span>
                                                {statusLabel ? (
                                                  <Tag
                                                    color={statusColor}
                                                    style={{
                                                      marginLeft: 8,
                                                      whiteSpace: 'nowrap',
                                                    }}
                                                  >
                                                    {statusLabel}
                                                  </Tag>
                                                ) : null}
                                              </div>
                                            );
                                          }

                                          // トピック以外はテキストのみ
                                          return text;
                                        })()}
                                      </td>
                                      {/* 現在構成案（将来の自構成スコア） */}
                                      <td
                                        className={
                                          scoringStatus === 'completed'
                                            ? 'heatmap-cell'
                                            : ''
                                        }
                                        style={{
                                          textAlign: 'center',
                                          verticalAlign: 'middle',
                                          padding: 0,
                                          border: '1px solid #f0f0f0',
                                          borderLeft: 'none',
                                          height: 40,
                                          width: 72,
                                          color: '#333',
                                          fontSize: 12,
                                          fontWeight: 500,
                                          cursor:
                                            scoringStatus === 'completed'
                                              ? 'pointer'
                                              : 'default',
                                          background:
                                            scoringStatus !== 'completed'
                                              ? '#fafafa'
                                              : (() => {
                                                  const v =
                                                    getCurrentPlanVal(r);
                                                  const clamp = (n, min, max) =>
                                                    Math.max(
                                                      min,
                                                      Math.min(max, n),
                                                    );
                                                  const t = clamp(v, 0.1, 10);
                                                  if (heatmapType === 'terms') {
                                                    const ratio =
                                                      (t - 0.1) / (10 - 0.1);
                                                    if (ratio <= 0.15)
                                                      return '#f0fdff';
                                                    if (ratio <= 0.3)
                                                      return '#e6f9ff';
                                                    if (ratio <= 0.45)
                                                      return '#d1f2ff';
                                                    if (ratio <= 0.6)
                                                      return '#bae7ff';
                                                    if (ratio <= 0.75)
                                                      return '#91d5ff';
                                                    return '#69c0ff';
                                                  } else if (
                                                    heatmapType === 'related'
                                                  ) {
                                                    const ratio =
                                                      (t - 0.1) / (10 - 0.1);
                                                    if (ratio <= 0.15)
                                                      return '#f6ffed';
                                                    if (ratio <= 0.3)
                                                      return '#e6f7db';
                                                    if (ratio <= 0.45)
                                                      return '#d9f7be';
                                                    if (ratio <= 0.6)
                                                      return '#c7eb9f';
                                                    if (ratio <= 0.75)
                                                      return '#b5d97f';
                                                    return '#95c95f';
                                                  } else if (
                                                    heatmapType === 'lsi'
                                                  ) {
                                                    const ratio =
                                                      (t - 0.1) / (10 - 0.1);
                                                    if (ratio <= 0.15)
                                                      return '#f9f0ff';
                                                    if (ratio <= 0.3)
                                                      return '#f2e8ff';
                                                    if (ratio <= 0.45)
                                                      return '#efdbff';
                                                    if (ratio <= 0.6)
                                                      return '#e8d5ff';
                                                    if (ratio <= 0.75)
                                                      return '#ddc7ff';
                                                    return '#d1b7ff';
                                                  }
                                                  const rankVal = Math.max(
                                                    1,
                                                    Math.min(10, Math.round(t)),
                                                  );
                                                  if (rankVal <= 1)
                                                    return '#fffef9';
                                                  if (rankVal <= 2)
                                                    return '#fffdf2';
                                                  if (rankVal <= 3)
                                                    return '#fffbeb';
                                                  if (rankVal <= 4)
                                                    return '#fff8e1';
                                                  if (rankVal <= 5)
                                                    return '#fff5d6';
                                                  if (rankVal <= 6)
                                                    return '#fff2cc';
                                                  if (rankVal <= 7)
                                                    return '#ffefbf';
                                                  if (rankVal <= 8)
                                                    return '#ffecb3';
                                                  if (rankVal <= 9)
                                                    return '#ffe9a6';
                                                  return '#ffec99';
                                                })(),
                                        }}
                                        title={
                                          scoringStatus !== 'completed'
                                            ? ''
                                            : (() => {
                                                const v = getCurrentPlanVal(r);
                                                return v.toFixed
                                                  ? v.toFixed(1)
                                                  : '';
                                              })()
                                        }
                                        onClick={() => {
                                          if (scoringStatus === 'completed') {
                                            const v = getCurrentPlanVal(r);
                                            setScoreModal({
                                              open: true,
                                              row: r,
                                              col: -1,
                                              value: v,
                                            });
                                          }
                                        }}
                                      >
                                        {scoringStatus !== 'completed' ? (
                                          <Skeleton.Button
                                            active
                                            size="small"
                                            style={{
                                              width: 40,
                                              height: 16,
                                              minWidth: 40,
                                            }}
                                          />
                                        ) : (
                                          (() => {
                                            const v = getCurrentPlanVal(r);
                                            return v.toFixed
                                              ? v.toFixed(1)
                                              : '';
                                          })()
                                        )}
                                      </td>
                                      {/* AVG 値セル */}
                                      {(() => {
                                        const avg = Number.isFinite(
                                          r?.average_score,
                                        )
                                          ? r.average_score
                                          : compCount > 0
                                            ? Array.from({
                                                length: compCount,
                                              }).reduce(
                                                (sum, _, ci) =>
                                                  sum + getVal(r, ci),
                                                0,
                                              ) / compCount
                                            : 0;
                                        const getTopicColor = vv => {
                                          const val = Math.max(
                                            1,
                                            Math.min(10, Math.round(vv || 0)),
                                          );
                                          if (val <= 1) return '#fffef9';
                                          if (val <= 2) return '#fffdf2';
                                          if (val <= 3) return '#fffbeb';
                                          if (val <= 4) return '#fff8e1';
                                          if (val <= 5) return '#fff5d6';
                                          if (val <= 6) return '#fff2cc';
                                          if (val <= 7) return '#ffefbf';
                                          if (val <= 8) return '#ffecb3';
                                          if (val <= 9) return '#ffe9a6';
                                          return '#ffec99';
                                        };
                                        const getTermsColor = value => {
                                          if (value === 0 || value < 0.1)
                                            return '#f0fdff';
                                          const normalizedValue = Math.min(
                                            Math.max(value, 0.1),
                                            10.0,
                                          );
                                          const ratio =
                                            (normalizedValue - 0.1) /
                                            (10.0 - 0.1);
                                          if (ratio <= 0.15) return '#f0fdff';
                                          if (ratio <= 0.3) return '#e6f9ff';
                                          if (ratio <= 0.45) return '#d1f2ff';
                                          if (ratio <= 0.6) return '#bae7ff';
                                          if (ratio <= 0.75) return '#91d5ff';
                                          return '#69c0ff';
                                        };
                                        const getRelatedColor = value => {
                                          if (value === 0 || value < 0.1)
                                            return '#f6ffed';
                                          const normalizedValue = Math.min(
                                            Math.max(value, 0.1),
                                            10.0,
                                          );
                                          const ratio =
                                            (normalizedValue - 0.1) /
                                            (10.0 - 0.1);
                                          if (ratio <= 0.15) return '#f6ffed';
                                          if (ratio <= 0.3) return '#e6f7db';
                                          if (ratio <= 0.45) return '#d9f7be';
                                          if (ratio <= 0.6) return '#c7eb9f';
                                          if (ratio <= 0.75) return '#b5d97f';
                                          return '#95c95f';
                                        };
                                        const getLsiColor = value => {
                                          if (value === 0 || value < 0.1)
                                            return '#f9f0ff';
                                          const normalizedValue = Math.min(
                                            Math.max(value, 0.1),
                                            10.0,
                                          );
                                          const ratio =
                                            (normalizedValue - 0.1) /
                                            (10.0 - 0.1);
                                          if (ratio <= 0.15) return '#f9f0ff';
                                          if (ratio <= 0.3) return '#f2e8ff';
                                          if (ratio <= 0.45) return '#efdbff';
                                          if (ratio <= 0.6) return '#e8d5ff';
                                          if (ratio <= 0.75) return '#ddc7ff';
                                          return '#d1b7ff';
                                        };
                                        const colorFor = v =>
                                          heatmapType === 'topic'
                                            ? getTopicColor(v)
                                            : heatmapType === 'terms'
                                              ? getTermsColor(v)
                                              : heatmapType === 'related'
                                                ? getRelatedColor(v)
                                                : getLsiColor(v);
                                        return (
                                          <td
                                            style={{
                                              background: colorFor(avg),
                                              textAlign: 'center',
                                              verticalAlign: 'middle',
                                              padding: 0,
                                              border: '1px solid #f0f0f0',
                                              borderLeft: 'none',
                                              height: 40,
                                              width: 56,
                                              color: '#333',
                                              fontSize: 12,
                                              fontWeight: 500,
                                            }}
                                            title={
                                              avg?.toFixed ? avg.toFixed(1) : ''
                                            }
                                          >
                                            {avg?.toFixed ? avg.toFixed(1) : ''}
                                          </td>
                                        );
                                      })()}
                                      {Array.from({ length: compCount }).map(
                                        (_, ci) => {
                                          const value = getVal(r, ci);
                                          const getTopicColor = vv => {
                                            const val = Math.max(
                                              1,
                                              Math.min(10, Math.round(vv || 0)),
                                            );
                                            if (val <= 1) return '#fffef9';
                                            if (val <= 2) return '#fffdf2';
                                            if (val <= 3) return '#fffbeb';
                                            if (val <= 4) return '#fff8e1';
                                            if (val <= 5) return '#fff5d6';
                                            if (val <= 6) return '#fff2cc';
                                            if (val <= 7) return '#ffefbf';
                                            if (val <= 8) return '#ffecb3';
                                            if (val <= 9) return '#ffe9a6';
                                            return '#ffec99';
                                          };
                                          const getTermsColor = value => {
                                            if (value === 0 || value < 0.1)
                                              return '#f0fdff';
                                            const normalizedValue = Math.min(
                                              Math.max(value, 0.1),
                                              10.0,
                                            );
                                            const ratio =
                                              (normalizedValue - 0.1) /
                                              (10.0 - 0.1);
                                            if (ratio <= 0.15) return '#f0fdff';
                                            if (ratio <= 0.3) return '#e6f9ff';
                                            if (ratio <= 0.45) return '#d1f2ff';
                                            if (ratio <= 0.6) return '#bae7ff';
                                            if (ratio <= 0.75) return '#91d5ff';
                                            return '#69c0ff';
                                          };
                                          const getRelatedColor = value => {
                                            if (value === 0 || value < 0.1)
                                              return '#f6ffed';
                                            const normalizedValue = Math.min(
                                              Math.max(value, 0.1),
                                              10.0,
                                            );
                                            const ratio =
                                              (normalizedValue - 0.1) /
                                              (10.0 - 0.1);
                                            if (ratio <= 0.15) return '#f6ffed';
                                            if (ratio <= 0.3) return '#e6f7db';
                                            if (ratio <= 0.45) return '#d9f7be';
                                            if (ratio <= 0.6) return '#c7eb9f';
                                            if (ratio <= 0.75) return '#b5d97f';
                                            return '#95c95f';
                                          };
                                          const getLsiColor = value => {
                                            if (value === 0 || value < 0.1)
                                              return '#f9f0ff';
                                            const normalizedValue = Math.min(
                                              Math.max(value, 0.1),
                                              10.0,
                                            );
                                            const ratio =
                                              (normalizedValue - 0.1) /
                                              (10.0 - 0.1);
                                            if (ratio <= 0.15) return '#f9f0ff';
                                            if (ratio <= 0.3) return '#f2e8ff';
                                            if (ratio <= 0.45) return '#efdbff';
                                            if (ratio <= 0.6) return '#e8d5ff';
                                            if (ratio <= 0.75) return '#ddc7ff';
                                            return '#d1b7ff';
                                          };
                                          const bg =
                                            heatmapType === 'topic'
                                              ? getTopicColor(value)
                                              : heatmapType === 'terms'
                                                ? getTermsColor(value)
                                                : heatmapType === 'related'
                                                  ? getRelatedColor(value)
                                                  : getLsiColor(value);
                                          return (
                                            <td
                                              key={ci}
                                              className="heatmap-cell"
                                              style={{
                                                background: bg,
                                                textAlign: 'center',
                                                verticalAlign: 'middle',
                                                padding: 0,
                                                border: '1px solid #f0f0f0',
                                                borderLeft: 'none',
                                                height: 40,
                                                width: 56,
                                                color: '#333',
                                                fontSize: 12,
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                              }}
                                              title={
                                                value.toFixed
                                                  ? value.toFixed(1)
                                                  : ''
                                              }
                                              onClick={() =>
                                                setScoreModal({
                                                  open: true,
                                                  row: r,
                                                  col: ci,
                                                  value,
                                                })
                                              }
                                            >
                                              {value.toFixed
                                                ? value.toFixed(1)
                                                : ''}
                                            </td>
                                          );
                                        },
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <Typography.Text type="secondary">
                      ヒートマップデータなし
                    </Typography.Text>
                  )}
                </div>
              ),
            },
          ]}
        />
        <div className="analysis-modal-footer-fixed">
          <Button
            type="primary"
            ghost
            onClick={async () => {
              if (!uuid) {
                message.error('UUIDが見つかりません');
                return;
              }

              // 分析結果がない場合はスコアリングを実行しない
              if (!hasAnalysis) {
                return;
              }

              try {
                // ⭐ 階層構造を保持した形式で送信（type, placementLevel, placementMode, childrenを含む）
                const outlineData = {
                  working_draft: {
                    sections: sections.map(s => ({
                      id: s.id,
                      title: s.title,
                      subsections: (s.subsections || []).map(sub => ({
                        id: sub.id,
                        title: sub.title,
                        type: sub.type || 'heading', // h4, topping, heading など
                        placementLevel: sub.placementLevel || '', // h3, h4, h3_child, h4_child など
                        placementMode: sub.placementMode || '', // h4_child など（互換性のため両方送信）
                        children: sub.children || [], // H4配下の独自情報など
                        toppingData: sub.toppingData || undefined, // 独自情報の場合
                      })),
                    })),
                  },
                };

                // execution_countを先に取得（状態更新前）
                let previousExecutionCount = 0;
                try {
                  const currentResult = await v2ArticlesApi.getScoring(uuid);
                  previousExecutionCount = currentResult?.execution_count || 0;
                } catch (error) {
                  console.error(
                    '[Step3][Rescoring] Failed to get previous execution_count:',
                    error,
                  );
                }

                // 状態をリセット（この後にAPIを叩くことで古い結果の影響を受けない）
                setScoringStatus('processing');
                setCoverageScore(null);
                setUniquenessScore(null);
                setScoringData(null);
                setTopicScores({}); // トピックスコアもリセット

                await v2ArticlesApi.startScoring(uuid, outlineData);
                message.success('再スコアリングを開始しました');

                // 少し待ってからポーリング開始（バックエンドがジョブをキューに入れる時間を確保）
                await new Promise(resolve => setTimeout(resolve, 1000));

                // ポーリング開始（結果を待つ）
                let pollingCount = 0;
                const maxPolls = 40; // 最大120秒（3秒 × 40回）
                const pollingInterval = setInterval(async () => {
                  pollingCount++;
                  try {
                    const result = await v2ArticlesApi.getScoring(uuid);

                    // 新しいスコアリング結果かどうかをexecution_countで判定
                    const isNewResult =
                      (result?.execution_count || 0) > previousExecutionCount;

                    if (result?.status === 'completed' && isNewResult) {
                      // 状態を更新
                      const newScoringData = result.scoring_data || {};
                      const newCoverageScore = result.coverage_score;
                      const newUniquenessScore = result.uniqueness_score;

                      setScoringData(newScoringData);
                      setCoverageScore(newCoverageScore);
                      setUniquenessScore(newUniquenessScore);
                      setScoringStatus('completed');

                      clearInterval(pollingInterval);
                      // 実行回数を更新
                      setScoringExecutionCount(
                        result?.execution_count || scoringExecutionCount + 1,
                      );
                      message.success('再スコアリングが完了しました');
                    } else if (result?.status === 'failed') {
                      setScoringStatus('failed');
                      clearInterval(pollingInterval);
                      message.error('再スコアリングが失敗しました');
                    } else if (pollingCount >= maxPolls) {
                      clearInterval(pollingInterval);
                      setScoringStatus('idle');
                      message.warning('再スコアリングがタイムアウトしました');
                    }
                  } catch (error) {
                    console.error('[Step3][Rescoring] Polling error:', error);
                    if (pollingCount >= maxPolls) {
                      clearInterval(pollingInterval);
                      setScoringStatus('idle');
                    }
                  }
                }, 3000);
              } catch (error) {
                console.error('[Step3][Rescoring] Failed:', error);
                message.error('再スコアリングの開始に失敗しました');
                setScoringStatus('failed');
              }
            }}
            loading={scoringStatus === 'processing'}
            disabled={scoringStatus === 'processing'}
          >
            現在の構成案を再スコアリングする
          </Button>
          <Button onClick={() => setAnalysisOpen(false)}>閉じる</Button>
        </div>
      </Modal>
      <Modal
        title={
          scoreModal.row ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: (() => {
                    const v = scoreModal.value || 0;
                    const clamp = (n, min, max) =>
                      Math.max(min, Math.min(max, n));
                    const t = clamp(v, 0.1, 10);
                    if (heatmapType === 'terms') {
                      const ratio = (t - 0.1) / (10 - 0.1);
                      if (ratio <= 0.15) return '#f0fdff';
                      if (ratio <= 0.3) return '#e6f9ff';
                      if (ratio <= 0.45) return '#d1f2ff';
                      if (ratio <= 0.6) return '#bae7ff';
                      if (ratio <= 0.75) return '#91d5ff';
                      return '#69c0ff';
                    } else if (heatmapType === 'related') {
                      const ratio = (t - 0.1) / (10 - 0.1);
                      if (ratio <= 0.15) return '#f6ffed';
                      if (ratio <= 0.3) return '#e6f7db';
                      if (ratio <= 0.45) return '#d9f7be';
                      if (ratio <= 0.6) return '#c7eb9f';
                      if (ratio <= 0.75) return '#b5d97f';
                      return '#95c95f';
                    } else if (heatmapType === 'lsi') {
                      const ratio = (t - 0.1) / (10 - 0.1);
                      if (ratio <= 0.15) return '#f9f0ff';
                      if (ratio <= 0.3) return '#f2e8ff';
                      if (ratio <= 0.45) return '#efdbff';
                      if (ratio <= 0.6) return '#e8d5ff';
                      if (ratio <= 0.75) return '#ddc7ff';
                      return '#d1b7ff';
                    }
                    const rankVal = Math.max(1, Math.min(10, Math.round(t)));
                    if (rankVal <= 1) return '#fffef9';
                    if (rankVal <= 2) return '#fffdf2';
                    if (rankVal <= 3) return '#fffbeb';
                    if (rankVal <= 4) return '#fff8e1';
                    if (rankVal <= 5) return '#fff5d6';
                    if (rankVal <= 6) return '#fff2cc';
                    if (rankVal <= 7) return '#ffefbf';
                    if (rankVal <= 8) return '#ffecb3';
                    if (rankVal <= 9) return '#ffe9a6';
                    return '#ffec99';
                  })(),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 12, color: '#000', fontWeight: 500 }}>
                  {scoreModal.value?.toFixed
                    ? scoreModal.value.toFixed(1)
                    : scoreModal.value}
                </span>
              </div>
              <span style={{ fontWeight: 600 }}>
                {scoreModal.row.topic ||
                  scoreModal.row.term ||
                  scoreModal.row.keyword ||
                  scoreModal.row.name ||
                  scoreModal.row.label}
              </span>
            </div>
          ) : null
        }
        open={scoreModal.open}
        onCancel={() =>
          setScoreModal({ open: false, row: null, col: null, value: null })
        }
        footer={null}
        width={600}
        centered
      >
        {scoreModal.row ? (
          <>
            {scoreModal.col === -1 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <Typography.Text
                  style={{ fontSize: 14, color: '#666', fontWeight: '500' }}
                >
                  【現在の構成案】
                </Typography.Text>
                <Typography.Text
                  style={{ fontSize: 14, color: '#666', fontWeight: '400' }}
                >
                  編集中
                </Typography.Text>
              </div>
            ) : typeof scoreModal.col === 'number' ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <Typography.Text
                    style={{ fontSize: 14, color: '#666', fontWeight: '500' }}
                  >
                    【{scoreModal.col + 1}位】
                  </Typography.Text>
                  {(analysisFullFixture?.competitor_analysis
                    ?.competitor_details || [])[scoreModal.col] && (
                    <>
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(analysisFullFixture.competitor_analysis.competitor_details[scoreModal.col].domain || '')}&sz=32`}
                        alt="favicon"
                        style={{ width: 16, height: 16 }}
                        onError={e => (e.target.style.display = 'none')}
                      />
                      <Typography.Text
                        style={{
                          fontSize: 14,
                          color: '#666',
                          fontWeight: '400',
                        }}
                      >
                        {analysisFullFixture.competitor_analysis
                          .competitor_details[scoreModal.col].domain ||
                          analysisFullFixture.competitor_analysis
                            .competitor_details[scoreModal.col].site_name ||
                          ''}
                      </Typography.Text>
                    </>
                  )}
                </div>
                {/* ページタイトル（リンク付き） */}
                <div style={{ marginBottom: 16 }}>
                  {(() => {
                    const competitor = (compList || [])[scoreModal.col];
                    const url = competitor?.url || '#';
                    const title =
                      competitor?.title ||
                      competitor?.page_title ||
                      'ページタイトル不明';
                    return (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#1890ff',
                          fontSize: 15,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          display: 'block',
                          lineHeight: '1.4',
                        }}
                      >
                        {title}
                      </a>
                    );
                  })()}
                </div>
                {/* Strengthラベル、文字数、見出し数、サイト種別ラベル */}
                <div style={{ marginBottom: 16 }}>
                  <Space size={8} wrap>
                    {(() => {
                      const competitor = (compList || [])[scoreModal.col];
                      const strengthData =
                        competitor?.headings_analysis?.strength || {};
                      const totalHeadings =
                        competitor?.headings_analysis?.total_headings || 0;
                      const strengthValue =
                        strengthData.dominant_strength ||
                        strengthData.most_common_strength ||
                        null;

                      // Strengthラベル
                      if (strengthValue) {
                        return (
                          <>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '2px 8px',
                                borderRadius: 4,
                                backgroundColor: '#fff0f6',
                                border: '1px solid #ffadd2',
                              }}
                            >
                              <BarChartOutlined
                                style={{ color: '#eb2f96', fontSize: 16 }}
                              />
                              <Typography.Text
                                style={{
                                  fontSize: 16,
                                  fontWeight: 600,
                                  color: '#eb2f96',
                                }}
                              >
                                {strengthValue}
                              </Typography.Text>
                            </span>
                            {/* 文字数 */}
                            <Tag>
                              {(competitor?.word_count || 0).toLocaleString()}
                              文字
                            </Tag>
                            {/* 見出し数 */}
                            <Tag color="green">{totalHeadings}見出し</Tag>
                            {/* サイト種別ラベル */}
                            {competitor?.site_type &&
                              (() => {
                                const siteTypeMap = {
                                  owned_media: 'オウンドメディア',
                                  service_site: 'サービスサイト',
                                  portal: 'ポータル',
                                  ec: 'EC',
                                  news: 'ニュース',
                                  corporate_site: 'コーポレートサイト',
                                  government_site: '公的機関',
                                  sns: 'SNS',
                                  ugc: 'UGC',
                                  others: 'その他',
                                };
                                const colorByType = {
                                  owned_media: {
                                    bg: '#edf2ff',
                                    text: '#2f54eb',
                                  },
                                  service_site: {
                                    bg: '#e6fffb',
                                    text: '#13c2c2',
                                  },
                                  portal: { bg: '#fff7e6', text: '#ad6800' },
                                  ec: { bg: '#fff1f0', text: '#cf1322' },
                                  news: { bg: '#f9f0ff', text: '#531dab' },
                                  corporate_site: {
                                    bg: '#e6f4ff',
                                    text: '#0958d9',
                                  },
                                  government_site: {
                                    bg: '#f6ffed',
                                    text: '#389e0d',
                                  },
                                  sns: { bg: '#fcffe6', text: '#7cb305' },
                                  ugc: { bg: '#fff0f6', text: '#c41d7f' },
                                  others: { bg: '#f5f5f5', text: '#595959' },
                                };
                                return (
                                  <Tag
                                    style={{
                                      backgroundColor:
                                        (
                                          colorByType[competitor.site_type] ||
                                          {}
                                        ).bg || '#f5f5f5',
                                      color:
                                        (
                                          colorByType[competitor.site_type] ||
                                          {}
                                        ).text || '#595959',
                                      border: 'none',
                                    }}
                                  >
                                    {siteTypeMap[competitor.site_type] ||
                                      competitor.site_type}
                                  </Tag>
                                );
                              })()}
                          </>
                        );
                      }
                      return null;
                    })()}
                  </Space>
                </div>
              </>
            ) : (
              <Typography.Text type="secondary">平均値</Typography.Text>
            )}
            {scoreModal.col === -1 ? (
              <div>
                <div
                  style={{
                    maxHeight: 400,
                    overflowY: 'auto',
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                  }}
                >
                  <Collapse
                    size="small"
                    ghost
                    expandIconPosition="end"
                    className="competitor-analysis-collapse"
                    style={{ backgroundColor: '#fafafa' }}
                  >
                    {(() => {
                      const structuredHeadings = [];

                      // sections配列は {title, subsections: [{title}]} の構造
                      sections.forEach(section => {
                        const h2Text = section.title || '';
                        const h3s = (section.subsections || [])
                          .filter(sub => sub.type === 'heading')
                          .map(sub => sub.title);

                        structuredHeadings.push({
                          text: h2Text,
                          level: 2,
                          h3s: h3s,
                        });
                      });

                      return structuredHeadings.map((headingItem, index) => {
                        const hasSubHeadings =
                          headingItem.h3s && headingItem.h3s.length > 0;

                        if (!hasSubHeadings) {
                          return (
                            <div
                              key={index}
                              className="ant-collapse-item"
                              style={{
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: '#fff',
                              }}
                            >
                              <div
                                className="ant-collapse-header"
                                style={{
                                  padding: '12px 16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  minHeight: '48px',
                                  cursor: 'default',
                                }}
                              >
                                <Tag color="orange" size="small">
                                  H2
                                </Tag>
                                <Typography.Text
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    flex: 1,
                                  }}
                                >
                                  {headingItem.text.length > 48
                                    ? headingItem.text.substring(0, 48) + '...'
                                    : headingItem.text}
                                </Typography.Text>
                                <div style={{ width: '16px' }}></div>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <Collapse.Panel
                            header={
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  minHeight: '24px',
                                }}
                              >
                                <Tag color="orange" size="small">
                                  H2
                                </Tag>
                                <Typography.Text
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    flex: 1,
                                  }}
                                >
                                  {headingItem.text.length > 48
                                    ? headingItem.text.substring(0, 48) + '...'
                                    : headingItem.text}
                                </Typography.Text>
                              </div>
                            }
                            key={index}
                            style={{ backgroundColor: '#fff' }}
                          >
                            <div style={{ paddingLeft: 24 }}>
                              {headingItem.h3s.map((subText, subIndex) => (
                                <div
                                  key={subIndex}
                                  style={{
                                    padding: '8px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                  }}
                                >
                                  <Tag color="gold" size="small">
                                    H3
                                  </Tag>
                                  <Typography.Text
                                    style={{ fontSize: 12, color: '#666' }}
                                  >
                                    {subText.length > 60
                                      ? subText.substring(0, 60) + '...'
                                      : subText}
                                  </Typography.Text>
                                </div>
                              ))}
                            </div>
                          </Collapse.Panel>
                        );
                      });
                    })()}
                  </Collapse>
                </div>
              </div>
            ) : (
              typeof scoreModal.col === 'number' &&
              (compList || []).length > 0 &&
              (() => {
                const c = compList[scoreModal.col];
                const heads =
                  (c &&
                    ((c.headings_analysis && c.headings_analysis.headings) ||
                      c.headings ||
                      [])) ||
                  [];
                if (!heads.length) return null;

                // リサーチ結果詳細と同じ階層構造に変換
                const structuredHeadings = [];
                const topLevel = Math.min(
                  ...heads
                    .map(h => h.level || 2)
                    .filter(level => level >= 2 && level <= 4),
                );
                const isH3Top = topLevel === 3;

                if (isH3Top) {
                  // H3が最上位の場合：H3->H4構造
                  let currentH3 = null;
                  heads.forEach(heading => {
                    const level = heading.level || 2;
                    if (level === 3) {
                      currentH3 = {
                        text: heading.text || heading.title || '',
                        level: 3,
                        h4s: [],
                      };
                      structuredHeadings.push(currentH3);
                    } else if (level === 4 && currentH3) {
                      currentH3.h4s.push(heading.text || heading.title || '');
                    }
                  });
                } else {
                  // H2が最上位の場合：H2->H3構造
                  let currentH2 = null;
                  heads.forEach(heading => {
                    const level = heading.level || 2;
                    if (level === 2) {
                      currentH2 = {
                        text: heading.text || heading.title || '',
                        level: 2,
                        h3s: [],
                      };
                      structuredHeadings.push(currentH2);
                    } else if (level === 3 && currentH2) {
                      currentH2.h3s.push(heading.text || heading.title || '');
                    }
                  });
                }

                return (
                  <div
                    style={{
                      maxHeight: 400,
                      overflowY: 'auto',
                      border: '1px solid #f0f0f0',
                      borderRadius: 4,
                    }}
                  >
                    <Collapse
                      size="small"
                      ghost
                      expandIconPosition="end"
                      className="competitor-analysis-collapse"
                      style={{ backgroundColor: '#fafafa' }}
                    >
                      {structuredHeadings.map((headingItem, index) => {
                        const isH3Structure = headingItem.level === 3;
                        const hasSubHeadings = isH3Structure
                          ? headingItem.h4s && headingItem.h4s.length > 0
                          : headingItem.h3s && headingItem.h3s.length > 0;

                        if (!hasSubHeadings) {
                          return (
                            <div
                              key={index}
                              className="ant-collapse-item"
                              style={{
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: '#fff',
                              }}
                            >
                              <div
                                className="ant-collapse-header"
                                style={{
                                  padding: '12px 16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  minHeight: '48px',
                                  cursor: 'default',
                                }}
                              >
                                <Tag
                                  color={isH3Structure ? 'blue' : 'orange'}
                                  size="small"
                                >
                                  {isH3Structure ? 'H3' : 'H2'}
                                </Tag>
                                <Typography.Text
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    flex: 1,
                                  }}
                                >
                                  {headingItem.text.length > 48
                                    ? headingItem.text.substring(0, 48) + '...'
                                    : headingItem.text}
                                </Typography.Text>
                                <div style={{ width: '16px' }}></div>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <Collapse.Panel
                            header={
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  minHeight: '24px',
                                }}
                              >
                                <Tag
                                  color={isH3Structure ? 'blue' : 'orange'}
                                  size="small"
                                >
                                  {isH3Structure ? 'H3' : 'H2'}
                                </Tag>
                                <Typography.Text
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    flex: 1,
                                  }}
                                >
                                  {headingItem.text.length > 48
                                    ? headingItem.text.substring(0, 48) + '...'
                                    : headingItem.text}
                                </Typography.Text>
                              </div>
                            }
                            key={index}
                            style={{ backgroundColor: '#fff' }}
                          >
                            <div style={{ paddingLeft: 24 }}>
                              {(isH3Structure
                                ? headingItem.h4s
                                : headingItem.h3s
                              ).map((subText, subIndex) => (
                                <div
                                  key={subIndex}
                                  style={{
                                    padding: '8px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                  }}
                                >
                                  <Tag color="gold" size="small">
                                    {isH3Structure ? 'H4' : 'H3'}
                                  </Tag>
                                  <Typography.Text
                                    style={{ fontSize: 12, color: '#666' }}
                                  >
                                    {subText.length > 60
                                      ? subText.substring(0, 60) + '...'
                                      : subText}
                                  </Typography.Text>
                                </div>
                              ))}
                            </div>
                          </Collapse.Panel>
                        );
                      })}
                    </Collapse>
                  </div>
                );
              })()
            )}
          </>
        ) : null}
      </Modal>
      <Modal
        title="メモの編集"
        open={memoModal.open}
        onCancel={() =>
          setMemoModal({
            open: false,
            type: null,
            secIndex: null,
            subIndex: null,
            text: '',
          })
        }
        onOk={() => {
          const list = Array.from(sections);
          if (memoModal.type === 'section') {
            list[memoModal.secIndex].memo = memoModal.text;
          } else if (memoModal.type === 'sub') {
            list[memoModal.secIndex].subsections[memoModal.subIndex].memo =
              memoModal.text;
          }
          setSections(list);
          setMemoModal({
            open: false,
            type: null,
            secIndex: null,
            subIndex: null,
            text: '',
          });
        }}
      >
        <Input.TextArea
          rows={6}
          value={memoModal.text}
          onChange={e =>
            setMemoModal(curr => ({ ...curr, text: e.target.value }))
          }
        />
      </Modal>

      {/* 独自情報 追加モーダル */}
      <Modal
        title="独自情報の設定"
        open={addModal.open}
        onCancel={() => setAddModal({ open: false, secIndex: null })}
        onOk={() => {
          if (addModal.secIndex != null) {
            const title = toppingConfig.title?.trim() || '独自情報（カスタム）';
            const list = Array.from(sections);
            const payload = {
              id: genId('sub'),
              title,
              type: 'topping',
              locked: false,
              memo: '',
              toppingData: {
                id: genId('top'),
                title,
                category: 'custom',
                recordCount: 0,
                strength: 2,
                content: { summary: '' },
              },
            };
            if (typeof addModalAfterIndex === 'number') {
              list[addModal.secIndex].subsections.splice(
                addModalAfterIndex + 1,
                0,
                payload,
              );
            } else {
              list[addModal.secIndex].subsections.push(payload);
            }
            setSections(list);
          }
          setAddModal({ open: false, secIndex: null });
          setAddModalAfterIndex(null);
          setToppingConfig({ mode: 'section_embed', title: '' });
        }}
        okText="挿入"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text>活用モード</Typography.Text>
          <Radio.Group
            value={toppingConfig.mode}
            onChange={e =>
              setToppingConfig(cfg => ({ ...cfg, mode: e.target.value }))
            }
          >
            <Radio value="section_embed">セクション内活用</Radio>
            <Radio value="inline_part">パーツとしてそのまま活用</Radio>
          </Radio.Group>
          <Typography.Text>タイトル（任意）</Typography.Text>
          <Input
            placeholder="表示名（任意）"
            value={toppingConfig.title}
            onChange={e =>
              setToppingConfig(cfg => ({ ...cfg, title: e.target.value }))
            }
          />
        </Space>
      </Modal>

      {/* 直接配置（raw）モーダル */}
      <Modal
        title="直接配置（HTML/テキスト）"
        open={addRawModal.open}
        onCancel={() =>
          setAddRawModal({
            open: false,
            secIndex: null,
            afterIndex: null,
            html: '',
            text: '',
          })
        }
        onOk={() => {
          if (addRawModal.secIndex != null) {
            insertRaw(
              addRawModal.secIndex,
              { html: addRawModal.html, text: addRawModal.text },
              addRawModal.afterIndex,
            );
          }
          setAddRawModal({
            open: false,
            secIndex: null,
            afterIndex: null,
            html: '',
            text: '',
          });
        }}
        okText="挿入"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text>テキスト（任意）</Typography.Text>
          <Input.TextArea
            rows={4}
            value={addRawModal.text}
            onChange={e =>
              setAddRawModal(m => ({ ...m, text: e.target.value }))
            }
            placeholder="直接本文として配置するテキスト"
          />
          <Typography.Text>HTML（任意）</Typography.Text>
          <Input.TextArea
            rows={6}
            value={addRawModal.html}
            onChange={e =>
              setAddRawModal(m => ({ ...m, html: e.target.value }))
            }
            placeholder="HTMLスニペットを直接配置"
          />
        </Space>
      </Modal>

      {/* 独自情報 詳細/設定モーダル */}
      <Modal
        title="独自情報の設定"
        open={toppingModal.open}
        onCancel={() =>
          setToppingModal({
            open: false,
            secIndex: null,
            subIndex: null,
            data: null,
            mode: undefined,
            memo: '',
          })
        }
        onOk={() => {
          if (toppingModal.secIndex != null && toppingModal.subIndex != null) {
            const list = Array.from(sections);
            const subref =
              list[toppingModal.secIndex].subsections[toppingModal.subIndex];

            subref.placementMode =
              toppingModal.mode || subref.placementMode || 'section_embed';
            subref.memo = toppingModal.memo || subref.memo || '';
            // パーツとしてそのまま配置の場合、contentも保存
            if (toppingModal.content !== undefined) {
              if (!subref.toppingData) {
                subref.toppingData = {};
              }
              subref.toppingData.content = toppingModal.content;
            }

            setSections(list);
          }
          setToppingModal({
            open: false,
            secIndex: null,
            subIndex: null,
            data: null,
            mode: undefined,
            memo: '',
            content: undefined,
          });
        }}
        okText="保存"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 独自情報データ名を表示 */}
          {toppingModal.data?.toppingData?.title && (
            <div style={{ marginBottom: -8 }}>
              <Typography.Text
                style={{
                  fontSize: 12,
                  color: '#722ed1',
                  fontWeight: 500,
                }}
              >
                {toppingModal.data.toppingData.title}
              </Typography.Text>
            </div>
          )}
          <div>
            <Typography.Text
              strong
              style={{ display: 'block', marginBottom: 8 }}
            >
              配置モード
            </Typography.Text>
            <Segmented
              value={toppingModal.mode || 'section_embed'}
              onChange={value => setToppingModal(m => ({ ...m, mode: value }))}
              options={[
                { label: '適宜', value: 'section_embed' },
                { label: 'パーツとしてそのまま配置', value: 'inline_part' },
              ]}
              block
            />
          </div>

          {/* 配置モードに応じて表示を切り替え */}
          {(toppingModal.mode || 'section_embed') === 'section_embed' ? (
            <div>
              <Typography.Text
                strong
                style={{ display: 'block', marginBottom: 8 }}
              >
                目的・役割
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
              >
                この独自情報をコンテンツ生成にどう活用するか記載してください
              </Typography.Text>

              {/* テンプレートボタン */}
              <div
                style={{
                  marginBottom: 12,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                {[
                  '一次情報として補強',
                  '商品情報として訴求',
                  '実績データとして提示',
                  '専門知識として解説',
                  'ユーザー事例として紹介',
                  '最新情報として追加',
                ].map(template => (
                  <Button
                    key={template}
                    size="small"
                    onClick={() => {
                      const currentMemo = toppingModal.memo || '';
                      const newMemo = currentMemo
                        ? `${currentMemo}\n${template}`
                        : template;
                      setToppingModal(m => ({ ...m, memo: newMemo }));
                    }}
                    style={{
                      fontSize: 12,
                      height: 28,
                    }}
                  >
                    {template}
                  </Button>
                ))}
              </div>

              <Input.TextArea
                rows={5}
                value={toppingModal.memo || ''}
                onChange={e =>
                  setToppingModal(m => ({ ...m, memo: e.target.value }))
                }
                placeholder="例：実際の導入事例として、信頼性を高めるために活用する"
              />
            </div>
          ) : (
            <div>
              <Typography.Text
                strong
                style={{ display: 'block', marginBottom: 8 }}
              >
                コンテンツ編集
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
              >
                この独自情報をそのまま記事に配置する際の内容を編集できます（HTML使用可）
              </Typography.Text>
              <Input.TextArea
                rows={8}
                value={(() => {
                  // toppingModal.contentが設定されていればそれを使用
                  if (toppingModal.content !== undefined) {
                    return toppingModal.content;
                  }

                  // 初期値をtoppingDataから取得
                  const toppingData = toppingModal.data?.toppingData;
                  if (!toppingData) {
                    return '';
                  }

                  // toppingData.contentが文字列ならそのまま使用
                  if (typeof toppingData.content === 'string') {
                    return toppingData.content;
                  }

                  // contentがオブジェクトの場合
                  if (
                    typeof toppingData.content === 'object' &&
                    toppingData.content !== null
                  ) {
                    return (
                      toppingData.content.html ||
                      toppingData.content.body ||
                      toppingData.content.text ||
                      toppingData.content.summary ||
                      ''
                    );
                  }

                  // その他のフィールドを試す
                  return (
                    toppingData.text ||
                    toppingData.body ||
                    toppingData.html ||
                    ''
                  );
                })()}
                onChange={e =>
                  setToppingModal(m => ({ ...m, content: e.target.value }))
                }
                placeholder="HTMLを含むコンテンツを入力..."
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
            </div>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default Step3Composer;
